import type { TimelineNarrationScene, TimelineNarrationTrack, TimelineNarrationTrackId, TimelineNarrationMode } from './timelineNarration'

export const norseTimelineNarrationRouteId = 'norse-story-mode-route'
const audioRoot = `audio/timeline/${norseTimelineNarrationRouteId}`
const labels = {
  en: { en: 'English', 'zh-Hant': 'English' },
  mandarin: { en: 'Chinese', 'zh-Hant': '中文' },
}

const track = (
  sceneId: string,
  id: TimelineNarrationTrackId,
  config: Omit<TimelineNarrationTrack, 'id' | 'src'>,
): TimelineNarrationTrack => ({
  id,
  src: `${audioRoot}/${sceneId}/${id}.mp3`,
  ...config,
})

export const norseTimelineNarrationScenes: TimelineNarrationScene[] = [
  {
    sceneId: "norse-cosmology-gap-before-worlds",
    globalOrder: 1,
    storyId: "norse-cosmology",
    stepId: "gap-before-worlds",
    sourceStepIds: ["gap-before-worlds", "ymir-from-rime", "audhumla-and-buri"],
    sourceIds: ["prose-edda-gylfaginning-4-gap-worlds", "poetic-edda-voluspo-3-5-creation", "prose-edda-gylfaginning-5-ymir-audhumla"],
    tracks: {
      en: track("norse-cosmology-gap-before-worlds", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "The route begins before earth, sea, sky, or divine halls exist. Ginnungagap separates cold Niflheim from fiery Muspell, and the first conditions of creation are already under pressure. Rime and heat meet in the gap. Ymir comes to life, Audhumla feeds him with milk, and she licks Buri from salty ice. Giant life and divine ancestry therefore appear side by side. Creation begins as tension, not as a blank page.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-gap-before-worlds", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "故事先回到世界尚未成形之處。大地、海、天空和神宮都還不存在，只有金倫加鴻溝隔開寒冷的尼福爾海姆與火熱的穆斯貝爾；寒霜、熱氣與深處泉源先佔住創世的兩端。寒與熱在裂隙中相遇，滴落物被賦予生命，尤彌爾由此出現。接着歐德姆布拉以乳汁滋養他，又從鹽冰中舔出布里，讓巨人生命與神族祖先在同一個開端裏並列。這段開場把北歐創世寫成一種張力，而不是從空白中突然完成的世界。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-borr-bestla-brothers",
    globalOrder: 2,
    storyId: "norse-cosmology",
    stepId: "borr-bestla-brothers",
    sourceStepIds: ["borr-bestla-brothers", "ymir-becomes-world"],
    sourceIds: ["prose-edda-gylfaginning-6-borr-bestla", "prose-edda-gylfaginning-7-ymir-world"],
    tracks: {
      en: track("norse-cosmology-borr-bestla-brothers", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Buri's line carries creation into kinship. Buri begets Borr, and Borr with Bestla has Odin, Vili, and Ve. The world-makers are tied to both divine ancestry and giant blood. The story then turns creation into a violent remaking. The brothers kill Ymir and fashion the world from his body: sea from blood, earth from flesh, mountains from bone, and sky from skull. The cosmos begins with mixed kinship and transformed violence.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-borr-bestla-brothers", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "布里的後代把創世從生命開端帶進親族秩序。布里生博爾；博爾與巨人之女貝絲特拉生下奧丁、維利與維。三兄弟既連着神族祖先，也帶着巨人血脈。故事隨即轉向更激烈的創造：三兄弟殺死尤彌爾，把他的身體改造成世界。血成海，肉成大地，骨骼成山，頭骨成天，世界不是平靜建成，而是由一個古老生命的身體重新分配而來。因此，北歐宇宙從一開始就帶着親族混合與暴力改造。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-ask-and-embla",
    globalOrder: 3,
    storyId: "norse-cosmology",
    stepId: "ask-and-embla",
    sourceStepIds: ["ask-and-embla"],
    sourceIds: ["prose-edda-gylfaginning-9-ask-embla", "poetic-edda-voluspo-17-18-humans"],
    tracks: {
      en: track("norse-cosmology-ask-and-embla", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "After the world is shaped, the story does not stop with land, sea, and sky. Odin, Vili, and Ve find Ask and Embla by the shore as lifeless wood without breath, sense, or speech. The brothers give them life, understanding, and bodily perception. Humanity is not born in a palace or grown without agency; it is formed when the gods give wood the conditions of life. The scene moves creation toward the human world.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-ask-and-embla", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "世界被造成後，故事沒有停在山海與天空。奧丁、維利與維在海邊遇見兩段木頭，阿斯克與恩布拉還沒有生命、呼吸、理智、面貌或說話能力。三兄弟分別給予他們生命氣息、理解與感官形態，使木頭成為第一對人。人類不是從神族宮廷裏誕生，也不是自然長成，而是在世界邊緣被神明賦予能活、能想、能感受的條件。這一幕把創世從宇宙材料推到人間。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-sol-and-mani-courses",
    globalOrder: 4,
    storyId: "norse-cosmology",
    stepId: "sol-and-mani-courses",
    sourceStepIds: ["sol-and-mani-courses"],
    sourceIds: ["prose-edda-gylfaginning-11-sol-mani"],
    tracks: {
      en: track("norse-cosmology-sol-and-mani-courses", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "After earth, sky, and humanity are made, the myth asks a practical question: how do sun and moon move through this new world? This is not mere astronomical scenery, but the moment the world gains rhythm. The gods set Sol and Mani in the heavens. Sol drives the chariot made to light the world, while Mani directs the moon's course and phases.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-sol-and-mani-courses", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "創世造出大地、天空與人間後，北歐故事還要回答一件很具體的事：日和月如何在這個世界裏運行。這不是純粹的天文背景，而是新世界開始有節奏的時刻。諸神把蘇爾與瑪尼置於天上。蘇爾駕着為照亮世界而造的日車；瑪尼掌管月亮的行程與盈虧。於是光不再只是從穆斯貝爾飛出的火花，而成為每天都會走過天空的安排。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-wolves-pursue-sun-and-moon",
    globalOrder: 5,
    storyId: "norse-cosmology",
    stepId: "wolves-pursue-sun-and-moon",
    sourceStepIds: ["wolves-pursue-sun-and-moon"],
    sourceIds: ["prose-edda-gylfaginning-12-sun-moon-wolves"],
    tracks: {
      en: track("norse-cosmology-wolves-pursue-sun-and-moon", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "As soon as the sun's chariot and the moon begin to move, the sky comes under pressure. Norse myth does not treat them as permanently secure scenery: one wolf follows the daylight path, while another waits ahead on the moon's way. Sun and moon continue across the heavens, yet their speed is no longer mere regular motion.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-wolves-pursue-sun-and-moon", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "日車和月亮一旦開始運行，天空立刻有了壓力。北歐神話沒有把日月寫成永遠安穩的背景：一頭狼緊隨日車，另一頭在月道前方等候。日與月仍照常穿過天際，可是它們的疾行不再只是規律的移動。這裡還不是末日；危險只以追趕的姿態存在，讓每一次白晝和夜晚都帶著尚未落下的陰影。到了諸神黃昏，天空的崩壞便不是突如其來的災變，而是漫長追逐終於得逞。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-yggdrasil-three-roots",
    globalOrder: 6,
    storyId: "norse-cosmology",
    stepId: "yggdrasil-three-roots",
    sourceStepIds: ["yggdrasil-three-roots", "norns-at-urd-well"],
    sourceIds: ["prose-edda-gylfaginning-15-yggdrasil-roots", "poetic-edda-grimnismol-29-35-yggdrasil", "prose-edda-gylfaginning-15-norns"],
    tracks: {
      en: track("norse-cosmology-yggdrasil-three-roots", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "After humanity appears, the route widens again to the structure of the cosmos. Yggdrasil is not a decorative tree; it connects gods, giants, the dead, and the human world. At the Well of Urd, the Norns allot human lives. They connect lifespan, fate, and cosmic order beside the tree, so the world is held by time as well as space. This scene makes the Norse cosmos readable as one structure.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-yggdrasil-three-roots", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "人類出現後，故事把視野拉回整個宇宙的支架。世界樹不是裝飾性的背景樹，而是把神族、巨人、死者與人間連在一起的結構；它的根伸向不同方向，也把不同力量拉到同一棵樹下。兀兒德之泉旁，諾倫替人類分配壽命。她們不再只是旁觀者，而是在世界樹旁把生命長短、命運次序與宇宙穩定連起來。世界的存在因此不單靠空間，也靠時間與命數維持。",
      }),
    },
  },
  {
    sceneId: "norse-cosmology-nidhogg-bifrost-alfheim",
    globalOrder: 7,
    storyId: "norse-cosmology",
    stepId: "nidhogg-bifrost-alfheim",
    sourceStepIds: ["nidhogg-bifrost-alfheim"],
    sourceIds: ["prose-edda-gylfaginning-13-bifrost", "prose-edda-gylfaginning-16-nidhogg-tree", "prose-edda-gylfaginning-17-alfheim-realms", "poetic-edda-grimnismol-29-35-yggdrasil"],
    tracks: {
      en: track("norse-cosmology-nidhogg-bifrost-alfheim", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Once Yggdrasil is established, the story adds its vulnerable surroundings. Nidhogg gnaws below, Bifrost serves as a road to the gods, and Alfheim is marked within divine distribution. The tree becomes more than a pillar. Its roots can be harmed, its bridge can connect or break, and the worlds include more than Asgard, Jotunheim, and Midgard. The creation route ends with a living, pressured cosmos.",
      }),
      'zh-Hant-mandarin': track("norse-cosmology-nidhogg-bifrost-alfheim", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "世界樹建立後，故事補上它周圍並不安穩的一面。尼德霍格在根部啃咬，彩虹橋成為通往神明世界的道路，亞爾夫海姆則在神族分配中被標示出來。這三個元素把世界樹從單純的宇宙柱變成有損耗、有通道、有分配的結構。樹根會受傷，橋可以連接也可能斷裂，光明精靈之地則顯示諸界不是只有神宮、巨人世界和人間。創世故事因此收在一個動態宇宙裏：世界已經有形狀和路徑，卻不是永遠穩定的完成品。",
      }),
    },
  },
  {
    sceneId: "norse-rigsthula-first-household",
    globalOrder: 8,
    storyId: "norse-rigsthula",
    stepId: "first-household",
    sourceStepIds: ["first-household", "thrall-born"],
    sourceIds: ["poetic-edda-rigsthula-1-45"],
    tracks: {
      en: track("norse-rigsthula-first-household", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Rig enters the household of Ai and Edda, shares their table, and stays three nights in bed. Nine months later, Edda bears Thrall. The birth stanza is damaged, and the opening's account of Rig's identity is uncertain. The route therefore follows only how this poem places a visitor and a household inside one origin sequence. Thrall is not a label used to summarize any real population.",
      }),
      'zh-Hant-mandarin': track("norse-rigsthula-first-household", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "里格走進艾與埃達的家戶，與兩人同席，又在床上留了三夜。九月後，埃達生下特雷爾。出生的一節殘缺，開頭對里格身分的說法也不穩固。時間線因此只跟隨這首詩如何把一名訪客和一個家戶放進同一段來處敘事。特雷爾不是被用來概括任何真實人群的標籤。這裡讀的是詩先把一段家戶故事說成起點，再讓它成為第一支的由來。",
      }),
    },
  },
  {
    sceneId: "norse-rigsthula-thrall-and-thir",
    globalOrder: 9,
    storyId: "norse-rigsthula",
    stepId: "thrall-and-thir",
    sourceStepIds: ["thrall-and-thir", "karl-born"],
    sourceIds: ["poetic-edda-rigsthula-1-45"],
    tracks: {
      en: track("norse-rigsthula-thrall-and-thir", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Thir enters Thrall's home, the two live together, and a long list of children follows. Rig then reaches the second household, and nine months later Amma bears Karl. Both passages join home, partners, and descendants, yet neither requires us to make every listed name permanent or turn work descriptions into inborn worth. The poem uses a repeated visit structure to move to its next branch.",
      }),
      'zh-Hant-mandarin': track("norse-rigsthula-thrall-and-thir", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "西爾走進特雷爾的家，兩人同住，後面接著一串子女名字。里格隨後到第二家戶，九月後阿瑪生下卡爾。兩段都把家、伴侶與後代連在一起，但並不要求讀者把長長的名字全變成永久人物，或把勞務描述當成天生價值。詩透過重複的來訪結構換到下一支；時間線保留這個節奏，卻不替它補出一套真實社會的固定階序。",
      }),
    },
  },
  {
    sceneId: "norse-rigsthula-karl-and-snor",
    globalOrder: 10,
    storyId: "norse-rigsthula",
    stepId: "karl-and-snor",
    sourceStepIds: ["karl-and-snor", "jarl-born"],
    sourceIds: ["poetic-edda-rigsthula-1-45"],
    tracks: {
      en: track("norse-rigsthula-karl-and-snor", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Snor arrives at Karl's home with keys and veil; the two exchange rings, prepare a bed, and make a dwelling. The third sequence then has Mothir bear Jarl. The poem writes each household differently: this one joins cultivation, property, and marriage, while the next moves toward weapons, land, and kingship. This is not a chart that puts people in their places.",
      }),
      'zh-Hant-mandarin': track("norse-rigsthula-karl-and-snor", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "斯諾爾帶著鑰匙和面紗來到卡爾家，兩人交換戒指、整理床鋪、建立居所。第三段接著讓莫希爾生下雅爾。詩把每一段家戶寫得不同：這一段把耕作、家產與婚配放在一起，下一段則逐步轉向武器、土地與王權。這不是一張把人排好位置的社會圖。它是一首在殘缺手稿中保存下來的文化詩，讓家戶敘事一層一層走向它想稱頌的王權。",
      }),
    },
  },
  {
    sceneId: "norse-rigsthula-rig-teaches-jarl",
    globalOrder: 11,
    storyId: "norse-rigsthula",
    stepId: "rig-teaches-jarl",
    sourceStepIds: ["rig-teaches-jarl", "kon-learns-runes"],
    sourceIds: ["poetic-edda-rigsthula-1-45"],
    tracks: {
      en: track("norse-rigsthula-rig-teaches-jarl", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 33,
        text: "Rig returns to teach Jarl runes and call him son; Jarl fights, gains land, and marries Erna. Kon, their youngest child, then learns runes. A crow advises Kon to go to the halls of Dan and Danp, and the manuscript breaks after the invitation. It does not say that he rides out, wins, or becomes king.",
      }),
      'zh-Hant-mandarin': track("norse-rigsthula-rig-teaches-jarl", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "里格回來教雅爾符文、稱他為子；雅爾作戰、獲地，並迎娶艾爾娜。康在兩人的子嗣中最年幼，接著學會符文。烏鴉建議康前往丹與丹普的廳堂，手稿就在邀請之後中斷。它沒有說他出征、勝利或登位。因此這條線停在一個被推向遠方的年輕人身上：王權的語言已經出現，結局卻沒有留下。這份未完成正是文本的邊界。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-frigg-warning",
    globalOrder: 12,
    storyId: "norse-vafthruthnismol",
    stepId: "frigg-warning",
    sourceStepIds: ["frigg-warning"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-frigg-warning", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Odin wants to seek Vafthruthnir and measure what each knows. Frigg says no giant can match this opponent, yet still wishes Odin a safe return. She does not forbid the journey. Her words place the danger of a contest before Odin first.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-frigg-warning", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "奧丁想去找瓦夫蘇魯德尼爾，比一比誰知道得更多。弗麗嘉說，在所有巨人當中，沒有人可與這位對手相比；她仍祝奧丁平安來回。這不是阻止他的命令。她的話先把一場問答的危險放到奧丁面前：比起武力，這次要面對的是一位難以探清見聞的對手。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-gagnrath-enters-hall",
    globalOrder: 13,
    storyId: "norse-vafthruthnismol",
    stepId: "gagnrath-enters-hall",
    sourceStepIds: ["gagnrath-enters-hall"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-gagnrath-enters-hall", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Odin enters the giant's hall without giving his own name. He calls himself Gagnrath. Vafthruthnir gives him a seat and says they will soon know who is wiser. Strength does not clash at once. Whether the guest leaves depends on his answers.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-gagnrath-enters-hall", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "奧丁走進巨人的廳堂，沒有報上自己的名字。他稱自己為加格拉德；瓦夫蘇魯德尼爾讓他坐下，說兩人很快就會知道誰更有智慧。力量沒有立刻交鋒。來客能否離開，要看他能否回答；巨人已說明，若不比他更有智慧，這趟拜訪就沒有出口。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-four-questions-at-doorway",
    globalOrder: 14,
    storyId: "norse-vafthruthnismol",
    stepId: "four-questions-at-doorway",
    sourceStepIds: ["four-questions-at-doorway"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-four-questions-at-doorway", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "Vafthruthnir asks in turn about day, night, a river, and a battlefield to come. Odin names Ifing, the river said to lie between gods and giants and never freeze. After four answers, the giant yields the seat and the questioning turn. The contest finally reverses.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-four-questions-at-doorway", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "瓦夫蘇魯德尼爾一連問起白日、夜晚、河流和將來的戰場。奧丁答出伊芬河，說它隔在諸神與巨人之間，永遠不結冰。四題答完，巨人把座位與提問權讓給來客；較量才真正翻面。奧丁也答出蘇爾特與諸神將在維格里德相遇，證明他知道的不再只是眼前的河。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-world-in-an-answer",
    globalOrder: 15,
    storyId: "norse-vafthruthnismol",
    stepId: "world-in-an-answer",
    sourceStepIds: ["world-in-an-answer"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-world-in-an-answer", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 28,
        text: "Odin's turn begins with a question: where did earth and sky first come from? Vafthruthnir answers that land, mountains, sky, and sea came from Ymir's body. It is his answer in this contest, not a rule that flattens every Norse account into one origin.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-world-in-an-answer", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "輪到奧丁問：大地和天空最初從哪裡來。瓦夫蘇魯德尼爾回答，土地、山、天空和海都出自伊米爾的身體。這是他在競賽中的答覆，不是把所有北歐故事壓成同一種世界起源。問答從馬與河的名字，推進到整個世界用什麼材料構成。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-what-the-giant-remembers",
    globalOrder: 16,
    storyId: "norse-vafthruthnismol",
    stepId: "what-the-giant-remembers",
    sourceStepIds: ["what-the-giant-remembers"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-what-the-giant-remembers", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Odin presses farther and asks what Vafthruthnir remembers. The giant speaks of Bergelmir borne in a boat and places him in an ancient giant lineage. The poem keeps this past as memory, not as a history we can safely fill in.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-what-the-giant-remembers", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 28,
        text: "奧丁把問題推得更遠，問瓦夫蘇魯德尼爾究竟記得什麼。巨人說起貝爾格爾米爾，說他曾被船載著，也把他放進一條古老的巨人血脈。詩讓這段往昔以記憶的形式留下，而不是把它展成一段可補全的歷史。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-wind-from-eagles-wings",
    globalOrder: 17,
    storyId: "norse-vafthruthnismol",
    stepId: "wind-from-eagles-wings",
    sourceStepIds: ["wind-from-eagles-wings"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-wind-from-eagles-wings", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 33,
        text: "Odin asks again: where does wind over the sea come from when it cannot itself be seen? Vafthruthnir says Hraesvelg sits in eagle form at heaven's end and wind comes from his wings. The image remains in the giant's speech: it gives wind a shape without asking readers to treat it as natural science.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-wind-from-eagles-wings", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "奧丁再問，吹過海面的風來自何處，明明看不見它本身。瓦夫蘇魯德尼爾說，在天邊以鷹形坐著的赫瑞斯維爾格，風從他的翅膀而出。這幅畫面留在巨人的話裡：它為風添上形狀，卻沒有要求讀者把它當成自然學。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-nine-worlds-claim",
    globalOrder: 18,
    storyId: "norse-vafthruthnismol",
    stepId: "nine-worlds-claim",
    sourceStepIds: ["nine-worlds-claim"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-nine-worlds-claim", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "Odin asks whether the giant can truly speak of all the knowledge of gods and giants. Vafthruthnir says he has crossed nine worlds and gone beneath to the place where the dead dwell. The claim reaches far, yet the poem does not turn it into another travel story.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-nine-worlds-claim", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "奧丁問巨人是否真能說出神與巨人的一切知識。瓦夫蘇魯德尼爾答說，他走過九界，也到過尼福爾海之下死者居住的地方。這一句把他的見聞推到很遠，卻沒有替這段問答另造一條旅行故事。奧丁沒有追問每一段旅程，因為他正把問題收束到最後一件誰也替不了的事。",
      }),
    },
  },
  {
    sceneId: "norse-vafthruthnismol-question-no-guest-could-ask",
    globalOrder: 19,
    storyId: "norse-vafthruthnismol",
    stepId: "question-no-guest-could-ask",
    sourceStepIds: ["question-no-guest-could-ask"],
    sourceIds: ["poetic-edda-vafthruthnismol-1-43-54-55-contest"],
    tracks: {
      en: track("norse-vafthruthnismol-question-no-guest-could-ask", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "At last Odin asks what he himself whispered to his son before the pyre. Vafthruthnir does not expose the secret. He says no person can know it, and in that instant recognizes who stands before him. The contest ends without a killing. The giant concedes Odin's greater wisdom, and the secret stays between them.",
      }),
      'zh-Hant-mandarin': track("norse-vafthruthnismol-question-no-guest-could-ask", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "最後，奧丁問自己在兒子火葬前耳語了什麼。瓦夫蘇魯德尼爾沒有揭開秘密。他說世上沒有人能知道，並在這一刻認出面前的人。競賽不以殺戮收場；巨人承認奧丁更有智慧，秘密仍留在兩人之間。他已講完自己所知的古事，卻仍越不過奧丁對兒子說過的那一句耳語。",
      }),
    },
  },
  {
    sceneId: "norse-divine-order-odin-frigg-authority",
    globalOrder: 20,
    storyId: "norse-divine-order",
    stepId: "odin-frigg-authority",
    sourceStepIds: ["odin-frigg-authority", "thor-as-future-force"],
    sourceIds: ["prose-edda-gylfaginning-20-divine-authority", "prose-edda-gylfaginning-35-asynjur-freyja", "prose-edda-gylfaginning-21-thor-mjolnir", "prose-edda-skaldskaparmal-35-treasures"],
    tracks: {
      en: track("norse-divine-order-odin-frigg-authority", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "After the worlds take shape, the route enters Aesir order. Odin and Frigg first anchor authority and household, joining kingship, marriage, and foresight within the divine centre. Thor and Mjolnir then give that centre visible force. The hammer is not simply a weapon; it is the gods' clearest answer to pressure from the giant world.",
      }),
      'zh-Hant-mandarin': track("norse-divine-order-odin-frigg-authority", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "諸界成形後，故事進入阿斯神族的秩序。奧丁與弗麗嘉先佔住神族權威與家庭中心；他們不是單獨的人名，而是把神宮、王權、婚姻和預知壓力連在一起的核心。托爾與妙爾尼爾隨後進入這個框架。錘不是普通武器，而是神族面對巨人世界時最容易被看見的力量；托爾的出現也讓神族秩序不再只是議事與親族，還包括守護與反擊。",
      }),
    },
  },
  {
    sceneId: "norse-divine-order-njord-hostage-settlement",
    globalOrder: 21,
    storyId: "norse-divine-order",
    stepId: "njord-hostage-settlement",
    sourceStepIds: ["njord-hostage-settlement", "skadi-nine-night-compact", "freyr-freyja-vanir-children"],
    sourceIds: ["prose-edda-gylfaginning-23-njord-skadi-hostage", "prose-edda-gylfaginning-24-freyr-freyja", "prose-edda-gylfaginning-35-asynjur-freyja"],
    tracks: {
      en: track("norse-divine-order-njord-hostage-settlement", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Divine order is not built only inside the Aesir. Njord enters as a Vanir hostage, and Freyr and Freyja become major powers within the gods' story. Skadi's marriage settlement shows another kind of fragile order: compensation can create a bond, but the sea and mountain life cannot fully merge. This scene places Vanir, Aesir, and giant-world figures within one negotiated structure.",
      }),
      'zh-Hant-mandarin': track("norse-divine-order-njord-hostage-settlement", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "神族秩序不是只靠阿斯神族內部完成。尼奧爾德作為華納人質進入阿斯神族，弗雷與芙蕾雅也因此成為神族故事裏的重要力量；和解不是抽象原則，而是靠人被送進另一個家庭維持。絲卡蒂的婚姻協議又顯示秩序常以補償和妥協處理危機。她與尼奧爾德在海與山之間分住九夜，協議能建立聯繫，卻不能完全消除生活位置的差異。這一段把華納、阿斯神族和巨人世界放進同一組脆弱安排。",
      }),
    },
  },
  {
    sceneId: "norse-divine-order-freyr-sees-gerd",
    globalOrder: 22,
    storyId: "norse-divine-order",
    stepId: "freyr-sees-gerd",
    sourceStepIds: ["freyr-sees-gerd", "skirnir-errand"],
    sourceIds: ["prose-edda-gylfaginning-37-freyr-gerd-skirnir", "poetic-edda-skirnismol-1-42-freyr-gerd"],
    tracks: {
      en: track("norse-divine-order-freyr-sees-gerd", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Once order is established, desire pushes divine sight toward the giant world. Freyr sees Gerd from the high seat, and longing turns into an illness of silence. Skirnir takes the errand with Freyr's horse and sword. The negotiation moves from gifts to threats, and the agreement is fixed by nine nights of waiting. Freyr gains Gerd but gives up his sword.",
      }),
      'zh-Hant-mandarin': track("norse-divine-order-freyr-sees-gerd", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "秩序建立後，慾望把神族視線推向巨人世界。弗雷坐上高座，看見格爾德，愛慕立即變成無法安放的焦慮；他的沉默與病態不是孤立情緒，而是神族與巨人世界再次相觸的入口。斯基爾尼爾接下求婚任務，帶着弗雷的馬與劍前往格爾德所在之處。談判從贈禮走向威嚇，最後把九夜等待寫進婚約。這段故事使愛、差使、武器和迫使同意的語言纏在一起。弗雷得到格爾德，卻失去自己的劍。",
      }),
    },
  },
  {
    sceneId: "norse-divine-order-treasure-craft-contest",
    globalOrder: 23,
    storyId: "norse-divine-order",
    stepId: "treasure-craft-contest",
    sourceStepIds: ["treasure-craft-contest", "treasures-enter-divine-order"],
    sourceIds: ["prose-edda-skaldskaparmal-35-treasures", "prose-edda-gylfaginning-21-thor-mjolnir"],
    tracks: {
      en: track("norse-divine-order-treasure-craft-contest", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Divine order also depends on objects, and objects often begin in trouble. Loki's action draws out a craft contest, tying dwarven skill, wager, and divine power together. Mjolnir, Draupnir, Gullinbursti, and other treasures enter the gods' hands. Each changes what later stories can do: defence, funeral gift, splendour, and authority all become material.",
      }),
      'zh-Hant-mandarin': track("norse-divine-order-treasure-craft-contest", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "神族秩序也靠器物支撐，而器物常由麻煩開始。洛基的行動引出工藝競賽，侏儒打造出多件寶物；這不是單純展示清單，而是讓手藝、賭注和神族權力連在一起。妙爾尼爾、德羅普尼爾、古林博斯提與其他寶物進入神族手中，每件物都改變後面故事的可能性。托爾從此以錘守住秩序，德羅普尼爾會在巴德爾葬禮再出現，弗雷的金豬和其他寶物則擴大神族的象徵力量。",
      }),
    },
  },
  {
    sceneId: "norse-divine-order-loki-among-aesir-and-sigyn",
    globalOrder: 24,
    storyId: "norse-divine-order",
    stepId: "loki-among-aesir-and-sigyn",
    sourceStepIds: ["loki-among-aesir-and-sigyn"],
    sourceIds: ["prose-edda-gylfaginning-33-loki"],
    tracks: {
      en: track("norse-divine-order-loki-among-aesir-and-sigyn", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "The treasures give divine order visible force and make Loki an agent of change. Yet Gylfaginning first places him among the Aesir: he has the giant Farbauti as father and Laufey as mother, while still being numbered among the gods. Chapter 33 calls Loki cunning, says he often brings hardship upon the gods before helping them out by craft, and places Sigyn and Narfi within his divine household. Loki is not an outside destroyer.",
      }),
      'zh-Hant-mandarin': track("norse-divine-order-loki-among-aesir-and-sigyn", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "寶物帶來神族可見的力量，也讓洛基成為改變秩序的人物。但在這些事件之前，《欺騙古魯菲》先把他放在阿斯神族的名單裏：他有巨人父親法爾包提與母親勞菲，卻同時被列為神族的一員。這個位置本身已經帶着兩邊世界交纏的張力。第33章形容洛基善於狡智，常使諸神陷入困境，又會以計謀幫助他們脫身；西格恩與納爾菲則讓他在神族生活中已有家庭。洛基不是從外面闖進來的破壞者。",
      }),
    },
  },
  {
    sceneId: "norse-poetic-mead-kvasir-from-peace",
    globalOrder: 25,
    storyId: "norse-poetic-mead",
    stepId: "kvasir-from-peace",
    sourceStepIds: ["kvasir-from-peace", "dwarves-kill-kvasir"],
    sourceIds: ["prose-edda-skaldskaparmal-1-poetic-mead"],
    tracks: {
      en: track("norse-poetic-mead-kvasir-from-peace", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "This strand begins with the vat left after Aesir and Vanir make peace. The gods make Kvasir from it, and the text calls him so wise that people with difficult questions seek him out. Fjalar and Galar kill Kvasir, gather his blood in three vessels, and mix it with honey. The source turns the loss into a drink associated with poetry and learned skill, without supplying a motive for the killing.",
      }),
      'zh-Hant-mandarin': track("norse-poetic-mead-kvasir-from-peace", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "這條支線從阿斯與華納結束衝突後留下的一缸唾液開始。眾神以它造出克瓦希爾；文本說他無所不知，遇到難題的人都會向他求問。和平在這裏不是一個抽象的背景，而是被留在器皿中、又化成一位能回答問題的存在。費亞拉爾和加拉爾邀克瓦希爾同行，卻殺死他，把血收進三個器皿，再與蜜混合。於是血不單被隱去：它被轉成一種飲物，文本以詩與學問的能力說明它的價值。",
      }),
    },
  },
  {
    sceneId: "norse-poetic-mead-suttungr-takes-ransom",
    globalOrder: 26,
    storyId: "norse-poetic-mead",
    stepId: "suttungr-takes-ransom",
    sourceStepIds: ["suttungr-takes-ransom", "gunnlod-guards-hnitbjorg"],
    sourceIds: ["prose-edda-skaldskaparmal-1-poetic-mead"],
    tracks: {
      en: track("norse-poetic-mead-suttungr-takes-ransom", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "The mead next becomes the ransom that saves the dwarves. After Fjalar and Galar kill Gillingr and his wife, Suttungr strands them where the rising sea will cover them; they surrender the mead for their lives. Suttungr carries it to Hnitbjorg and assigns his daughter Gunnlod to guard it. The source supplies the arrangement, not Gunnlod's private reason, so the timeline leaves that motive unstated.",
      }),
      'zh-Hant-mandarin': track("norse-poetic-mead-suttungr-takes-ransom", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "蜜酒接着成為矮人求生時能提出的賠償。費亞拉爾和加拉爾害死Gillingr與其妻，蘇頓格把他們放到漲潮就會沒頂的礁石上；兩人交出蜜酒，才換回性命。這一筆交易把原本由克瓦希爾而來的飲物，從矮人手中轉到巨人一方。蘇頓格沒有把它留在眼前。他把蜜酒帶到赫尼特比約格，交給女兒岡恩洛德看守。",
      }),
    },
  },
  {
    sceneId: "norse-poetic-mead-bolverkr-works-for-baugi",
    globalOrder: 27,
    storyId: "norse-poetic-mead",
    stepId: "bolverkr-works-for-baugi",
    sourceStepIds: ["bolverkr-works-for-baugi", "rati-opens-the-rock"],
    sourceIds: ["prose-edda-skaldskaparmal-1-poetic-mead"],
    tracks: {
      en: track("norse-poetic-mead-bolverkr-works-for-baugi", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Odin comes to Baugi under the name Bolverkr. When nine workers die fighting over his whetstone, he offers a summer's labour in return for one drink of the mead. The alias belongs to this episode; it is not another god. After the work, Baugi drills Hnitbjorg with Rati and falsely claims to have broken through. Odin tests the hole with chips, demands a real bore, and changes into a serpent to slip inside.",
      }),
      'zh-Hant-mandarin': track("norse-poetic-mead-bolverkr-works-for-baugi", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "奧丁到包吉身邊時使用Bolverkr這個名字。九名工人因他借出的磨刀石互相爭奪而死，包吉忽然缺少收割的人手；奧丁提出替他做完一夏的工作，只要求喝一口蜜酒。這是計策與勞作並行的一段，不需要把化名變成另一位神祇。包吉答應幫忙，卻說蘇頓格不會願意交出蜜酒。收成結束後，兩人走到赫尼特比約格；包吉用Rati鑽岩，先謊稱已經穿透。",
      }),
    },
  },
  {
    sceneId: "norse-poetic-mead-gunnlod-three-draughts",
    globalOrder: 28,
    storyId: "norse-poetic-mead",
    stepId: "gunnlod-three-draughts",
    sourceStepIds: ["gunnlod-three-draughts", "odin-returns-with-mead"],
    sourceIds: ["prose-edda-skaldskaparmal-1-poetic-mead"],
    tracks: {
      en: track("norse-poetic-mead-gunnlod-three-draughts", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Inside the mountain, Gunnlod lets Odin stay three nights and permits three draughts. The text puts permission and result together: each draught empties one vessel, while her personal reason remains unstated. Odin leaves as an eagle with the mead, Suttungr pursues in eagle form, and the gods prepare vessels in Asgard. Odin deposits the mead there; the source also notes the small spill associated with poor poetry.",
      }),
      'zh-Hant-mandarin': track("norse-poetic-mead-gunnlod-three-draughts", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "山內的岡恩洛德讓奧丁住了三夜，並允許他喝三口。文本把許可與結果並排：奧丁每一口都喝空一個器皿，三口喝盡原先盛放蜜酒的三器。她的准許是此處可見的關係；故事沒有明說她的個人理由，因此不把它寫成愛、忠誠或背叛的判詞。奧丁帶着蜜酒化為鷹離開，蘇頓格也化為鷹追趕。眾神在阿斯加德預備器皿，奧丁飛到城中便把蜜酒吐進去；文本也留下少量因追趕而灑落的酒，作為壞詩人的份額。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-hall-praised-too-loudly",
    globalOrder: 29,
    storyId: "norse-lokasenna",
    stepId: "hall-praised-too-loudly",
    sourceStepIds: ["hall-praised-too-loudly"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-hall-praised-too-loudly", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Ægir gives a feast, Fimafeng is praised, and Loki kills him before being driven outside. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-hall-praised-too-loudly", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "埃吉爾設宴，芬瑪芬格受稱讚；洛基殺了他，被趕到門外。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-loki-waits-at-door",
    globalOrder: 30,
    storyId: "norse-lokasenna",
    stepId: "loki-waits-at-door",
    sourceStepIds: ["loki-waits-at-door"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-loki-waits-at-door", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 30,
        text: "Eldir warns him, but Loki says he will go in and disrupt the feast. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-loki-waits-at-door", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 26,
        text: "埃爾迪爾警告他，洛基仍說自己要進去攪亂宴席。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-place-made-at-table",
    globalOrder: 31,
    storyId: "norse-lokasenna",
    stepId: "place-made-at-table",
    sourceStepIds: ["place-made-at-table"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-place-made-at-table", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "Odin orders a place for Loki, and Vidar gives him a drink. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-place-made-at-table", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "奧丁要求給洛基座位，維達爾遞上飲品。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-bragi-and-idunn-hold-line",
    globalOrder: 32,
    storyId: "norse-lokasenna",
    stepId: "bragi-and-idunn-hold-line",
    sourceStepIds: ["bragi-and-idunn-hold-line"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-bragi-and-idunn-hold-line", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 31,
        text: "Bragi refuses to welcome Loki, while Idunn tries to keep words from becoming more. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-bragi-and-idunn-hold-line", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "布拉基不肯歡迎洛基；伊登努力把話留在話語之內。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-hall-answers-back",
    globalOrder: 33,
    storyId: "norse-lokasenna",
    stepId: "hall-answers-back",
    sourceStepIds: ["hall-answers-back"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-hall-answers-back", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "Gefjun, Frigg, Freyja, Njord, Tyr, and Freyr speak in turn; every accusation remains attributable to its speaker. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-hall-answers-back", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "吉菲恩、弗麗嘉、弗蕾雅、尼約德、提爾與弗雷先後開口；每一項指控仍屬說話者。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-servants-and-watchman-speak",
    globalOrder: 34,
    storyId: "norse-lokasenna",
    stepId: "servants-and-watchman-speak",
    sourceStepIds: ["servants-and-watchman-speak"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-servants-and-watchman-speak", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 30,
        text: "Byggvir, Heimdall, and Skadi speak up; warning begins to outweigh mockery. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-servants-and-watchman-speak", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "比格維爾、海姆達爾和斯卡蒂出面；警告開始壓過取笑。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-one-last-cup-one-warning",
    globalOrder: 35,
    storyId: "norse-lokasenna",
    stepId: "one-last-cup-one-warning",
    sourceStepIds: ["one-last-cup-one-warning"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-one-last-cup-one-warning", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 28,
        text: "Sif offers a cup, and Beyla hears Thor on his way. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-one-last-cup-one-warning", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "西芙遞來一杯酒，貝拉聽見托爾正在趕來。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-lokasenna-thor-arrives",
    globalOrder: 36,
    storyId: "norse-lokasenna",
    stepId: "thor-arrives",
    sourceStepIds: ["thor-arrives"],
    sourceIds: ["poetic-edda-lokasenna-1-65"],
    tracks: {
      en: track("norse-lokasenna-thor-arrives", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Thor threatens him repeatedly; Loki grants that he fights well and leaves Ægir's hall. A feast begins with a servant's death; Loki returns to the hall, the gods answer in turn, and he leaves before Thor. This is a contest of speech in one poem, not a biography built from every insult.",
      }),
      'zh-Hant-mandarin': track("norse-lokasenna-thor-arrives", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "托爾反覆威脅，洛基承認他善戰，離開埃吉爾的廳堂。一場宴席從侍者之死開始，洛基回到廳堂，眾神輪流回應，最後在托爾面前離席。這是詩中的言語較量，不是把每一句辱罵都當成傳記。",
      }),
    },
  },
  {
    sceneId: "norse-alvissmol-a-bride-expected",
    globalOrder: 37,
    storyId: "norse-alvissmol",
    stepId: "a-bride-expected",
    sourceStepIds: ["a-bride-expected", "the-father-returns"],
    sourceIds: ["poetic-edda-alvissmol-1-35"],
    tracks: {
      en: track("norse-alvissmol-a-bride-expected", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "When Alvis comes to the door, he treats the marriage as settled: the bride will grace his benches, and he is eager to take her home. The poem gives the bride no speech and begins instead with the suitor's confidence. Thor returns and stops that confidence at once. He says a father has first right in his daughter's marriage, particularly when an alleged promise was made in his absence.",
      }),
      'zh-Hant-mandarin': track("norse-alvissmol-a-bride-expected", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "阿爾維斯走到門前時，已把婚事當成定局：新娘會裝點他的座位，他也急著帶她回家。詩沒有讓新娘說話，而是先讓求婚者的期待佔滿場面。托爾回來後，立刻把這份期待停下來。他說女兒婚事的首要權利在父親手中，尤其是自己不在場時作出的許諾。這不是一段互訴心意的婚約，而是誰能替未出場的新娘決定的爭議。傳統把詩中無名的新娘辨認為特魯德，但詩句沒有說出名字。",
      }),
    },
  },
  {
    sceneId: "norse-alvissmol-desire-meets-a-condition",
    globalOrder: 38,
    storyId: "norse-alvissmol",
    stepId: "desire-meets-a-condition",
    sourceStepIds: ["desire-meets-a-condition", "earth-and-sky"],
    sourceIds: ["poetic-edda-alvissmol-1-35"],
    tracks: {
      en: track("norse-alvissmol-desire-meets-a-condition", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Alvis does not withdraw; he still asks Thor to permit the marriage. Thor replies that he will not stop him if he can name what Thor wants to know in every world. The offer looks open, yet it postpones the decision into an exchange whose rhythm Thor controls. The first questions concern the names of earth and heaven among different groups.",
      }),
      'zh-Hant-mandarin': track("norse-alvissmol-desire-meets-a-condition", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "阿爾維斯沒有退去，仍要求托爾允許這門婚事。托爾便說，若他真能說出各個世界的知識，自己不會阻止他得到新娘。看似留下了一條路，實際上把決定延後到一場由托爾掌握節奏的問答。第一批問題問大地與天穹在不同群體中的稱名。阿爾維斯答得流利，把人、眾神、華納、巨人、精靈與其他存在的詞語排在一起。這些回答讓他顯得博聞，卻不等於詩替讀者畫出一張固定的九界地圖。",
      }),
    },
  },
  {
    sceneId: "norse-alvissmol-moon-and-sun",
    globalOrder: 39,
    storyId: "norse-alvissmol",
    stepId: "moon-and-sun",
    sourceStepIds: ["moon-and-sun", "wind-sea-and-fire"],
    sourceIds: ["poetic-edda-alvissmol-1-35"],
    tracks: {
      en: track("norse-alvissmol-moon-and-sun", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "The examination moves from earth and heaven to moon and sun. Alvis can still list the names used by different groups; these lights are not enemies to conquer but subjects that make the exchange last another round. Thor neither disputes the answers nor returns to the bride. Then come wind, calm, sea, and fire. Alvis sets terms from Hel, giants, and Vanir beside one another.",
      }),
      'zh-Hant-mandarin': track("norse-alvissmol-moon-and-sun", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "盤問從大地與天穹轉到月亮和太陽。阿爾維斯仍能列出不同群體的稱名；日月在這裡不是要被征服的對手，而是讓問答一輪輪延長的題目。托爾既沒有爭論答案，也沒有讓話題回到新娘。接著是風、平靜、海與火。阿爾維斯把冥界、巨人與華納的說法並排，每一組詞都顯示詩偏向詩人如何換名，而不是某個神如何行動。故事因此有一種奇特的靜止感：兩人一直說話，婚事卻沒有前進。然而夜色本身正在前進。",
      }),
    },
  },
  {
    sceneId: "norse-alvissmol-wood-night-seed-and-ale",
    globalOrder: 40,
    storyId: "norse-alvissmol",
    stepId: "wood-night-seed-and-ale",
    sourceStepIds: ["wood-night-seed-and-ale", "day-arrives"],
    sourceIds: ["poetic-edda-alvissmol-1-35"],
    tracks: {
      en: track("norse-alvissmol-wood-night-seed-and-ale", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Wood, night, seed, and ale form the final round. Alvis can answer these as well, including terms placed with Vanaheim for seed and drink; his learning does not collapse, and Thor admits he has not seen greater store of old knowledge. Praise turns immediately into disclosure. Thor says that day has caught the dwarf and sunlight has entered the hall.",
      }),
      'zh-Hant-mandarin': track("norse-alvissmol-wood-night-seed-and-ale", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "木、夜、種子與酒成為最後一輪題目。阿爾維斯連這些也能回答，尤其把華納世界的說法列進種子與酒的詞語裡；他的學識沒有崩潰，托爾也承認自己未見過更豐富的古老知識。讚賞隨即轉成揭露。托爾說白晝已捉住侏儒，陽光已照入廳堂。詩在這一句停止，沒有描寫阿爾維斯接下來的形體，也沒有回頭交代新娘最後歸誰。這個結尾令前面的長問答有了另一層意思：托爾不是要贏得知識競賽，而是要把時間留到天亮。",
      }),
    },
  },
  {
    sceneId: "norse-harbarthsljoth-thor-reaches-sound",
    globalOrder: 41,
    storyId: "norse-harbarthsljoth",
    stepId: "thor-reaches-sound",
    sourceStepIds: ["thor-reaches-sound", "thor-asks-for-ferry"],
    sourceIds: ["poetic-edda-harbarthsljoth-1-56"],
    tracks: {
      en: track("norse-harbarthsljoth-thor-reaches-sound", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Thor is returning from the east when he hears someone speaking from the other side of a sound. He has not come seeking a contest, nor does he arrive with a victory already completed; first there is water and a boat that is not on his shore. He asks who is there, says he is travelling home, and asks the man to bring the boat across.",
      }),
      'zh-Hant-mandarin': track("norse-harbarthsljoth-thor-reaches-sound", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "托爾從東方返程，走到海峽邊時聽見有人在另一岸說話。他不是來尋找比試，也沒有帶著一段已完成的勝利故事；眼前先是一條水面與一艘不在自己這邊的船。他向對方問路，說自己正要回家，請那人把船帶過來。這是一個很小、很直接的要求，卻立刻把行程交到陌生人的手裡：托爾能否繼續前行，取決於對方願不願意靠岸。詩沒有替海峽定出地名，也沒有先交代掌船者的來歷。",
      }),
    },
  },
  {
    sceneId: "norse-harbarthsljoth-harbard-starts-mocking",
    globalOrder: 42,
    storyId: "norse-harbarthsljoth",
    stepId: "harbard-starts-mocking",
    sourceStepIds: ["harbard-starts-mocking", "names-across-sound"],
    sourceIds: ["poetic-edda-harbarthsljoth-1-56"],
    tracks: {
      en: track("norse-harbarthsljoth-harbard-starts-mocking", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "The man on the far bank does not agree; he begins by making fun of Thor's dress and hunger. Thor hears not a ferryman's terms but words meant to humiliate him, and the crossing becomes a contest neither man will yield. Thor then says that he is Odin's son. The other man does not offer a longer history; he answers only that his name is Harbard.",
      }),
      'zh-Hant-mandarin': track("norse-harbarthsljoth-harbard-starts-mocking", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "對岸的人沒有應允，反而先取笑托爾的衣著與飢餓。托爾聽到的不是擺渡人的條件，而是一連串要令他難堪的話；過河從一件可以安排的小事，變成兩人誰也不肯讓步的較量。托爾於是說自己是奧丁之子。對方沒有交出更長的身分來歷，只回答自己的名字是哈爾巴德。這個名字足以讓對話繼續，卻沒有消除隔岸之人的陌生感。貝洛斯的導言以奧丁的化身來閱讀這名擺渡人，但詩句本身仍只讓他自稱哈爾巴德。",
      }),
    },
  },
  {
    sceneId: "norse-harbarthsljoth-boasts-face-across-water",
    globalOrder: 43,
    storyId: "norse-harbarthsljoth",
    stepId: "boasts-face-across-water",
    sourceStepIds: ["boasts-face-across-water", "glove-taunt-and-threat"],
    sourceIds: ["poetic-edda-harbarthsljoth-1-56"],
    tracks: {
      en: track("norse-harbarthsljoth-boasts-face-across-water", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Thor answers contempt with exploits, and Harbard says that he has stirred wars among rulers. Both men make their claims large, but the poem is not keeping biographies that can be checked away from the scene; each is trying to stand firm before the other. Harbard then turns a story about a glove into ridicule. Thor answers with a threat, still without getting the boat.",
      }),
      'zh-Hant-mandarin': track("norse-harbarthsljoth-boasts-face-across-water", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "托爾用戰功回應輕蔑，哈爾巴德也說起自己如何在王侯之間煽動戰爭。兩人都把話說得很大，但這不是替誰記下一份可以離開現場查驗的生平；它是要在對方面前站穩的說法。哈爾巴德接著把托爾與一隻手套的故事拿來取笑。托爾以威脅回答，仍沒有得到船。言語在此不再只是消磨時間：每一次回嘴都令兩人更難回到最初那個簡單的請求。水面保持原狀，船也留在對岸。",
      }),
    },
  },
  {
    sceneId: "norse-harbarthsljoth-sif-allegation-refused",
    globalOrder: 44,
    storyId: "norse-harbarthsljoth",
    stepId: "sif-allegation-refused",
    sourceStepIds: ["sif-allegation-refused", "no-passage-detour"],
    sourceIds: ["poetic-edda-harbarthsljoth-1-56"],
    tracks: {
      en: track("norse-harbarthsljoth-sif-allegation-refused", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Harbard carries the taunt to Sif, saying that she has a lover. Thor does not accept the line and calls him a liar to his face. The poem leaves the allegation in its speaker's mouth, without asking the reader to supply truth or consequence. At last Thor orders Harbard to bring the boat.",
      }),
      'zh-Hant-mandarin': track("norse-harbarthsljoth-sif-allegation-refused", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "哈爾巴德把挑釁帶到西芙，說她有情人。托爾沒有接受這句話，當面說他在說謊。詩讓這項指控停留在發話者口中，不要求讀者替它補上真相或後果。最後托爾命哈爾巴德把船帶來。哈爾巴德仍拒絕，指向一條要走很久的陸路；他不是給出折衷方案，而是把最初的障礙原封不動地留在托爾面前。詩到此收住。",
      }),
    },
  },
  {
    sceneId: "norse-grimnismol-brothers-lost-at-sea",
    globalOrder: 45,
    storyId: "norse-grimnismol",
    stepId: "brothers-lost-at-sea",
    sourceStepIds: ["brothers-lost-at-sea", "fosterling-king-wager"],
    sourceIds: ["poetic-edda-grimnismol-frame-1-54"],
    tracks: {
      en: track("norse-grimnismol-brothers-lost-at-sea", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "The manuscript prose opening says that Hrauthung's two sons, Agnar and Geirroth, are separated at sea. The young Agnar is taken in by Frigg, while Geirroth comes to Odin. This is not a tale of the brothers fighting each other; it is about two different fosterings that bring them back under the same horizon of kingship. Geirroth returns to his father first and takes the throne; years later, Agnar too is king.",
      }),
      'zh-Hant-mandarin': track("norse-grimnismol-brothers-lost-at-sea", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "手稿散文的引子說，赫勞東的兩個兒子阿格納與葛爾羅德在海上失散。年幼的阿格納被弗麗嘉收留；葛爾羅德則到了奧丁身邊。這不是一場兩兄弟彼此爭鬥的故事，而是兩段不同撫養如何把他們帶回同一片王權之下。葛爾羅德先回到父親身邊，坐上王位；多年後，阿格納也成了國王。奧丁與弗麗嘉於是為各自撫養的孩子打賭：哪一位會是較好的統治者。",
      }),
    },
  },
  {
    sceneId: "norse-grimnismol-fullas-warning",
    globalOrder: 46,
    storyId: "norse-grimnismol",
    stepId: "fullas-warning",
    sourceStepIds: ["fullas-warning", "grimnir-between-fires"],
    sourceIds: ["poetic-edda-grimnismol-frame-1-54"],
    tracks: {
      en: track("norse-grimnismol-fullas-warning", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Frigg sends Fulla to warn Geirroth that a visitor will be hard to harm with weapons, and that he can be recognized because he never speaks with dogs. It is news from Frigg's side of the wager, not an identity paper that names the stranger in advance. Odin then comes to the hall under the name Grimnir. Geirroth sees that he will not speak with dogs, takes the warning as true, and chooses neither recognition nor questions.",
      }),
      'zh-Hant-mandarin': track("norse-grimnismol-fullas-warning", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "弗麗嘉派富拉去警告葛爾羅德：有一名來客會施法，使刀劍傷不了他；能認出他的方法，是看他從不與狗說話。這是弗麗嘉一方給國王的消息，不是一張替陌生人預先揭名的身分紙。奧丁隨後以格里姆尼爾之名來到宮中。葛爾羅德看見他不肯同狗交談，便把警告當真，卻沒有選擇相認或詢問；他把這名陌生人夾在兩團火之間，讓他受了八夜折磨。故事的殘酷不在於國王缺少線索，而在於他把線索變成了懲罰的理由。",
      }),
    },
  },
  {
    sceneId: "norse-grimnismol-agnar-brings-horn",
    globalOrder: 47,
    storyId: "norse-grimnismol",
    stepId: "agnar-brings-horn",
    sourceStepIds: ["agnar-brings-horn", "halls-named-from-fire"],
    sourceIds: ["poetic-edda-grimnismol-frame-1-54"],
    tracks: {
      en: track("norse-grimnismol-agnar-brings-horn", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "The young Agnar comes to the space between the fires and gives Grimnir a horn full of drink, saying that his father should not make a man suffer without cause. Grimnir drinks and promises that this kindness will bring Agnar a reward; the first person in the hall to ease his pain is not the king. He then speaks from the fire of the gods' halls, lands, and keepers.",
      }),
      'zh-Hant-mandarin': track("norse-grimnismol-agnar-brings-horn", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "年少的阿格納走近兩火之間，遞給格里姆尼爾一隻裝滿酒的角，說父親不該讓一個人無故受苦。格里姆尼爾喝下酒，答應這份善意會讓阿格納得到回報；宮廷裡第一個實際減輕他痛苦的人，不是國王。接著，他從火邊說起眾神的居處、土地與看守者。這些長長的名稱不是一條可沿途走完的地圖：它們是在受困者口中展開的知識，也把原本被迫沉默的陌生人變成掌握話語節奏的人。",
      }),
    },
  },
  {
    sceneId: "norse-grimnismol-many-names-one-speaker",
    globalOrder: 48,
    storyId: "norse-grimnismol",
    stepId: "many-names-one-speaker",
    sourceStepIds: ["many-names-one-speaker", "odin-revealed-sword-falls"],
    sourceIds: ["poetic-edda-grimnismol-frame-1-54"],
    tracks: {
      en: track("norse-grimnismol-many-names-one-speaker", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "At the end of the recital, Grimnir gives a succession of names and epithets, finally naming Odin. This does not divide every name into a new god; one speaker in the fire is bringing his identity, layer by layer, before Geirroth. Geirroth at last understands whom he has made suffer and rises in haste to save him. But the manuscript's closing prose says that he slips as he draws his sword and falls onto it.",
      }),
      'zh-Hant-mandarin': track("norse-grimnismol-many-names-one-speaker", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "到誦說的末尾，格里姆尼爾連續報出許多名字與稱號，最後說出奧丁的名字。這不是把每個名字拆成新的神祇，而是一名說話者在火中把自己的身分逐層推到葛爾羅德面前。葛爾羅德終於明白自己讓誰受苦，連忙起身去救他。可手稿結尾散文說，他拔劍時失足跌倒，自己的劍落在他身上。剛才仍坐在王位上的人，沒有等到補救完成便已死去。阿格納繼承王國；格里姆尼爾的承諾落在行動而非血統的次序上。",
      }),
    },
  },
  {
    sceneId: "norse-skirnismol-watchtower",
    globalOrder: 49,
    storyId: "norse-skirnismol",
    stepId: "watchtower",
    sourceStepIds: ["watchtower", "confession"],
    sourceIds: ["poetic-edda-skirnismol-1-42-freyr-gerd"],
    tracks: {
      en: track("norse-skirnismol-watchtower", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Freyr sits in Hlithskjolf and looks into the giant world, seeing Gerd, Gymir's daughter. Light from her arms shines over sea and sky, but gives him no peace. Njord sends Skirnir to ask why. Recalling their trust from youth, Skirnir draws out Freyr's wish to be with Gerd and his sense that no god or elf will permit it. The poem first writes desire as silence and imbalance, not as love already returned.",
      }),
      'zh-Hant-mandarin': track("norse-skirnismol-watchtower", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "弗雷坐在赫利德斯凱爾夫，從那裡望進巨人世界，看見格米爾的女兒格爾德。光從她的手臂映到海與天，他卻沒有因此得到安寧。尼奧爾德要斯基爾尼爾問明原因。斯基爾尼爾憑兩人年少時的信任追問，弗雷才說出自己想與格爾德同在，卻覺得沒有人會允許。故事先把欲求寫成沉默與失衡，而非已獲回應的愛情。",
      }),
    },
  },
  {
    sceneId: "norse-skirnismol-errand",
    globalOrder: 50,
    storyId: "norse-skirnismol",
    stepId: "errand",
    sourceStepIds: ["errand", "arrival"],
    sourceIds: ["poetic-edda-skirnismol-1-42-freyr-gerd"],
    tracks: {
      en: track("norse-skirnismol-errand", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Skirnir asks for the horse that crosses dark and flame and a sword against giants. Freyr gives them, and the messenger rides into the giant world. Fierce dogs stand before Gymir's home; a herdsman says Skirnir is either doomed or dead already. Still he enters Gerd's hall. Gerd first fears that the visitor outside is her brother's slayer, so the meeting begins in danger and hostility.",
      }),
      'zh-Hant-mandarin': track("norse-skirnismol-errand", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 38,
        text: "斯基爾尼爾索要能穿越黑暗與火焰的馬，以及可對巨人作戰的劍。弗雷交出這些東西，使者便騎入巨人世界。格米爾家門前有兇犬；山上的牧人說他不是注定要死，就是已經死了。斯基爾尼爾仍進入格爾德的廳堂。格爾德先怕外頭來的是殺兄之人，這場會面一開始便帶著敵意與危險。",
      }),
    },
  },
  {
    sceneId: "norse-skirnismol-gifts",
    globalOrder: 51,
    storyId: "norse-skirnismol",
    stepId: "gifts",
    sourceStepIds: ["gifts", "threat"],
    sourceIds: ["poetic-edda-skirnismol-1-42-freyr-gerd"],
    tracks: {
      en: track("norse-skirnismol-gifts", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Skirnir first offers Gerd eleven golden apples, then a ring that makes eight more every ninth night. She refuses both, saying her father's house lacks no gold and she will not dwell with Freyr. He then shows his sword, saying he will cut off her head and threatening her father. Gerd still does not yield, saying that Gymir will fight if he finds Skirnir there.",
      }),
      'zh-Hant-mandarin': track("norse-skirnismol-gifts", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 40,
        text: "斯基爾尼爾先給格爾德十一個金蘋果，再給每九夜生出八環的金環。她兩次拒絕，說父家不缺黃金，也不願和弗雷同住。他於是亮出劍，說會砍下她的頭，又威脅她的父親。格爾德沒有因此改口，反說格米爾若發現他在此，會來戰鬥。詩把禮物與武力緊連，不能把這段出使說成單純的戀愛遊說。",
      }),
    },
  },
  {
    sceneId: "norse-skirnismol-curse",
    globalOrder: 52,
    storyId: "norse-skirnismol",
    stepId: "curse",
    sourceStepIds: ["curse", "return"],
    sourceIds: ["poetic-edda-skirnismol-1-42-freyr-gerd"],
    tracks: {
      en: track("norse-skirnismol-curse", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Skirnir turns to a magic wand and curse, threatening Gerd with isolation, humiliation, longing, and never having a husband. The passage has damaged and rearranged lines, yet it does not erase the pressure of the curse. Gerd then offers him mead and says that in nine nights she will meet Njord's son at Barri. Skirnir returns, and Freyr asks only when he may hear news. He receives only Barri and nine nights.",
      }),
      'zh-Hant-mandarin': track("norse-skirnismol-curse", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "斯基爾尼爾改用魔杖與咒語，威脅格爾德孤絕、羞辱、欲求與永無婚配。這段行文有缺字與編排問題，卻沒有抹去咒語施加的壓力。格爾德隨後請他收下蜜酒，說九夜後會在巴里與尼奧爾德之子相會。斯基爾尼爾回去，弗雷只問何時能有消息。他得到的只是巴里與九夜。詩在第九夜到來之前停下，沒有替這個約期寫成已發生的結合。",
      }),
    },
  },
  {
    sceneId: "norse-thor-giant-tests-hammer-stolen",
    globalOrder: 53,
    storyId: "norse-thor-giant-tests",
    stepId: "hammer-stolen",
    sourceStepIds: ["hammer-stolen", "thrym-demand", "bridal-disguise", "hammer-recovered"],
    sourceIds: ["poetic-edda-thrymskvitha-1-12-hammer-stolen", "poetic-edda-thrymskvitha-13-21-disguise", "poetic-edda-thrymskvitha-22-33-recovery"],
    tracks: {
      en: track("norse-thor-giant-tests-hammer-stolen", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Thor's route begins with the loss of his clearest sign of force. Mjolnir is missing, and Thrym demands Freyja as the price for returning it. When Freyja refuses, Loki and the gods drive a disguise plot. Thor goes as the bride, nearly exposing himself through appetite and rage, while Loki explains each oddity away. When the hammer is placed in the bride's lap to bless the marriage, Thor regains his power.",
      }),
      'zh-Hant-mandarin': track("norse-thor-giant-tests-hammer-stolen", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "托爾的故事先從失去力量標誌開始。妙爾尼爾不見了，索列姆提出以芙蕾雅為代價才歸還錘；神族面對的不是公開戰鬥，而是一場把婚姻、寶物和羞辱綁在一起的勒索。芙蕾雅拒絕後，洛基與海姆達爾等神明推動偽裝計策，托爾被迫扮成新娘前往巨人世界。宴席上，他的食量和怒氣幾乎暴露身份，洛基則把異常一一說成新娘等待婚事的反應。當妙爾尼爾被放到「新娘」膝上祝聖婚禮時，托爾重新握住自己的力量。",
      }),
    },
  },
  {
    sceneId: "norse-thor-giant-tests-goats-and-servants",
    globalOrder: 54,
    storyId: "norse-thor-giant-tests",
    stepId: "goats-and-servants",
    sourceStepIds: ["goats-and-servants", "skrymir-threshold"],
    sourceIds: ["prose-edda-gylfaginning-44-goats-thialfi", "prose-edda-gylfaginning-45-skrymir", "prose-edda-gylfaginning-47-illusion-revealed"],
    tracks: {
      en: track("norse-thor-giant-tests-goats-and-servants", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Another set of tests begins on the road. Thor serves his goats as food and commands that no bone be broken; when Thialfi breaks the rule, the revived goat is lamed and the human household pays the cost. Thialfi and Roskva then follow Thor into the giant-world threshold. Skrymir's immense body and strange companionship place Thor's strength into an unstable scale. This is more than travel.",
      }),
      'zh-Hant-mandarin': track("norse-thor-giant-tests-goats-and-servants", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "另一組試煉從旅途開始。托爾帶着洛基前行，以自己的山羊供餐，又要求骨頭不得折斷；夏爾菲犯禁後，山羊復活時留下跛足，神明的食物規則立刻變成凡人家庭的代價。夏爾菲與羅斯克瓦因此跟隨托爾，旅途也進入巨人世界的門檻。斯克里米爾的巨大身體、手套與看似輕鬆的同行，使托爾的力量第一次被放到不穩定的尺度中；他的錘擊看似打中，實際效果卻被巨人世界吸收或轉化。這段不是單純旅行插曲。",
      }),
    },
  },
  {
    sceneId: "norse-thor-giant-tests-utgard-contests",
    globalOrder: 55,
    storyId: "norse-thor-giant-tests",
    stepId: "utgard-contests",
    sourceStepIds: ["utgard-contests", "thor-illusion-tests"],
    sourceIds: ["prose-edda-gylfaginning-46-utgard-tests", "prose-edda-gylfaginning-47-illusion-revealed"],
    tracks: {
      en: track("norse-thor-giant-tests-utgard-contests", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Utgard turns the travellers' abilities into contests. Loki eats, Thialfi runs, and Thor drinks from a horn, lifts a cat, and wrestles an old woman. Each loss hides another scale. The ruler reveals the illusions: Loki faced fire, Thialfi chased thought, Thor drank from the sea, lifted the Midgard Serpent, and wrestled old age. The scene pushes heroic strength to cosmic limits.",
      }),
      'zh-Hant-mandarin': track("norse-thor-giant-tests-utgard-contests", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "烏特加德把托爾一行人的能力變成競賽。洛基比食量，夏爾菲比奔跑，托爾則先後喝角中之酒、舉起巨貓、又與老婦角力；每一場看似失敗，實際都被幻術藏起真正尺度。巨人首領最後揭開真相：洛基面對的是火，夏爾菲追不上思想，托爾喝的是大海，舉的是耶夢加得，角力對手則是老年。這不是嘲笑托爾無力，而是顯示他的力量已經觸及世界級的對象，只是他當時不知道自己面對的是甚麼。",
      }),
    },
  },
  {
    sceneId: "norse-thor-giant-tests-hymir-serpent-fishing",
    globalOrder: 56,
    storyId: "norse-thor-giant-tests",
    stepId: "hymir-serpent-fishing",
    sourceStepIds: ["hymir-serpent-fishing"],
    sourceIds: ["prose-edda-gylfaginning-47-illusion-revealed", "prose-edda-gylfaginning-48-hymir-fishing", "poetic-edda-hymiskvitha-17-25-serpent"],
    tracks: {
      en: track("norse-thor-giant-tests-hymir-serpent-fishing", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Thor's relation to the serpent becomes direct again on Hymir's boat. This time the Midgard Serpent is not an illusion-cat but a real enemy drawn up by a bull's head bait. Thor raises the hammer to strike, but Hymir cuts the line in fear. The source keeps the confrontation interrupted: Thor has brought the serpent close, but the final battle does not happen here. The scene becomes a postponed doom rehearsal.",
      }),
      'zh-Hant-mandarin': track("norse-thor-giant-tests-hymir-serpent-fishing", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "托爾與巨蛇的關係在希米爾船上再次變得直接。這一次不是烏特加德幻術中的巨貓，而是耶夢加得被牛頭作餌釣上來；托爾把戰鬥拉到海面之上，末日敵手短暫提前相遇。當巨蛇露出水面，托爾舉錘要擊殺牠，希米爾卻因恐懼割斷釣線。來源保留了這場對峙的中斷：托爾的力量已經足以把巨蛇拉近，但故事不讓最後決戰在此完成。這段因此像一次未能落下的末日預演。",
      }),
    },
  },
  {
    sceneId: "norse-thrymskvitha-missing-hammer",
    globalOrder: 57,
    storyId: "norse-thrymskvitha",
    stepId: "missing-hammer",
    sourceStepIds: ["missing-hammer", "feather-dress"],
    sourceIds: ["poetic-edda-thrymskvitha-1-12-hammer-stolen"],
    tracks: {
      en: track("norse-thrymskvitha-missing-hammer", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Thor wakes with Mjolnir gone. He searches about him, beard shaking, and tells Loki that the hammer has been stolen, though no one on earth or in heaven knows how. They go to Freyja's hall, where Loki borrows her feather-dress and flies into giant land. He finds Thrym on a mound, fastening golden leashes on dogs and smoothing the manes of his horses, as if waiting to be questioned. What is lost is not replaceable.",
      }),
      'zh-Hant-mandarin': track("norse-thrymskvitha-missing-hammer", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "托爾醒來時，妙爾尼爾已不在身邊。他翻找四周，鬍鬚震動，先把這件事告訴洛基：人間和天上都不知道，錘卻已被偷走。兩人走到芙蕾雅的住處，洛基借走她的羽衣，飛入巨人之地。他在土丘上找到索列姆；巨人正替狗繫金繩、替馬梳鬃，像是早已等着有人前來問話。失去的不是一件可隨時替代的器物。托爾沒有錘，神族便要先用查問與飛行去追回原本能以力量守住的東西。",
      }),
    },
  },
  {
    sceneId: "norse-thrymskvitha-demand",
    globalOrder: 58,
    storyId: "norse-thrymskvitha",
    stepId: "demand",
    sourceStepIds: ["demand", "freyja-refuses"],
    sourceIds: ["poetic-edda-thrymskvitha-1-12-hammer-stolen"],
    tracks: {
      en: track("norse-thrymskvitha-demand", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Thrym admits that he has buried Thor's hammer deep underground and says no one will take it back unless Freyja becomes his wife. Loki returns to the gods' home and repeats the condition exactly. The gods ask Freyja to put on the bridal veil. Her anger shakes the hall and breaks Brisingamen at her breast; she says all the gods would call her shameless if she went to giant land. She does not become the price of exchange.",
      }),
      'zh-Hant-mandarin': track("norse-thrymskvitha-demand", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "索列姆承認自己把托爾的錘埋在地下深處，並說除非芙蕾雅成為他的妻子，誰也不能把它帶回。洛基回到神族住處，把這個條件原原本本說出來。眾神於是要芙蕾雅披上新娘頭紗。她怒得讓整座大廳震動，布里辛加曼也在胸前斷裂；她說若真的前往巨人之地，所有神明都會把她看成最不知羞恥的人。她沒有成為交換品。她的拒絕把索列姆原本想用婚事鎖住的條件推回眾神手中，逼他們想出另一種取錘的辦法。",
      }),
    },
  },
  {
    sceneId: "norse-thrymskvitha-council",
    globalOrder: 59,
    storyId: "norse-thrymskvitha",
    stepId: "council",
    sourceStepIds: ["council", "wedding-road"],
    sourceIds: ["poetic-edda-thrymskvitha-13-21-disguise"],
    tracks: {
      en: track("norse-thrymskvitha-council", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Gods and goddesses gather in council. Heimdall says that Thor should take the bridal veil and Brisingamen, with a skirt to the knees, keys at the waist, gems on the breast, and a cap to complete the disguise. Thor first refuses, saying people will call him womanly. Loki answers that giants will live in Asgard if Mjolnir is not brought home. Thor puts on the attire, and Loki says he will go as serving-maid.",
      }),
      'zh-Hant-mandarin': track("norse-thrymskvitha-council", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "眾神與女神聚在一起商議。海姆達爾說，讓托爾戴上新娘頭紗，再把布里辛加曼掛在胸前；裙襬垂到膝頭，鑰匙在腰間作響，寶石和帽子把偽裝做全。托爾起初不肯，說人們會笑他像女人。洛基卻提醒他，若妙爾尼爾不能帶回，巨人就會住進阿斯加德。於是托爾穿上裝束，洛基也說自己要作侍女同行。山羊拉着車衝向巨人之地，山岩震裂，土地如被火燒。",
      }),
    },
  },
  {
    sceneId: "norse-thrymskvitha-feast",
    globalOrder: 60,
    storyId: "norse-thrymskvitha",
    stepId: "feast",
    sourceStepIds: ["feast", "recovery"],
    sourceIds: ["poetic-edda-thrymskvitha-22-33-recovery"],
    tracks: {
      en: track("norse-thrymskvitha-feast", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Thrym believes Freyja has arrived and orders a wedding feast. The false bride eats an ox, eight salmon, all the food set for women, and three tuns of mead; Thrym marvels at a bride with such an appetite. Loki sits nearby and says that 'Freyja' has neither eaten nor slept for eight nights because she longed so for giant land.",
      }),
      'zh-Hant-mandarin': track("norse-thrymskvitha-feast", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "索列姆以為芙蕾雅已到了，命巨人準備婚宴。假新娘一人吃下一頭牛、八條鮭魚和所有為女子準備的美食，又喝下三桶蜜酒；索列姆驚訝得問，哪有新娘這樣進食。洛基坐在旁邊，說「芙蕾雅」因為太想來巨人之地，八夜沒有吃過，也八夜沒有睡過。索列姆掀開頭紗想吻她，見到怒目如火，洛基又立刻替她把異樣說成八夜未眠的渴望。最後索列姆命人把妙爾尼爾放到新娘膝上，替兩人的婚事祝聖。",
      }),
    },
  },
  {
    sceneId: "norse-geirrod-journey-geirrod-captures-loki",
    globalOrder: 61,
    storyId: "norse-geirrod-journey",
    stepId: "geirrod-captures-loki",
    sourceStepIds: ["geirrod-captures-loki", "loki-ransoms-life", "gridr-warns-thor"],
    sourceIds: ["prose-edda-skaldskaparmal-18-geirrod"],
    tracks: {
      en: track("norse-geirrod-journey-geirrod-captures-loki", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Geirrod captures Loki in hawk form, starves him for three months, and forces an oath to bring Thor without Mjolnir or the strength-girdle. On the way Thor stays with Gridr, who warns him and lends girdle, iron gloves, and her staff. The account also calls her Vidar's mother. Thor does not enter wholly unprepared, but his protection is now borrowed rather than customary.",
      }),
      'zh-Hant-mandarin': track("norse-geirrod-journey-geirrod-captures-loki", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "蓋羅德捉住鷹形的洛基，三個月不給食物，逼他以誓言換命。洛基答應把托爾帶到巨人住處，並讓他不帶妙爾尼爾與力量腰帶。托爾途中住進格里德家。她明說蓋羅德狡詐難纏，借出力量腰帶、鐵手套和自己的杖；來源也稱她是沉默的維達爾之母。托爾並非毫無準備地踏入陷阱，但他能使用的防身之物已不再是原本習以為常的武器。這條路的壓力由洛基的求生選擇開始，再由格里德的具體援助改變。",
      }),
    },
  },
  {
    sceneId: "norse-geirrod-journey-vimur-rises",
    globalOrder: 62,
    storyId: "norse-geirrod-journey",
    stepId: "vimur-rises",
    sourceStepIds: ["vimur-rises", "gjalp-at-source", "chair-trap-at-geirrod-hall", "thor-crushes-daughters"],
    sourceIds: ["prose-edda-skaldskaparmal-18-geirrod"],
    tracks: {
      en: track("norse-geirrod-journey-vimur-rises", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "At Vimur the water rises to Thor's shoulders, with Loki holding on behind the girdle. Thor sees Gjalp causing the spate from the ravines. He throws a stone and reaches shore by a rowan. In Geirrod's hall, Gjalp and Greip raise his chair toward the roof, and Thor braces Gridr's staff against the rafters. The route makes river and hall into linked ambushes: one would stop him on the way, the other in his seat.",
      }),
      'zh-Hant-mandarin': track("norse-geirrod-journey-vimur-rises", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "托爾涉入維穆爾時，河水升到肩頭；洛基抓住力量腰帶跟在後方。托爾看見加爾普橫跨山溝，正令洪水暴漲。他向水源擲石，靠花楸登岸。到了蓋羅德的廳堂，加爾普與格雷普又藏在唯一的椅子下，讓托爾向屋頂升去；托爾以格里德之杖抵住屋樑，反壓二人。來源把旅程拆成河流與屋內的連續阻攔：一重想把他困在路上，另一重想把他困在座位上。托爾能前進，是因為每次都把對方的安排變成可以反制的支點。",
      }),
    },
  },
  {
    sceneId: "norse-geirrod-journey-thor-kills-geirrod",
    globalOrder: 63,
    storyId: "norse-geirrod-journey",
    stepId: "thor-kills-geirrod",
    sourceStepIds: ["thor-kills-geirrod"],
    sourceIds: ["prose-edda-skaldskaparmal-18-geirrod"],
    tracks: {
      en: track("norse-geirrod-journey-thor-kills-geirrod", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "In the fire-filled hall, Geirrod takes a glowing iron bar, hides behind an iron pillar, and throws it at Thor. Thor catches it with the iron gloves and returns it through Geirrod, pillar, wall, and into the earth. The ending does not give Thor victory by his customary hammer. Gridr's borrowed gloves receive Geirrod's final attack, and the trap's owner is struck by his own weapon.",
      }),
      'zh-Hant-mandarin': track("norse-geirrod-journey-thor-kills-geirrod", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "蓋羅德在滿是火的廳堂裡拿起燒紅鐵條，躲到鐵柱後再擲向托爾。托爾以鐵手套接住它，把鐵條擲回，穿過蓋羅德、鐵柱和牆，直入地中。故事的結尾不讓托爾以慣用的錘取勝，而讓格里德借出的手套承接蓋羅德最後的攻擊。陷阱的主人被自己的器物擊中，旅程的每一次借力也在此收束。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-feast-needs-kettle",
    globalOrder: 64,
    storyId: "norse-hymiskvitha",
    stepId: "feast-needs-kettle",
    sourceStepIds: ["feast-needs-kettle"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-feast-needs-kettle", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Ægir is asked to prepare a feast for the gods but lacks a kettle large enough for the ale. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-feast-needs-kettle", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 22,
        text: "埃吉爾被要求為眾神設宴，卻缺少能煮夠酒的大鍋。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-tyr-knows-house-beyond-waves",
    globalOrder: 65,
    storyId: "norse-hymiskvitha",
    stepId: "tyr-knows-house-beyond-waves",
    sourceStepIds: ["tyr-knows-house-beyond-waves"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-tyr-knows-house-beyond-waves", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 24,
        text: "Tyr says Hymir lives east of Elivagar and calls him father in this poem. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-tyr-knows-house-beyond-waves", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "提爾說希米爾住在伊利伐加爾以東，並在本詩中稱他為父親。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-guests-beneath-kettles",
    globalOrder: 66,
    storyId: "norse-hymiskvitha",
    stepId: "guests-beneath-kettles",
    sourceStepIds: ["guests-beneath-kettles"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-guests-beneath-kettles", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Thor and Tyr reach Hymir's home, hide at first, and then eat at the giant's table. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-guests-beneath-kettles", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "托爾與提爾抵達希米爾家，先躲起來，再與巨人同桌進食。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-ox-head-for-sea",
    globalOrder: 67,
    storyId: "norse-hymiskvitha",
    stepId: "ox-head-for-sea",
    sourceStepIds: ["ox-head-for-sea"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-ox-head-for-sea", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Hymir sends Thor for bait; Thor takes a black ox's head and urges him farther offshore. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-ox-head-for-sea", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 24,
        text: "希米爾要托爾找釣餌；托爾折下黑牛的頭，催他把船划得更遠。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-serpent-reaches-boat",
    globalOrder: 68,
    storyId: "norse-hymiskvitha",
    stepId: "serpent-reaches-boat",
    sourceStepIds: ["serpent-reaches-boat"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-serpent-reaches-boat", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Thor hooks the world-serpent with the ox head and strikes it; this poem lets it sink back into the sea. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-serpent-reaches-boat", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "托爾以牛頭釣起世界蛇，揮錘擊打；這首詩讓牠沉回海中。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-strength-tested-way-home",
    globalOrder: 69,
    storyId: "norse-hymiskvitha",
    stepId: "strength-tested-way-home",
    sourceStepIds: ["strength-tested-way-home"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-strength-tested-way-home", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Thor carries boat and gear home, then must break the cup Hymir uses as a strength test. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-strength-tested-way-home", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "托爾扛回船與器具，接著必須打破希米爾用來試力的酒杯。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-kettle-too-heavy-tyr",
    globalOrder: 70,
    storyId: "norse-hymiskvitha",
    stepId: "kettle-too-heavy-tyr",
    sourceStepIds: ["kettle-too-heavy-tyr"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-kettle-too-heavy-tyr", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 24,
        text: "Hymir makes the kettle the final trial; Tyr fails, and Thor carries it away. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-kettle-too-heavy-tyr", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "希米爾把大鍋列為最後試驗；提爾失敗，托爾將它扛走。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "norse-hymiskvitha-ale-at-aegirs-hall",
    globalOrder: 71,
    storyId: "norse-hymiskvitha",
    stepId: "ale-at-aegirs-hall",
    sourceStepIds: ["ale-at-aegirs-hall"],
    sourceIds: ["poetic-edda-hymiskvitha-1-40"],
    tracks: {
      en: track("norse-hymiskvitha-ale-at-aegirs-hall", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Hymir brings pursuers; Thor clears them with Mjollnir and finally brings the kettle to Ægir. Ægir's feast needs a great kettle. Thor and Tyr cross the storm-waves, hook the world-serpent, and bring Hymir's kettle back to the gods.",
      }),
      'zh-Hant-mandarin': track("norse-hymiskvitha-ale-at-aegirs-hall", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "希米爾帶追兵而來，托爾用妙爾尼爾清除追擊，最後把大鍋帶回埃吉爾。埃吉爾的宴席需要一口大鍋。托爾與提爾越過風暴水域，先釣起世界蛇，再把希米爾的大鍋帶回眾神。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-fenrir-dangerous-kindred",
    globalOrder: 72,
    storyId: "binding-of-fenrir",
    stepId: "fenrir-dangerous-kindred",
    sourceStepIds: ["fenrir-dangerous-kindred", "tyr-feeds-growing-wolf"],
    sourceIds: ["prose-edda-gylfaginning-34-loki-children", "prose-edda-gylfaginning-34-fenrir-growth", "prose-edda-gylfaginning-34-ragnarok-odin", "prose-edda-gylfaginning-25-tyr-fenrir"],
    tracks: {
      en: track("binding-of-fenrir-fenrir-dangerous-kindred", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Fenrir's story does not begin with the bitten hand. It begins when the gods hear prophecy about Loki and Angrboda's children and bring that danger before them. Fenrir grows under divine eyes. The other gods fear approaching him, while Tyr alone feeds him, leaving a trace of trust before any binding takes place. The opening joins prophecy, kinship, and rule.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-fenrir-dangerous-kindred", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "芬里爾故事不是從咬斷手開始，而是從神明聽見預言開始。洛基與安格爾波達的子女在巨人世界長大，預言卻說災禍會由這一族而來；奧丁於是把危機帶到神族面前。芬里爾在眾神眼前長大，其他神明越來越不敢接近，只有提爾願意餵養牠。這份日常靠近使提爾與狼之間先有信任痕跡，也讓神族的恐懼不再只是遠方傳聞。這段開場把預言、親族和治理困境放在同一處。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-laeding-breaks",
    globalOrder: 73,
    storyId: "binding-of-fenrir",
    stepId: "laeding-breaks",
    sourceStepIds: ["laeding-breaks", "dromi-breaks"],
    sourceIds: ["prose-edda-gylfaginning-34-laeding-dromi"],
    tracks: {
      en: track("binding-of-fenrir-laeding-breaks", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "When Fenrir's strength can no longer be ignored, the gods first test him with Laedingr. They frame the fetter as a matter of strength and fame, but Fenrir snaps it apart. Dromi is stronger, and the challenge feels more dangerous. Fenrir still accepts, and the second fetter also breaks. The failures move the story from strength to craft and deception.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-laeding-breaks", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "當芬里爾的力量變得無法忽視，神族先以Lædingr試探牠。他們把束縛說成試力和名聲，芬里爾接受挑戰，一掙便把鐵鏈崩斷；第一次控制失敗反而證明牠更可怕。第二條Drómi更強，話術也更接近陷阱。芬里爾知道難度提高，仍然讓他們綁上；結果鐵鏈再次被扯斷，神族明白不能只用更重的鐵去回答這頭狼。兩次失敗把故事從力量競賽推向欺瞞與工藝。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-gleipnir-from-dark-elves",
    globalOrder: 74,
    storyId: "binding-of-fenrir",
    stepId: "gleipnir-from-dark-elves",
    sourceStepIds: ["gleipnir-from-dark-elves", "pledge-at-lyngvi"],
    sourceIds: ["prose-edda-gylfaginning-34-gleipnir-made", "prose-edda-gylfaginning-34-lyngvi-pledge"],
    tracks: {
      en: track("binding-of-fenrir-gleipnir-from-dark-elves", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "After iron fails, Odin turns to hidden craft. Skirnir is sent to the Black Elves' region, and the dwarves make Gleipnir from impossible things, though it looks like a soft ribbon. The gods bring Fenrir to Lyngvi and show him the binding. He sees the problem: if it is weak, breaking it brings no fame; if it hides a trap, the gods may not release him. Fenrir demands a hand as pledge, and Tyr places his hand in the wolf's mouth.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-gleipnir-from-dark-elves", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "鐵鏈失敗後，奧丁轉向隱秘工藝。斯基爾尼爾被派往黑精靈之地，侏儒製成Gleipnir；它由貓足聲、女人鬍鬚、石根、熊筋、魚氣與鳥唾等不可能之物構成，外表卻像柔軟細絲。神族把芬里爾帶到阿姆斯瓦特尼爾湖中的Lyngvi島，展示這條看似無害的束帶。芬里爾看出可疑：若它真的微弱，掙脫沒有名聲；若有陷阱，神族未必會釋放牠。於是牠要求有人把手放進自己口中作保，提爾把手伸進狼口。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-tyr-loses-hand",
    globalOrder: 75,
    storyId: "binding-of-fenrir",
    stepId: "tyr-loses-hand",
    sourceStepIds: ["tyr-loses-hand"],
    sourceIds: ["prose-edda-gylfaginning-25-tyr-fenrir", "prose-edda-gylfaginning-34-binding-fastened"],
    tracks: {
      en: track("binding-of-fenrir-tyr-loses-hand", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Once Gleipnir is placed on Fenrir, he strains with all his power. The ribbon tightens instead of breaking, and the gods do not release him. Fenrir understands the broken pledge and bites off Tyr's hand. The gods control the wolf for now, but deception enters divine order, and Tyr's missing hand becomes the visible cost.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-tyr-loses-hand", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "Gleipnir套上後，芬里爾立即全力掙扎。前兩條鐵鏈都因牠的力量斷裂，這一次束帶卻越掙越緊；諸神看見束縛成功，沒有依約放牠。芬里爾明白保證已被背棄，便咬下提爾仍在口中的手。神族暫時控制了預言中的狼，卻把失信寫進自己的秩序；提爾原本把右手放進狼口作保，如今成為獨手的神，缺手也成為這場勝利永遠可見的代價。束帶困住芬里爾，卻沒有取消預言。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-gelgja-gjoll-thviti-fastening",
    globalOrder: 76,
    storyId: "binding-of-fenrir",
    stepId: "gelgja-gjoll-thviti-fastening",
    sourceStepIds: ["gelgja-gjoll-thviti-fastening"],
    sourceIds: ["prose-edda-gylfaginning-34-binding-fastened"],
    tracks: {
      en: track("binding-of-fenrir-gelgja-gjoll-thviti-fastening", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Once Gleipnir catches Fenrir, the gods turn a moment of success into lasting confinement. The gods do not leave the binding at the lakeshore. They carry their success downward, so every struggle meets a deeper weight and Fenrir is pulled farther from the gods' sight. This is not another trial but the moment of victory turned into a captivity Fenrir cannot end himself.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-gelgja-gjoll-thviti-fastening", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "Gleipnir困住芬里爾後，神族仍要把這一瞬間的成功變成長久困禁。神族把Gelgja穿過束帶末端，接上巨大的Gjoll石，再把石頭固定在Thviti。束帶收緊後，這些沉重的固定物不讓芬里爾把它拖回湖岸；每次反撲都拉向更深的重量，牠越想逃，越遠離神族，也越難再看見諸神。狼的怒氣沒有消失，只被石與深處一同壓住。",
      }),
    },
  },
  {
    sceneId: "binding-of-fenrir-sword-and-van-river",
    globalOrder: 77,
    storyId: "binding-of-fenrir",
    stepId: "sword-and-van-river",
    sourceStepIds: ["sword-and-van-river", "bound-until-ragnarok"],
    sourceIds: ["prose-edda-gylfaginning-34-binding-fastened", "prose-edda-gylfaginning-34-ragnarok-odin"],
    tracks: {
      en: track("binding-of-fenrir-sword-and-van-river", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Holding Fenrir's body does not remove the threat. The gods force a sword into his mouth and leave his jaws open. The slaver that runs from him becomes River Van. Tyr's hand, the sword, and the water remember how the gods bought temporary safety. Fenrir remains for Ragnarok; the binding delays his fate with Odin without dissolving it.",
      }),
      'zh-Hant-mandarin': track("binding-of-fenrir-sword-and-van-river", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "困住芬里爾的身體並不等於消除牠的威脅。神族把一把劍塞進狼口，令牠被迫張開雙顎。從牠口中流出的涎水形成范河。提爾的手、狼口的劍和河水都記住神族如何換取暫時安全。神族不殺芬里爾，因為不願以狼血玷污他們視為聖地的地方；他們選擇把牠留在那裡。芬里爾留到諸神黃昏；受縛延遲牠與奧丁的命運，卻沒有解除它。神族用看守換得一時安寧，卻一直知道受縛不等於無害。",
      }),
    },
  },
  {
    sceneId: "norse-oaths-treasures-giants-laeding-and-dromi-named",
    globalOrder: 78,
    storyId: "norse-oaths-treasures-giants",
    stepId: "laeding-and-dromi-named",
    sourceStepIds: ["laeding-and-dromi-named", "gelgja-gjoll-thviti-fastening", "sword-and-van-river"],
    sourceIds: ["prose-edda-gylfaginning-34-laeding-dromi", "prose-edda-gylfaginning-34-binding-fastened", "prose-edda-gylfaginning-34-ragnarok-odin"],
    tracks: {
      en: track("norse-oaths-treasures-giants-laeding-and-dromi-named", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "This sequence magnifies the details of Fenrir's binding. Laedingr and Dromi are not simply failed fetters; their names preserve the two attempts that make Gleipnir's success meaningful. Gleipnir is then fixed through Gelgja, Gjoll, and Thviti, while the sword and the river Van preserve the wolf's pain. The details do not rewrite the Fenrir story.",
      }),
      'zh-Hant-mandarin': track("norse-oaths-treasures-giants-laeding-and-dromi-named", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "芬里爾受縛的細節在這條支線中被重新放大。Lædingr與Drómi不再只是失敗的鐵鏈，而是被命名的前兩次嘗試；Gleipnir的成功也因此建立在普通力量兩度失效之上。束帶隨後被接到Gelgja、Gjoll與Thviti等固定物上，受縛不再只是套在狼身上的柔帶，而是一整組把危機壓到深處的裝置。劍撐住狼口，Ván河則留下牠流出的痛苦。",
      }),
    },
  },
  {
    sceneId: "norse-oaths-treasures-giants-bragi-and-idunn",
    globalOrder: 79,
    storyId: "norse-oaths-treasures-giants",
    stepId: "bragi-and-idunn",
    sourceStepIds: ["bragi-and-idunn", "thjazi-takes-idunn", "idunn-restored-thjazi-atoned"],
    sourceIds: ["prose-edda-gylfaginning-26-bragi-idunn", "prose-edda-skaldskaparmal-1-2-idunn-thjazi"],
    tracks: {
      en: track("norse-oaths-treasures-giants-bragi-and-idunn", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Another divine fragility lies in youth. Bragi and Idunn first join poetry, marriage, and guarded renewal; her apples are the condition by which the gods remain young. When Thjazi takes Idunn, the gods age, and Loki is forced to repair the harm. He brings her back, Thjazi pursues, and the gods kill the giant at their boundary. The scene shows that the gods depend on guarded things.",
      }),
      'zh-Hant-mandarin': track("norse-oaths-treasures-giants-bragi-and-idunn", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 55,
        text: "神族秩序的另一種脆弱藏在青春裏。布拉基與伊登的關係先把詩歌、婚姻和青春守護放在一起；伊登的蘋果不是普通食物，而是神明持續年輕的條件。夏基奪走伊登後，神族立刻變老，洛基被迫補救。他把伊登帶回，夏基追趕而來，最後在阿斯神族的防線中被殺；危機解除，卻沒有把巨人世界的餘波完全抹去。這段故事說明神族不是天然不朽到毫無依賴。他們需要被守護的器物，也會因洛基牽出的事故而迅速衰敗。",
      }),
    },
  },
  {
    sceneId: "norse-oaths-treasures-giants-sif-hair-and-treasures",
    globalOrder: 80,
    storyId: "norse-oaths-treasures-giants",
    stepId: "sif-hair-and-treasures",
    sourceStepIds: ["sif-hair-and-treasures", "sif-and-ullr", "mimir-and-wisdom"],
    sourceIds: ["prose-edda-skaldskaparmal-35-treasures", "prose-edda-gylfaginning-30-31-vali-ullr", "prose-edda-skaldskaparmal-10-14-kennings", "prose-edda-gylfaginning-15-yggdrasil-roots", "prose-edda-gylfaginning-51-gjallarhorn-yggdrasil"],
    tracks: {
      en: track("norse-oaths-treasures-giants-sif-hair-and-treasures", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Loki cutting Sif's hair looks like mischief, but it draws out a larger act of compensation. Dwarven craft repairs the harm and brings new treasures into divine order. Sif and Ullr also gain clearer places within the divine network: she is more than the victim of the episode, and Ullr remains a significant divine name at the edge of the order. Mimir and the well of wisdom add another support.",
      }),
      'zh-Hant-mandarin': track("norse-oaths-treasures-giants-sif-hair-and-treasures", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "洛基剪去希芙的頭髮，表面上像一場惡作劇，實際卻牽出更大的補償。為了修補損害，他推動侏儒工藝，金髮與寶物再次進入神族秩序。希芙與烏勒爾的神族位置也在這些片段中被固定：她不是只有受害者身份，還連到家庭與後續神族名單；烏勒爾則作為重要神名保留在秩序邊緣。密米爾與智慧之泉帶來另一種守護。神族需要武器、青春和寶物，也需要記憶與智慧的深處來源。",
      }),
    },
  },
  {
    sceneId: "norse-oaths-treasures-giants-builder-bargain",
    globalOrder: 81,
    storyId: "norse-oaths-treasures-giants",
    stepId: "builder-bargain",
    sourceStepIds: ["builder-bargain", "builder-wage-lost"],
    sourceIds: ["prose-edda-gylfaginning-42-builder"],
    tracks: {
      en: track("norse-oaths-treasures-giants-builder-bargain", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Asgard's defence is shaped by a dangerous bargain. A builder offers to make the wall, but the requested payment is Freyja, the sun, and the moon. The work advances too quickly because of Svadilfari. Loki is forced to intervene, draws the horse away, and Thor ends the broken bargain with violence. The scene is not merely an origin for the wall. It exposes how divine safety depends on wagers, deadlines, and risky repair.",
      }),
      'zh-Hant-mandarin': track("norse-oaths-treasures-giants-builder-bargain", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "阿斯加德的防線也由危險交易塑成。建城巨人提出建牆，報酬卻是芙蕾雅、太陽和月亮；神族接受附帶期限的協議，以為可以讓條件變得不可能。工程因斯瓦迪爾法利的力量而迅速推進，神族發現自己快要輸掉不該交出的代價。洛基被迫干預，變形引開馬匹，使建城者無法如期完成；托爾最後以暴力終止失控的交易。這段不是單純說明城牆來源，而是暴露神族秩序怎樣靠賭注、期限和補救維持。",
      }),
    },
  },
  {
    sceneId: "norse-oaths-treasures-giants-hrungnir-in-asgard",
    globalOrder: 82,
    storyId: "norse-oaths-treasures-giants",
    stepId: "hrungnir-in-asgard",
    sourceStepIds: ["hrungnir-in-asgard", "hrungnir-duel", "vali-avenger-survivor"],
    sourceIds: ["prose-edda-skaldskaparmal-17-hrungnir", "prose-edda-gylfaginning-30-31-vali-ullr", "prose-edda-skaldskaparmal-10-14-kennings", "prose-edda-gylfaginning-53-renewal-survivors"],
    tracks: {
      en: track("norse-oaths-treasures-giants-hrungnir-in-asgard", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Hrungnir's story brings giant threat into the gods' feast. He enters Asgard, and boasting turns the divine centre itself into a dangerous place. Thor's duel returns the insult to combat. The giant falls, but Thor is wounded by stone, so divine honour is preserved at a cost. Vali then links this story to another pressure: vengeance after Baldr's death and survival after the fire.",
      }),
      'zh-Hant-mandarin': track("norse-oaths-treasures-giants-hrungnir-in-asgard", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "赫朗格尼爾的故事把巨人威脅帶進神族宴席。他闖入阿斯加德，在酒與誇口中把神族中心變成危險場所；問題不單在邊界之外，而是已經坐到神明席上。托爾與赫朗格尼爾的決鬥把這份侮辱轉回戰鬥。巨人倒下，托爾也承受碎石與傷勢，顯示神族可以壓下入侵，卻不是毫無代價地維持尊嚴。瓦利在這段故事末端帶來另一種時間壓力：他既連到巴德爾之死後的復仇，也在火後新生名單中留存。",
      }),
    },
  },
  {
    sceneId: "norse-baldrs-draumar-baldrs-dreams",
    globalOrder: 83,
    storyId: "norse-baldrs-draumar",
    stepId: "baldrs-dreams",
    sourceStepIds: ["baldrs-dreams", "odin-descends"],
    sourceIds: ["poetic-edda-baldrs-draumar-1-5-odin-descent"],
    tracks: {
      en: track("norse-baldrs-draumar-baldrs-dreams", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Baldr begins to dream dark dreams. Gods and goddesses gather, unable to tell why death's shadow has fallen on him. Odin does not wait in the council. He saddles Sleipnir, passes Garm, and rides to Hel's high hall and a seeress's grave. The danger has not become a blow, but the gods already know it is near. Odin rides not to undo a death, but to name it before it arrives.",
      }),
      'zh-Hant-mandarin': track("norse-baldrs-draumar-baldrs-dreams", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "巴德爾連續做下不祥夢。眾神與女神聚在一起，不知道死亡的影子為何落到他身上。奧丁沒有留在會議裡等待答案。他替斯萊普尼爾備鞍，穿過加姆，騎到赫爾的高殿與一座女先知的墳前。危險還沒有化成一擊，神族卻已經知道它正在靠近。奧丁的路徑不是為了阻止一場已發生的死亡，而是想在命運落下以前問清楚它的名字。",
      }),
    },
  },
  {
    sceneId: "norse-baldrs-draumar-seeress-rises",
    globalOrder: 84,
    storyId: "norse-baldrs-draumar",
    stepId: "seeress-rises",
    sourceStepIds: ["seeress-rises", "false-name"],
    sourceIds: ["poetic-edda-baldrs-draumar-1-5-odin-descent", "poetic-edda-baldrs-draumar-6-10-death-foretold"],
    tracks: {
      en: track("norse-baldrs-draumar-seeress-rises", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Odin works magic at the eastern grave-door, compelling the long-dead seeress to rise. She first asks who has called her back from snow, rain, and dew. Odin does not give his name. Calling himself Way-tamer, he asks for whom Hel has spread rings and brewed mead. The exchange is unequal from the start: Odin seeks an answer from the dead, and the seeress soon sees that he hides his identity.",
      }),
      'zh-Hant-mandarin': track("norse-baldrs-draumar-seeress-rises", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "奧丁在東面的墓門前施法，長眠的女先知被迫起來。她先問是誰把她從雪、雨與露水下喚回。奧丁沒有報出本名，只自稱道路馴服者。他問赫爾為誰鋪滿金飾，又為誰釀下蜜酒。這段對話從一開始就不平等：奧丁要從死者口中取得答案，女先知也很快看出他正在遮掩身分。詩保留了她的沉默與抵抗，沒有把她變成順從的傳話者。",
      }),
    },
  },
  {
    sceneId: "norse-baldrs-draumar-mead-for-baldr",
    globalOrder: 85,
    storyId: "norse-baldrs-draumar",
    stepId: "mead-for-baldr",
    sourceStepIds: ["mead-for-baldr", "hodr-named"],
    sourceIds: ["poetic-edda-baldrs-draumar-6-10-death-foretold"],
    tracks: {
      en: track("norse-baldrs-draumar-mead-for-baldr", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "The seeress says bright mead is already prepared for Baldr in Hel. This is no comfort; it is an image of death already arranged. Odin asks who will take Baldr's life, and she answers: Hodr. She names neither mistletoe nor a shot; this poem makes the coming death an irrevocable prophecy. Baldr still belongs to the living world, but the dead already have a place for him.",
      }),
      'zh-Hant-mandarin': track("norse-baldrs-draumar-mead-for-baldr", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 50,
        text: "女先知說赫爾已為巴德爾備下明亮的蜜酒。這不是安慰，而是一幅死亡已預先安排好的圖景。奧丁追問誰會奪去巴德爾的生命，女先知答道：霍德爾。她沒有講槲寄生，也沒有講射擊；這首詩只把將至的死亡說成一句無法收回的預言。巴德爾仍在生者的世界，冥界卻已有他的席位。詩的恐怖不在於展示殺害，而在於答案已經在死者的屋裡等着，眾神卻不能從預言中得到逃路。",
      }),
    },
  },
  {
    sceneId: "norse-baldrs-draumar-vali-named",
    globalOrder: 86,
    storyId: "norse-baldrs-draumar",
    stepId: "vali-named",
    sourceStepIds: ["vali-named", "seeress-unmasks-odin"],
    sourceIds: ["poetic-edda-baldrs-draumar-11-14-vali-ragnarok", "poetic-edda-baldrs-draumar-introduction"],
    tracks: {
      en: track("norse-baldrs-draumar-vali-named", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Odin asks who will avenge Baldr. The seeress says Rindr will bear Vali, who will fight before he has washed or combed his hair. When Odin asks once more, the seeress finally speaks his true name. She tells him to ride home and says no one will visit her so again before Loki is loosed and the gods meet their doom. Neither vengeance nor doom has happened here; both press forward as prophecy.",
      }),
      'zh-Hant-mandarin': track("norse-baldrs-draumar-vali-named", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "奧丁再問誰會為巴德爾報復。女先知說琳德爾會生下瓦利；他尚未洗手梳髮便會出戰。當奧丁又問哭泣的少女，女先知終於叫破他的名字。她讓他回家，並說在洛基脫縛、諸神遭劫以前，再不會有人像他這樣來訪。這不是已完成的復仇或末日，而是連續向前逼近的預言。巴德爾的夢使死亡有了名字，瓦利使報復有了名字，洛基的脫縛則把這一夜的求問接到更遙遠的毀滅。",
      }),
    },
  },
  {
    sceneId: "norse-baldr-loki-baldr-dreams",
    globalOrder: 87,
    storyId: "norse-baldr-loki",
    stepId: "baldr-dreams",
    sourceStepIds: ["baldr-dreams", "frigg-oaths", "loki-finds-mistletoe"],
    sourceIds: ["prose-edda-gylfaginning-49-baldr-dreams-oaths", "prose-edda-gylfaginning-49-loki-mistletoe-hodr"],
    tracks: {
      en: track("norse-baldr-loki-baldr-dreams", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Baldr's story begins with dreams. Death enters the divine household as a warning, and Odin and Frigg's centre becomes a place of fear. Frigg takes oaths from things not to harm Baldr, but mistletoe is left out because it seems too small. Loki learns the gap and turns protection into vulnerability. The tragedy is precise: the gods do protect Baldr, but not completely.",
      }),
      'zh-Hant-mandarin': track("norse-baldr-loki-baldr-dreams", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "巴德爾的故事先以夢開始。死亡預兆進入神族中心，奧丁與弗麗嘉的家庭不再只是權威所在，也成為恐懼所在；神明知道最明亮的神可能會被奪走。弗麗嘉向萬物取誓，要求它們不傷害巴德爾。誓言看似把世界全部納入保護，但槲寄生因細小而被遺漏；洛基得知這個缺口後，故事立刻從守護轉向可被利用的漏洞。這段場景把悲劇寫得很精準：神族不是沒有保護巴德爾，而是保護近乎完整卻不完整。",
      }),
    },
  },
  {
    sceneId: "norse-baldr-loki-hodr-shot",
    globalOrder: 88,
    storyId: "norse-baldr-loki",
    stepId: "hodr-shot",
    sourceStepIds: ["hodr-shot", "funeral-and-draupnir"],
    sourceIds: ["prose-edda-gylfaginning-49-loki-mistletoe-hodr", "prose-edda-gylfaginning-49-funeral-hermod"],
    tracks: {
      en: track("norse-baldr-loki-hodr-shot", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "The gods turn Baldr's safety into a game, throwing things at him without harm. Loki gives mistletoe to blind Hodr and guides the fatal shot. Baldr falls, and the gods hold his funeral. Nanna dies, Draupnir is placed on the ship, and Thor hallows the burning with the hammer. The scene moves divine order from repairable danger to irreversible loss. Baldr dies where oath, brotherly hand, and Loki's scheme meet.",
      }),
      'zh-Hant-mandarin': track("norse-baldr-loki-hodr-shot", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "神明把巴德爾的無傷變成遊戲，向他投擲物件而不造成傷害。洛基把槲寄生交給霍德爾，引導這位盲眼神明射出致命一擊；歡樂場面在一瞬間變成死亡現場。巴德爾倒下後，神族為他舉行葬禮。南娜死去，德羅普尼爾被放到葬船上，托爾以錘祝聖火葬；那些前面象徵秩序和寶物的物件，現在進入喪禮。這段把神族世界從可修補的危機推到失去不可逆的位置。",
      }),
    },
  },
  {
    sceneId: "norse-baldr-loki-hermod-to-hel",
    globalOrder: 89,
    storyId: "norse-baldr-loki",
    stepId: "hermod-to-hel",
    sourceStepIds: ["hermod-to-hel", "thokk-refuses"],
    sourceIds: ["prose-edda-gylfaginning-49-funeral-hermod", "prose-edda-gylfaginning-49-hel-thokk"],
    tracks: {
      en: track("norse-baldr-loki-hermod-to-hel", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "After death, the gods still try to bring Baldr back. Hermod rides to Hel and asks the realm of the dead to release him. Hel sets a condition: if all things weep for Baldr, he may return. The world seems to mourn until Thokk refuses, and suspicion returns to Loki. The tragedy is locked. Baldr is not simply killed; he is denied return. Two almost universal acts, oath and mourning, fail because of one gap.",
      }),
      'zh-Hant-mandarin': track("norse-baldr-loki-hermod-to-hel", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "死亡之後，神族仍嘗試把巴德爾帶回。赫爾莫德騎往赫爾，求死者世界釋放巴德爾；故事從阿斯加德的葬禮走到死者所在之地。赫爾提出條件：若萬物都為巴德爾哭泣，他可以回來。世界似乎願意哀悼，直到托克拒絕哭泣；這個拒絕使條件失敗，也讓懷疑再次指向洛基的變形與惡意。這段場景把悲劇鎖死。",
      }),
    },
  },
  {
    sceneId: "norse-baldr-loki-loki-bound",
    globalOrder: 90,
    storyId: "norse-baldr-loki",
    stepId: "loki-bound",
    sourceStepIds: ["loki-bound", "sigyn-venom-until-ragnarok"],
    sourceIds: ["prose-edda-gylfaginning-50-loki-capture", "prose-edda-gylfaginning-50-loki-bound-sigyn"],
    tracks: {
      en: track("norse-baldr-loki-loki-bound", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "After Thokk's refusal, Loki is no longer merely troublesome; he becomes the rupture the gods must punish. He changes shape and hides, but cannot escape. The gods catch him, bind him to stones through the fate of his sons, and set venom dripping above his face while Sigyn tries to catch it. This binding echoes Fenrir's, but darker. Loki is fixed in place, yet Baldr is not restored; the wound remains until doom.",
      }),
      'zh-Hant-mandarin': track("norse-baldr-loki-loki-bound", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "托克拒哭後，洛基不再只是惹禍者，而成為神族必須處置的破裂來源。他變形逃避，藏在遠處，仍然不能完全離開神族追捕的範圍。神明捕住洛基，以他兒子的命運作成懲罰，把他綁在石上，蛇毒滴落在他臉上；西格恩在旁接毒，當她移開盆時，痛苦使洛基掙動，大地也隨之震動。這段受縛與芬里爾受縛互相呼應，但情感更黑暗。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-awful-winter",
    globalOrder: 91,
    storyId: "norse-ragnarok",
    stepId: "awful-winter",
    sourceStepIds: ["awful-winter", "sun-moon-wolves", "fetters-and-sea"],
    sourceIds: ["prose-edda-gylfaginning-51-awful-winter", "prose-edda-gylfaginning-51-wolves-fetters", "poetic-edda-voluspo-40-58-ragnarok", "prose-edda-gylfaginning-51-naglfar-muspell-vigrid"],
    tracks: {
      en: track("norse-ragnarok-awful-winter", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Ragnarok does not begin directly on the battlefield. Long winter first breaks seasons and kinship order; doom enters ordinary life before armies gather. Then the sky is attacked. Skoll swallows Sol, Hati takes Mani, and the stars vanish. After the earth shakes, bonds and sea begin to break. Fenrir gets free, Jormungandr drives the sea over land, and Naglfar is loosed. Earlier delays return as active crisis.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-awful-winter", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "諸神黃昏不是直接從戰場開始。長冬先破壞季節與人間親族秩序，世界在刀兵與寒冷中失常；戰士尚未集結，末日已經進入日常。接着天象被攻擊。斯庫爾吞下蘇爾，哈提奪去瑪尼，星辰消失；天空不再只是背景，而成為會被狼吞奪的秩序。大地震動後，束縛與海潮也開始破裂。芬里爾脫身，耶夢加得攪動海水，納格爾法被放出。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-muspell-to-vigrid",
    globalOrder: 92,
    storyId: "norse-ragnarok",
    stepId: "muspell-to-vigrid",
    sourceStepIds: ["muspell-to-vigrid", "gjallarhorn-council"],
    sourceIds: ["prose-edda-gylfaginning-51-naglfar-muspell-vigrid", "poetic-edda-voluspo-40-58-ragnarok", "prose-edda-gylfaginning-51-gjallarhorn-yggdrasil"],
    tracks: {
      en: track("norse-ragnarok-muspell-to-vigrid", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Once the crisis has routes, hostile forces move toward Vigrid. Surtr and the sons of Muspell come from fire, Loki returns to the doom scene, and Bifrost breaks under pressure. Heimdall blows Gjallarhorn, the gods assemble, Odin seeks Mimir's well for final counsel, and Yggdrasil trembles. Scattered signs become a battlefield.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-muspell-to-vigrid", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "危機有了通路後，敵對軍勢開始向維格利德聚集。蘇爾特爾與穆斯貝爾之子從火界而來，洛基也回到末日場景，彩虹橋在重壓下斷裂。海姆達爾吹響加拉爾號角，神明集會，奧丁前往密米爾之泉求取最後智慧，世界樹震動。看守、王權、智慧與宇宙支架同被召入末日。這一幕把分散的災兆收束成最後戰場。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-odin-fenrir-vidar",
    globalOrder: 93,
    storyId: "norse-ragnarok",
    stepId: "odin-fenrir-vidar",
    sourceStepIds: ["odin-fenrir-vidar"],
    sourceIds: ["prose-edda-gylfaginning-51-final-battles", "poetic-edda-voluspo-40-58-ragnarok", "poetic-edda-vafthruthnismol-44-53-survivors"],
    tracks: {
      en: track("norse-ragnarok-odin-fenrir-vidar", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Odin's battle with Fenrir brings the prophecy of the binding story to its limit. The wolf the gods delayed now reaches the god-king. Fenrir swallows Odin. Vidar immediately avenges his father, setting his foot against the wolf's jaw and killing him. This is not a simple exchange of victory and defeat. The highest god is gone, and revenge cannot restore the old order.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-odin-fenrir-vidar", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "奧丁與芬里爾的對決把受縛故事的預言帶到終點。早前神族把狼留到諸神黃昏，現在期限抵達；神王面對的不是普通敵人，而是自己一直試圖延後的命運。芬里爾吞下奧丁，使預言成為戰場事實。維達爾隨即踏住狼顎，撕裂或殺死芬里爾，為父親復仇；父子、狼與末日之間的關係在一場殘酷反擊中完成。這段不是單純勝負交換。神族失去最高王者，即使復仇成功，也不能把舊秩序帶回來。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-thor-serpent-duel",
    globalOrder: 94,
    storyId: "norse-ragnarok",
    stepId: "thor-serpent-duel",
    sourceStepIds: ["thor-serpent-duel", "freyr-tyr-loki-fall"],
    sourceIds: ["prose-edda-gylfaginning-51-final-battles", "poetic-edda-voluspo-40-58-ragnarok"],
    tracks: {
      en: track("norse-ragnarok-thor-serpent-duel", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "The final field tightens several earlier threads at once. Thor faces Jormungandr not as a fishing rehearsal but as the completed doom duel. Thor kills the serpent, then dies after nine steps from its venom. Freyr faces Surtr without his sword; Tyr and Garm kill each other; Loki and Heimdall also fall together. Victory and death arrive together.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-thor-serpent-duel", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "最後戰場把多條早前伏線一同拉緊。托爾面對耶夢加得，這不再是希米爾船上被割斷釣線的預演；巨蛇與雷神終於在末日完成對決。托爾殺死耶夢加得，走出九步後被毒所殺。弗雷面對蘇爾特爾，沒有自己的劍；提爾與加姆互殺，洛基與海姆達爾也同歸於盡。每一場戰鬥都帶着之前故事留下的缺口。這一幕讓神族勝利與死亡同時發生。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-surtr-fire",
    globalOrder: 95,
    storyId: "norse-ragnarok",
    stepId: "surtr-fire",
    sourceStepIds: ["surtr-fire", "renewed-gods"],
    sourceIds: ["prose-edda-gylfaginning-51-surtr-fire", "poetic-edda-voluspo-40-58-ragnarok", "prose-edda-gylfaginning-53-renewal-survivors", "poetic-edda-voluspo-59-66-renewal"],
    tracks: {
      en: track("norse-ragnarok-surtr-fire", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "At last Surtr's fire burns the world. This is not a mere closing battle but the collapse of the old order: gods fall, the bridge is broken, the tree trembles, and fire reaches the cosmos. When the fire subsides, the earth rises from the sea. Vidar and Vali remain; Baldr and Hodr return from Hel, and Thor's sons bring Mjolnir to Ida-Plain. This is not the old divine order returned unchanged.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-surtr-fire", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "最後，蘇爾特爾的火焚燒世界。這不是普通戰鬥尾聲，而是舊秩序的全面崩潰：神明倒下，橋已斷裂，世界樹震動，火把戰場擴展成宇宙毀滅。火熄後，大地從海中升起。維達爾與瓦利仍在，巴德爾和霍德爾從赫爾歸來；托爾的兒子也把妙爾尼爾帶到伊達平原。這不是把舊日神族原封不動地還回來。失去仍在，但毀壞之後已有新的聚會；在伊達平原，他們談起舊日的事與古老的符文。",
      }),
    },
  },
  {
    sceneId: "norse-ragnarok-lif-and-lifthrasir-repopulate",
    globalOrder: 96,
    storyId: "norse-ragnarok",
    stepId: "lif-and-lifthrasir-repopulate",
    sourceStepIds: ["lif-and-lifthrasir-repopulate"],
    sourceIds: ["prose-edda-gylfaginning-53-renewal-survivors", "poetic-edda-vafthruthnismol-44-53-survivors"],
    tracks: {
      en: track("norse-ragnarok-lif-and-lifthrasir-repopulate", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "The renewed world still needs human beings. As fire passes over the world, Lif and Lifthrasir hide in Hoddmimir's Wood rather than vanish with the old world. They live on morning dew until the earth can be lived on again; from these two, later descendants people the world. The source does not turn this into a heroic escape. It gives only a hiding place, waiting, and morning dew.",
      }),
      'zh-Hant-mandarin': track("norse-ragnarok-lif-and-lifthrasir-repopulate", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 46,
        text: "新世界仍需要人類。烈火穿過世界時，利夫與利夫特拉西爾藏在霍德密米爾之林，沒有隨舊世界一同消失。他們以晨露為食，等到火後的大地重新可居；從這兩人開始，後代再度使人類遍及世界。原典沒有把這段寫成一場英雄式逃亡；它只留下躲藏、等待和晨露。兩人沒有靠勝利取得新世界，也沒有立刻建立新的王權；他們先活下來，才有後代。",
      }),
    },
  },
  {
    sceneId: "norse-volsungs-branstock-sword",
    globalOrder: 97,
    storyId: "norse-volsungs",
    stepId: "branstock-sword",
    sourceStepIds: ["branstock-sword", "sigurd-inheritance"],
    sourceIds: ["volsunga-saga-3-branstock-sword", "volsunga-saga-11-12-sigmund-hjordis-gram", "volsunga-saga-13-sigurd-regin-grani"],
    tracks: {
      en: track("norse-volsungs-branstock-sword", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "The heroic cycle begins with a stranger in the Volsung hall. Odin drives a sword into the Branstock tree, and only Sigmund can draw it. The sword later breaks, is preserved through Hjordis, and becomes the material Sigurd will inherit. Fatherly glory, maternal preservation, and future heroism are joined before Sigurd's deeds begin. The Volsung story is therefore not simply a human adventure.",
      }),
      'zh-Hant-mandarin': track("norse-volsungs-branstock-sword", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 48,
        text: "英雄支線從沃爾松族廳堂中的陌生來客開始。奧丁把劍插入樹幹，只有西格蒙德能拔出；神器不是單純寶物，而是把神明干預直接放進家族命運。劍後來破碎，經過希約蒂絲保存，成為西格爾德繼承的材料。故事把父系榮耀、母親保存和未來英雄連在一起：西格爾德尚未出場完成偉業，命運已經以碎片形式等着他。這段開場使沃爾松族故事不再只是人間冒險。",
      }),
    },
  },
  {
    sceneId: "norse-volsungs-regin-fosters-sigurd",
    globalOrder: 98,
    storyId: "norse-volsungs",
    stepId: "regin-fosters-sigurd",
    sourceStepIds: ["regin-fosters-sigurd", "andvari-loses-hoard", "hreidmar-house-falls"],
    sourceIds: ["volsunga-saga-13-sigurd-regin-grani", "volsunga-saga-15-gram-reforged", "volsunga-saga-14-andvari-hoard", "poetic-edda-reginsmol-andvari"],
    tracks: {
      en: track("norse-volsungs-regin-fosters-sigurd", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "As Sigurd grows, Regin becomes his fosterer and driver. Grani is chosen, Gram is reforged, and the hero's horse, sword, and training come together. Behind Regin's guidance lies the old hoard. Andvari is forced to give up the treasure and curses it; Hreidmar's house receives the gold, and Fafnir kills his father for it. The dragon-slaying gains weight.",
      }),
      'zh-Hant-mandarin': track("norse-volsungs-regin-fosters-sigurd", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "西格爾德長大後，雷金成為他的養育者和推動者。格拉尼被選出，格拉姆被重鍛，英雄的馬、劍與訓練逐步齊備；但雷金的指引並不純粹，背後連着自己家族的黃金舊事。安德瓦利被迫交出寶藏並咒詛黃金；赫雷德瑪爾家得到賠償後，詛咒沒有停在物件上，而是進入親族。法夫尼爾殺父奪金，化身為守財的龍，雷金也帶着仇恨與慾望把西格爾德推向牠。這段讓屠龍故事有了真正重量。",
      }),
    },
  },
  {
    sceneId: "norse-volsungs-regin-sends-sigurd",
    globalOrder: 99,
    storyId: "norse-volsungs",
    stepId: "regin-sends-sigurd",
    sourceStepIds: ["regin-sends-sigurd", "fafnir-slaying", "regin-slaying-and-gold"],
    sourceIds: ["volsunga-saga-13-sigurd-regin-grani", "volsunga-saga-18-fafnir-slaying", "poetic-edda-fafnismol-sigurd-fafnir", "volsunga-saga-19-regin-slaying"],
    tracks: {
      en: track("norse-volsungs-regin-sends-sigurd", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Regin sends Sigurd against Fafnir, and the dragon-slaying begins. Sigurd ambushes the dragon and strikes with Gram, winning heroic fame while Fafnir warns of the gold's danger. Afterward, Sigurd roasts the dragon's heart, understands birds, and learns that Regin plans betrayal. He kills Regin and takes the gold. The scene raises Sigurd to heroic height while moving the curse onto him.",
      }),
      'zh-Hant-mandarin': track("norse-volsungs-regin-sends-sigurd", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 51,
        text: "雷金把西格爾德推向法夫尼爾，屠龍行動於是開始。西格爾德在道路上埋伏，以格拉姆刺中龍；英雄名聲在這一擊中成形，但龍臨死前也指出黃金帶來的危險。法夫尼爾死後，西格爾德烤龍心，聽懂鳥語，得知雷金準備背叛他。於是他殺死雷金，把黃金、格拉姆與龍血知識一起帶走；原本被雷金安排的復仇，反過來吞掉安排者。這段場景把西格爾德推到英雄高峰，也把詛咒推到他身上。",
      }),
    },
  },
  {
    sceneId: "norse-volsungs-brynhild-and-giukings",
    globalOrder: 100,
    storyId: "norse-volsungs",
    stepId: "brynhild-and-giukings",
    sourceStepIds: ["brynhild-and-giukings", "gunnar-wooing-deception"],
    sourceIds: ["volsunga-saga-20-brynhild-mountain", "volsunga-saga-26-29-giukings-wooing"],
    tracks: {
      en: track("norse-volsungs-brynhild-and-giukings", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "After the dragon-slaying, Sigurd enters another danger: court and marriage. Brynhild, Gudrun, Gunnar, and the Giukings draw the hero into alliance, desire, and political arrangement. The wooing deception becomes the core. Sigurd changes identity to help Gunnar pass the flame and win Brynhild, so marriage is outwardly completed while identity and promise are distorted. This is not a calm reward after the dragon.",
      }),
      'zh-Hant-mandarin': track("norse-volsungs-brynhild-and-giukings", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 46,
        text: "屠龍之後，西格爾德進入另一種危險：王庭與婚姻。布倫希爾德、古德倫、貢納爾和久基家族把英雄帶進盟約、愛慕和政治安排之中。求婚欺瞞把危機推到核心。西格爾德以變換身份的方式幫貢納爾通過火焰，取得布倫希爾德；表面上婚約完成，實際上身份、記憶與承諾都已被扭曲。這段不是屠龍後的平靜獎賞，而是英雄悲劇的第二個引爆點。",
      }),
    },
  },
  {
    sceneId: "norse-volsungs-betrayal-and-death",
    globalOrder: 101,
    storyId: "norse-volsungs",
    stepId: "betrayal-and-death",
    sourceStepIds: ["betrayal-and-death", "atli-aftershock"],
    sourceIds: ["volsunga-saga-30-32-sigurd-death", "poetic-edda-sigurtharkvitha-death", "volsunga-saga-33-39-atli-giukings"],
    tracks: {
      en: track("norse-volsungs-betrayal-and-death", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "When the deception is exposed, oaths and marriages inside the court collapse. Brynhild's anger, Gudrun's position, and the choices of Gunnar and his brothers push Sigurd toward betrayal. Sigurd is killed. The dragon-slayer does not die in a monster's mouth but within human relationships, shifting the tragedy from creature to kinship and alliance. Atli's aftershock carries cursed gold and family hatred onward.",
      }),
      'zh-Hant-mandarin': track("norse-volsungs-betrayal-and-death", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "欺瞞揭開後，王庭內部的誓言與婚姻開始崩壞。布倫希爾德的憤怒、古德倫的婚姻位置、貢納爾與兄弟們的決定，把西格爾德推向背叛。西格爾德被殺，英雄支線從勝利轉入哀悼和報復。屠龍者沒有死在龍口中，而是死在人的關係裏；這使沃爾松族故事的悲劇重心從怪物轉到盟友、親族與婚姻。阿特利的餘波把受詛黃金和家族仇恨繼續推遠。",
      }),
    },
  },
  {
    sceneId: "norse-volund-brothers-and-swan-maidens",
    globalOrder: 102,
    storyId: "norse-volund",
    stepId: "brothers-and-swan-maidens",
    sourceStepIds: ["brothers-and-swan-maidens", "women-depart-brothers-divide", "nidud-seizes-smith"],
    sourceIds: ["poetic-edda-volundarkvitha-1-41"],
    tracks: {
      en: track("norse-volund-brothers-and-swan-maidens", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Three brothers and swan-maidens live together for seven winters; in the eighth, the women leave and that companionship ends. Egil and Slagfith leave to seek their partners, while Volund remains at Ulfdal. Nithud finds him alone, seizes him, and takes him to his own land. This is not disposable romantic background.",
      }),
      'zh-Hant-mandarin': track("norse-volund-brothers-and-swan-maidens", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "三兄弟與天鵝少女相伴七個冬天；到第八個冬天，女子離開，這段相伴便告結束。埃吉爾和斯拉格菲德出發尋找伴侶，沃倫德卻留在烏爾夫達爾。尼杜德發現他獨居，隨即把他捉走，帶到自己的土地。這不是一段可略去的浪漫前史。詩把離散、尋找和留下排成一條因果線：當同伴不在場，擅長鍛造的沃倫德就更容易被王權攫取。",
      }),
    },
  },
  {
    sceneId: "norse-volund-sword-ring-and-captivity",
    globalOrder: 103,
    storyId: "norse-volund",
    stepId: "sword-ring-and-captivity",
    sourceStepIds: ["sword-ring-and-captivity", "island-confinement", "bodvild-brings-ring"],
    sourceIds: ["poetic-edda-volundarkvitha-1-41"],
    tracks: {
      en: track("norse-volund-sword-ring-and-captivity", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Nithud takes Volund's sword and ring, deprives him of freedom of movement, and sets him to work on an island in a lake. When the ring breaks, Nithud's daughter Bodvild brings it to the island for repair. Her visit begins with an object and occurs inside a space where the captivity has not ended. Here royal power, objects, and vulnerability are compressed into one workshop.",
      }),
      'zh-Hant-mandarin': track("norse-volund-sword-ring-and-captivity", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 54,
        text: "尼杜德奪走沃倫德的劍與戒指，又使他失去行動能力，將他安置在湖中島上做工。戒指後來斷裂，尼杜德的女兒博德維爾德帶着它到島上，請沃倫德修補。她的來訪源自一件物品，也發生在囚禁關係沒有解除的空間裏。這一段把王權、物件與脆弱處境壓縮在同一座工坊。沃倫德的技藝仍被索取，卻不能改變他遭拘禁的事實；博德維爾德也不是自由進入一段相遇，而是沿着父親持有的戒指走進一個不對等局面。",
      }),
    },
  },
  {
    sceneId: "norse-volund-revenge-on-sons",
    globalOrder: 104,
    storyId: "norse-volund",
    stepId: "revenge-on-sons",
    sourceStepIds: ["revenge-on-sons", "coercion-and-flight"],
    sourceIds: ["poetic-edda-volundarkvitha-1-41"],
    tracks: {
      en: track("norse-volund-revenge-on-sons", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Volund takes Nithud's two unnamed sons as targets of revenge, kills them, and sends cruel tokens into the royal household. When Bodvild visits, he coerces her; he then makes a flying device and leaves. From the air he tells Nithud that the sons are dead, while Bodvild weeps to her father about the harm done to her. Flight does not turn the lay into a clean victory.",
      }),
      'zh-Hant-mandarin': track("norse-volund-revenge-on-sons", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 53,
        text: "沃倫德以尼杜德兩名未具名的兒子為報復目標，殺死他們，並把殘酷的證物送入王室。他也在博德維爾德來訪時強迫她，之後做成飛行裝置離開。尼杜德追問時，沃倫德從空中說明兒子已死，博德維爾德則向父親哭訴自己受到的傷害。飛離並沒有把這首詩收成一場乾淨的勝利。沃倫德逃離囚禁，卻把殺害與強迫留下給沒有掌握權力的孩子和女兒；尼杜德失去兒子，也仍須面對由自己扣押所展開的連鎖傷害。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hundingsbane-helgi-birth-under-norns",
    globalOrder: 105,
    storyId: "norse-helgi-hundingsbane",
    stepId: "helgi-birth-under-norns",
    sourceStepIds: ["helgi-birth-under-norns", "sigmund-names-helgi"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-i-1-58"],
    tracks: {
      en: track("norse-helgi-hundingsbane-helgi-birth-under-norns", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Borghild bears Helgi; the Norns come to the infant, and Sigmund names his son. Fate-language stands beside paternal naming, marking a young hero's path toward renown and conflict. The lay does not provide a fixed calendar that can be seamlessly joined to other Helgi or Volsung material; the timeline keeps only its visible birth, shaping, and naming.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hundingsbane-helgi-birth-under-norns", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "博爾希爾德生下赫爾吉；諾恩來到嬰兒身旁，西格蒙德再替兒子命名。命運語言與父系命名並列，為年輕英雄標出將走向名聲與衝突的一條路。這首歌不提供可與其他赫爾吉或沃爾松材料無縫拼合的固定年表；時間線只保留詩中可見的出生、塑造與命名。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hundingsbane-helgi-kills-hunding",
    globalOrder: 106,
    storyId: "norse-helgi-hundingsbane",
    stepId: "helgi-kills-hunding",
    sourceStepIds: ["helgi-kills-hunding", "sons-demand-and-fall"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-i-1-58"],
    tracks: {
      en: track("norse-helgi-hundingsbane-helgi-kills-hunding", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "At fifteen Helgi kills Hunding; Hunding's sons immediately demand payment and later fall in conflict. The first killing does not remain an isolated heroic feat, but is pushed into another demand for requital. Blood-debt drives the story: one death gives another side reason to press a claim, and later victory does not make violence appear naturally settled.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hundingsbane-helgi-kills-hunding", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "十五歲的赫爾吉殺死洪丁；洪丁諸子立刻要求報償，隨後也在衝突中倒下。第一次殺戮沒有停留成孤立的英雄戰功，而是被推入另一輪索償。血仇是敘事的推進力：一方的死亡成為另一方提出要求的理由，後來的勝利不會令暴力看似自然終止。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hundingsbane-sigrun-names-the-pledge",
    globalOrder: 107,
    storyId: "norse-helgi-hundingsbane",
    stepId: "sigrun-names-the-pledge",
    sourceStepIds: ["sigrun-names-the-pledge", "fleet-sets-out"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-i-1-58"],
    tracks: {
      en: track("norse-helgi-hundingsbane-sigrun-names-the-pledge", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Sigrun says her father has pledged her to Hothbrodd and that she does not want to go; Helgi sets out with Sinfjotli and a fleet. When danger rises at sea, the lay places Sigrun beside the ships, keeping both her will and her action visible before battle. A marriage pledge is not her consent; the timeline preserves refusal before aid rather than allowing the ending to erase what she said.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hundingsbane-sigrun-names-the-pledge", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "西格倫說父親已把她許給霍特布羅德，而她不願前往；赫爾吉答應迎戰，隨辛菲奧特利率船隊出發。海上遇險時，詩又把西格倫放在船隊旁，讓她的意願和行動都留在戰事之前。婚約安排不是她的同意；時間線保留她先表明意願、再施以援助的次序，不讓詩末結果倒灌成她從未說過拒絕。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hundingsbane-hothbrodd-musters",
    globalOrder: 108,
    storyId: "norse-helgi-hundingsbane",
    stepId: "hothbrodd-musters",
    sourceStepIds: ["hothbrodd-musters", "frekastein-victory"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-i-1-58"],
    tracks: {
      en: track("norse-helgi-hundingsbane-hothbrodd-musters", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Hothbrodd musters allies; at Frekastein, Sigrun recognizes Helgi's side as victorious and confirms Hothbrodd's fall. The lay closes with a bride-outcome, but does not travel back to say Sigrun's earlier refusal never existed. Frekastein and Logafjoll receive no modern location; this route keeps the unresolved tension among victory, pledge, and will.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hundingsbane-hothbrodd-musters", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "霍特布羅德召集盟友；在弗雷卡斯坦，西格倫辨認赫爾吉一方獲勝，並確認霍特布羅德倒下。詩以婚配結果收束，但沒有倒回去說西格倫早前的拒絕從未存在。弗雷卡斯坦與洛加菲厄爾都不作現代定位；這條時間線保留勝負、婚約和意願之間未被化解的張力。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-sigrlin-threatened-homeland",
    globalOrder: 109,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "sigrlin-threatened-homeland",
    sourceStepIds: ["sigrlin-threatened-homeland"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-sigrlin-threatened-homeland", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Hrothmar seeks Sigrlin's hand and, when refused, attacks the land of her father Svafnir. Svafnir is killed and Svavaland is plundered and burned; Hjorvarth takes Sigrlin away. No answer comes at once, but the devastation leaves Sigrlin's son a lost homeland and an old wrong still awaiting him.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-sigrlin-threatened-homeland", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "赫羅斯馬爾求娶西格琳不成，便攻打她父親斯瓦夫尼爾的土地。斯瓦夫尼爾被殺，斯瓦瓦蘭遭掠奪與焚毀；希約爾瓦德帶走西格琳。這場毀滅沒有立刻得到回應，卻留給西格琳的兒子一片失去的故地與未雪的舊仇。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-unnamed-son-receives-name",
    globalOrder: 110,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "unnamed-son-receives-name",
    sourceStepIds: ["unnamed-son-receives-name"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-unnamed-son-receives-name", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "The son of Hjorvarth and Sigrlin is old enough to travel, yet still has no name. A riding woman on a hill calls him Helgi and points him to a renowned sword; the prose calls her Svava, Eylimi's daughter. Name and weapon arrive in one meeting. The boy is named before he is pointed toward the conflicts to come.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-unnamed-son-receives-name", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "希約爾瓦德與西格琳的兒子已能出門，卻仍沒有名字。山上的騎馬女子叫他赫爾吉，又指給他一柄名劍；散文稱她為埃利米之女斯瓦娃。名字和武器在同一次相遇中來到少年身邊。他先被叫出名字，才被指向後來的戰事。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-hrothmar-avenged",
    globalOrder: 111,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "hrothmar-avenged",
    sourceStepIds: ["hrothmar-avenged"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-hrothmar-avenged", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Helgi asks why no one has answered for his mother's ruined land and his grandfather's death. Hjorvarth gives him followers, and Helgi goes out; the prose says he kills Hrothmar. He turns a wound from before his birth into his own act, but victory brings no lasting calm. Enmity at sea, vows, and another battle remain ahead.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-hrothmar-avenged", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "赫爾吉質問父親：母親的土地被毀、外祖父被殺，為何一直無人追討。希約爾瓦德交給他隨從，赫爾吉帶人出發；散文說他殺死赫羅斯馬爾。他把出生前留下的傷口變成自己的行動，但這次勝利沒有讓故事變得安穩。海上的敵意、誓言與另一場戰鬥仍在前面。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-hati-falls-before-the-fleet",
    globalOrder: 112,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "hati-falls-before-the-fleet",
    sourceStepIds: ["hati-falls-before-the-fleet"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-hati-falls-before-the-fleet", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "After Helgi kills the giant Hati, the fleet lies at a fjord by night. Hati's daughter Hrimgerth comes to the ships and brings her father's death into the danger of the night. Hati is dead but not gone from the story: through his daughter's voice, his death becomes another confrontation rather than an ending.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-hati-falls-before-the-fleet", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "赫爾吉殺死巨人哈提後，船隊夜泊峽灣。哈提的女兒赫里姆蓋爾德來到船邊，把父親的死與船隊的危險帶進夜裡。哈提已死，卻沒有從故事裡消失；女兒的聲音讓他的死亡變成下一場對峙，而不是一個結束。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-riders-over-the-sea",
    globalOrder: 113,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "riders-over-the-sea",
    sourceStepIds: ["riders-over-the-sea"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-riders-over-the-sea", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Hrimgerth says the ships survive because a woman and helmed riders guard them over the sea. The verses are damaged here, yet keep her until sunrise, when she is said to turn to stone. The prose joins Svava to Helgi's protection, but the sea-guard leaves identity uncertain. Night passes, the ships remain, and the answer still has a gap.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-riders-over-the-sea", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "赫里姆蓋爾德說，船隊能平安，是因為有女子與戴盔的騎者在海上護持。詩句在這裡殘缺，卻把她留到日出；天亮時，她被說成化為石頭。散文把斯瓦娃與赫爾吉的護持相連，但這段海上守護沒有把身分說得毫無疑問。夜色散去後，船隊留下，答案仍帶著缺口。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-helgi-and-svava-vows",
    globalOrder: 114,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "helgi-and-svava-vows",
    sourceStepIds: ["helgi-and-svava-vows"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-helgi-and-svava-vows", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Helgi seeks Svava's hand, and the two exchange vows and love one another. Svava remains at her father's home while Helgi goes to war; the vow joins them without bringing them into one household. That promise makes the later request weighty. When Helgi asks Svava to turn to Hethin, she faces words she has already spoken.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-helgi-and-svava-vows", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "赫爾吉向斯瓦娃求婚，兩人交換誓言，也彼此相愛。斯瓦娃仍留在父親家中，赫爾吉繼續出征；誓言讓兩人相連，卻沒有讓他們同住。這份承諾使後來的請求格外沉重。赫爾吉要斯瓦娃轉向赫辛時，她面對的是自己已經說出口的話。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-hethin-dangerous-vow",
    globalOrder: 115,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "hethin-dangerous-vow",
    sourceStepIds: ["hethin-dangerous-vow"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-hethin-dangerous-vow", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "At the king's toast, Hethin meets a fearsome woman who foretells a cost. He vows at the feast that he will have Svava, then goes in grief to confess the words to his brother Helgi. It is neither love between the two nor a proven fate. It is a hurtful demand that makes Hethin immediately see what he may tear apart.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-hethin-dangerous-vow", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "國王的祝酒開始時，赫辛遇見一名可怕的女子；她預告代價將臨到他身上。赫辛在酒席上誓言要得到斯瓦娃。話一出口，他便悲痛地去向兄長赫爾吉坦白。這不是兩人的愛情，也不是已被證實的命運安排；它是一個傷人的要求，使赫辛立刻看見自己會撕裂什麼。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-battle-at-frekastein",
    globalOrder: 116,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "battle-at-frekastein",
    sourceStepIds: ["battle-at-frekastein"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-battle-at-frekastein", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Alf, Hrothmar's son, sets Frekastein as the battle-place, and Helgi goes to meet him. The prose does not detail every blow. It says only that Helgi receives a mortal wound there. Alf is Hrothmar's son, but the poem does not state his motive. The father's death and the son's battlefield appear in succession, then Helgi moves toward his final farewell.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-battle-at-frekastein", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "赫羅斯馬爾的兒子阿爾夫指定弗雷卡斯坦作戰場，赫爾吉前去迎戰。散文沒有細寫每一擊，只說赫爾吉在那裡受了致命傷。阿爾夫是赫羅斯馬爾之子，詩卻沒有明說他的心意。父親的死與兒子指定的戰場相繼出現，接著赫爾吉走入最後的告別。",
      }),
    },
  },
  {
    sceneId: "norse-helgi-hjorvarthsson-svava-answers-after-battle",
    globalOrder: 117,
    storyId: "norse-helgi-hjorvarthsson",
    stepId: "svava-answers-after-battle",
    sourceStepIds: ["svava-answers-after-battle"],
    sourceIds: ["poetic-edda-helgakvitha-hjorvarthssonar-1-43"],
    tracks: {
      en: track("norse-helgi-hjorvarthsson-svava-answers-after-battle", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Mortally wounded Helgi sends Sigar for Svava and asks her to prepare a bed for Hethin. Svava recalls her vow with Helgi and says she would not willingly turn to an unrenowned man; Hethin vows vengeance for his brother. The poem does not say whether Hethin fulfils that vow, nor does it turn Svava's answer into consent. The final rebirth note is an echo, not a way to make this Helgi the same hero as another namesake.",
      }),
      'zh-Hant-mandarin': track("norse-helgi-hjorvarthsson-svava-answers-after-battle", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "受了致命傷的赫爾吉派西加爾召喚斯瓦娃，並要她為赫辛預備床鋪。斯瓦娃記起自己與赫爾吉的誓言，說她不會出於自己的意願轉向未具名望的人；赫辛則誓言為兄長復仇。詩沒有說赫辛是否完成復仇，也沒有把斯瓦娃的回答改寫成順從。最後的再生說法只是餘音，不能把這位赫爾吉和另一位同名英雄合成一人。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-warning-falsified",
    globalOrder: 118,
    storyId: "norse-atlamol",
    stepId: "warning-falsified",
    sourceStepIds: ["warning-falsified"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-warning-falsified", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Guthrun makes runic warning for her brothers; Vingi falsifies it for Atli. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-warning-falsified", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 22,
        text: "古德倫做出符文警告兄弟，文吉替阿特利把它弄假。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-kostbera-reads-danger",
    globalOrder: 119,
    storyId: "norse-atlamol",
    stepId: "kostbera-reads-danger",
    sourceStepIds: ["kostbera-reads-danger"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-kostbera-reads-danger", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Hogni's wife sees in damaged runes and dreams that this is no friendly invitation. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-kostbera-reads-danger", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "霍格尼之妻從受損符文與夢裡看出，這不是善意的邀請。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-glaumvor-dreams",
    globalOrder: 120,
    storyId: "norse-atlamol",
    stepId: "glaumvor-dreams",
    sourceStepIds: ["glaumvor-dreams"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-glaumvor-dreams", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Gunnar's wife tells dreams of gallows, serpents, and the dead; Gunnar says the journey is fixed. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-glaumvor-dreams", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "貢納爾之妻說出絞架、蛇與死者的夢；貢納爾仍說行程已定。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-sons-at-fjord",
    globalOrder: 121,
    storyId: "norse-atlamol",
    stepId: "sons-at-fjord",
    sourceStepIds: ["sons-at-fjord"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-sons-at-fjord", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Snaever and Solar leave with their father Hogni, while the households can only part by the fjord. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-sons-at-fjord", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 24,
        text: "斯奈瓦爾與索拉爾隨父親霍格尼出發，家人只能在峽灣邊送別。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-vingi-reveals-trap",
    globalOrder: 122,
    storyId: "norse-atlamol",
    stepId: "vingi-reveals-trap",
    sourceStepIds: ["vingi-reveals-trap"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-vingi-reveals-trap", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "On arrival, Vingi admits the invitation concealed a trap; the brothers' party kills him. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-vingi-reveals-trap", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 23,
        text: "到達後，文吉承認邀請底下藏著陷阱；兄弟一行殺死他。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-gudrun-takes-sword",
    globalOrder: 123,
    storyId: "norse-atlamol",
    stepId: "gudrun-takes-sword",
    sourceStepIds: ["gudrun-takes-sword"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-gudrun-takes-sword", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Guthrun greets her brothers, says she tried to keep them home, and then takes a sword into the fight. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-gudrun-takes-sword", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 24,
        text: "古德倫迎接兄弟，說她曾想把他們留在家中，然後拿劍加入戰鬥。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-brothers-last-sounds",
    globalOrder: 124,
    storyId: "norse-atlamol",
    stepId: "brothers-last-sounds",
    sourceStepIds: ["brothers-last-sounds"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-brothers-last-sounds", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Atli orders Hogni and Gunnar killed; Hogni endures his death and Gunnar sounds a harp with his feet. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-brothers-last-sounds", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "阿特利下令處死霍格尼與貢納爾；霍格尼忍受死亡，貢納爾以腳撥響豎琴。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-death-feast-for-atli",
    globalOrder: 125,
    storyId: "norse-atlamol",
    stepId: "death-feast-for-atli",
    sourceStepIds: ["death-feast-for-atli"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-death-feast-for-atli", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 26,
        text: "Guthrun kills her two unnamed sons by Atli and makes him learn the truth at the feast. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-death-feast-for-atli", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "古德倫殺死她與阿特利的兩個未具名兒子，讓阿特利在宴上得知真相。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-atlamol-hniflung-joins-revenge",
    globalOrder: 126,
    storyId: "norse-atlamol",
    stepId: "hniflung-joins-revenge",
    sourceStepIds: ["hniflung-joins-revenge"],
    sourceIds: ["poetic-edda-atlamol-1-99-greenland-variant"],
    tracks: {
      en: track("norse-atlamol-hniflung-joins-revenge", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 27,
        text: "Hniflung, Hogni's son, plans with Guthrun and kills Atli; this is the poem's poem-specific ending. A falsified warning, two wives' counsel, and a journey that will not turn back carry the Giukings into the trap at Atli's home and its revenge.",
      }),
      'zh-Hant-mandarin': track("norse-atlamol-hniflung-joins-revenge", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 26,
        text: "霍格尼之子赫尼夫隆與古德倫合謀，殺死阿特利；這是本詩的本詩所寫的結局。一封被竄改的警告、兩位妻子的勸阻與一場不肯回頭的旅程，將久基家推進阿特利之家的伏擊與復仇。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-sword-enters-house",
    globalOrder: 127,
    storyId: "norse-hervor-awakening",
    stepId: "sword-enters-house",
    sourceStepIds: ["sword-enters-house"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-sword-enters-house", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "The R text first brings Tyrfing into Arngrim's household. Arngrim marries Eyfura; they have twelve sons, and Angantyr is the eldest. The sword is not a floating legend but something carried among parents, sons, and a later inheritor. It gains a place in the family before it goes to war. The danger does not suddenly emerge from a mound.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-sword-enters-house", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "R本先讓提爾鋒進入阿恩格里姆的家。阿恩格里姆娶了埃芙拉，兩人有十二個兒子，安甘蒂爾是長子。這把劍不是懸空的傳說物件，而是在父母、兒子與日後繼承者之間傳遞。它先在家族裡得到位置，才被帶去戰場。故事的危險因此不是忽然從墓丘冒出。赫爾沃爾後來要求的劍，早已和父系、婚姻與長子的身分纏在一起。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-samsey-challenge",
    globalOrder: 128,
    storyId: "norse-hervor-awakening",
    stepId: "samsey-challenge",
    sourceStepIds: ["samsey-challenge"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-samsey-challenge", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "A competing marriage claim does not remain in the king's hall. Hjalmar and Arngrim's sons are set to meet on Samsey, where the outcome will be decided on an island. The arrangement draws people from their homes and puts Tyrfing into its next bearer's hands. Arngrim arms Angantyr, but cannot enter the duel for him.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-samsey-challenge", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "一場求婚衝突沒有留在王廳裡。赫亞爾馬爾與阿恩格里姆諸子被約到薩姆瑟島，讓勝負在海島上決定。約戰先把人從各自的家中拉開，也把提爾鋒推到下一個持有者手上。阿恩格里姆替安甘蒂爾備好武器，卻不能替兒子走進決鬥。島嶼從這裡開始承受整段故事：它不再只是背景，而是比試、埋葬與後來父女相見的同一片土地。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-samsey-duel",
    globalOrder: 129,
    storyId: "norse-hervor-awakening",
    stepId: "samsey-duel",
    sourceStepIds: ["samsey-duel"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-samsey-duel", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "When the ships reach Samsey, Hjalmar sees Tyrfing in Angantyr's hand. He insists on facing that opponent himself. Orvar-Odd does not abandon his companion, but takes the remaining berserks. They arrive together, then carry different dangers in the fight. The duel gives no one a whole victory. Angantyr falls, and Hjalmar is mortally wounded; the sword has left a mark that cannot be withdrawn.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-samsey-duel", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "船隊抵達薩姆瑟島後，赫亞爾馬爾看見安甘蒂爾手中的提爾鋒。他堅持由自己面對這位對手。奧爾瓦爾・奧德沒有離開同伴，卻轉去接下其餘狂戰士。兩人並肩而來，到了戰鬥時卻各自承擔不同的人數與風險。決鬥沒有給任何人完整的勝利。安甘蒂爾倒下，赫亞爾馬爾也受了致命傷；劍在此時已經留下無法撤回的痕跡。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-sword-leaves-survivor",
    globalOrder: 130,
    storyId: "norse-hervor-awakening",
    stepId: "sword-leaves-survivor",
    sourceStepIds: ["sword-leaves-survivor"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-sword-leaves-survivor", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "When Orvar-Odd reaches Hjalmar, the battlefield has grown quiet. Hjalmar says Angantyr's sword has struck near his heart, and then he dies. Angantyr and his brothers are buried with their weapons on Samsey. Tyrfing does not vanish when the duel ends; it is placed inside the father's mound. The burial separates two times: the immediate dead of battle and a daughter who will later return to the same island for this sword alone.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-sword-leaves-survivor", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "奧爾瓦爾・奧德走到赫亞爾馬爾身邊時，戰場已經安靜下來。赫亞爾馬爾說安甘蒂爾的劍傷到自己心口，之後死去。安甘蒂爾和兄弟則連同武器被葬在薩姆瑟島。提爾鋒沒有隨著決鬥結束而消失，而是被放進父親的墓丘。這個埋葬把兩個時間隔開：當下是決鬥的死者，日後卻會有一個女兒專為這把劍回到同一座島。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-hervor-learns-father",
    globalOrder: 131,
    storyId: "norse-hervor-awakening",
    stepId: "hervor-learns-father",
    sourceStepIds: ["hervor-learns-father"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-hervor-learns-father", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Hervor does not know who her father is. Her foster father names Angantyr and tells her he lies buried on Samsey. The news does not leave her still. She sets out to seek her dead father and the property and sword he has left behind. Her journey begins with a family name uncovered. The meeting in the mound has not happened, but inheritance is no longer a story held by others.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-hervor-learns-father", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 40,
        text: "赫爾沃爾原本不知道自己的父親是誰。養父說出安甘蒂爾的名字，也告訴她他葬在薩姆瑟島。這個消息沒有讓她安靜留在原處。她決定出行，想見已死的父親，也想取得他留下的財物與劍。她的旅程由一個被揭開的家族名字開始。墓丘中的相見還未發生，但繼承已不再只是別人替她保管的故事。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-ship-waits-sunset",
    globalOrder: 132,
    storyId: "norse-hervor-awakening",
    stepId: "ship-waits-sunset",
    sourceStepIds: ["ship-waits-sunset"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-ship-waits-sunset", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Hervor reaches Samsey by ship, then lands alone at sunset. A herdsman tells her not to ask for the mound, saying fire and the dead drive people from the island. She does not go back with him. She sees mound-fire rising in the darkness and walks toward the place where Angantyr and his brothers are buried. Fear is not an abstract trial here, but visible night-fire and an empty island.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-ship-waits-sunset", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "赫爾沃爾隨船到薩姆瑟島，卻在日落時獨自登岸。牧人勸她不要問墓丘的位置，說島上火焰與亡者使人逃離。她沒有隨他回去，而是繼續往前。她看見丘火在黑暗中燃起，便走向埋葬安甘蒂爾和兄弟的地方。恐懼在這一幕不是抽象的考驗，而是每個人都看得見的夜火與空島。赫爾沃爾選擇跨過它，讓父女相見成為可能。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-hervor-wakes-angantyr",
    globalOrder: 133,
    storyId: "norse-hervor-awakening",
    stepId: "hervor-wakes-angantyr",
    sourceStepIds: ["hervor-wakes-angantyr"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-hervor-wakes-angantyr", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Before the burning mound, Hervor calls Angantyr and his brothers. She comes not to mourn but to demand Tyrfing from them. Angantyr answers first as a father, telling her not to wake the dead and urging her back to the ships. Hervor refuses, saying neither mound-fire nor the dead can make her retreat. The dialogue makes inheritance a resistance rather than a quiet handover.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-hervor-wakes-angantyr", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "赫爾沃爾站在燃燒的墓丘前，叫出安甘蒂爾與他的兄弟。她不是來哀悼，而是要求他們交出提爾鋒。安甘蒂爾先以父親的聲音回答，叫她不要喚醒死者，也催她趕快回船。赫爾沃爾拒絕離去，說墓火與亡者不足以使她退步。這段對話讓繼承成為一場抵抗，而不是安靜的交接。父親已死，女兒仍要讓他承認她的要求。",
      }),
    },
  },
  {
    sceneId: "norse-hervor-awakening-inheritance-not-left",
    globalOrder: 134,
    storyId: "norse-hervor-awakening",
    stepId: "inheritance-not-left",
    sourceStepIds: ["inheritance-not-left"],
    sourceIds: ["hervarar-saga-r-1-4-awakening"],
    tracks: {
      en: track("norse-hervor-awakening-inheritance-not-left", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Angantyr says Tyrfing will harm Hervor's family and tells her to leave it in the mound. This is the warning given by the father in the R text. Hervor still refuses to return empty-handed. Angantyr finally gives the sword, and she takes it and bids farewell to her father in the mound. The story stops as she leaves; it does not complete the fate of later descendants.",
      }),
      'zh-Hant-mandarin': track("norse-hervor-awakening-inheritance-not-left", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "安甘蒂爾說提爾鋒會傷害赫爾沃爾的家族，並要她把它留在墓丘裡。這是R本墓中父親給出的警告。赫爾沃爾仍不肯空手回去。安甘蒂爾最後把劍交出，她接住它，向墓丘中的父親告別。故事在她離開時停下，沒有替這把劍補完後代的命運。她帶走的是一件遺產，也帶走父親明明說過卻未能阻止的危險。",
      }),
    },
  },
  {
    sceneId: "norse-helgakvitha-hundingsbana-ii-dag-seeks-revenge",
    globalOrder: 135,
    storyId: "norse-helgakvitha-hundingsbana-ii",
    stepId: "dag-seeks-revenge",
    sourceStepIds: ["dag-seeks-revenge", "odin-gives-spear"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-ii-28-50"],
    tracks: {
      en: track("norse-helgakvitha-hundingsbana-ii-dag-seeks-revenge", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "After Hogni dies, Dag sacrifices to Odin for his father. The prose then says that Odin puts a spear in his hand. The text does not explain why Odin gives it, nor does it turn Dag's vengeance into unquestionable justice; it only joins those acts together. This scene therefore follows how a weapon enters a family conflict, not a full account of Odin's intention.",
      }),
      'zh-Hant-mandarin': track("norse-helgakvitha-hundingsbana-ii-dag-seeks-revenge", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "霍格尼死後，達格為父親向奧丁獻祭。散文緊接著說奧丁把一支矛交到他手裡。文本沒有解釋奧丁為何給矛，也沒有替達格把復仇變成無可質疑的正義；它只把這兩個行動相連。因此這一幕讀的是一份武器如何進入家族衝突，不是奧丁意圖的完整說明。",
      }),
    },
  },
  {
    sceneId: "norse-helgakvitha-hundingsbana-ii-dag-kills-helgi",
    globalOrder: 136,
    storyId: "norse-helgakvitha-hundingsbana-ii",
    stepId: "dag-kills-helgi",
    sourceStepIds: ["dag-kills-helgi", "sigrun-curses-brother"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-ii-28-50"],
    tracks: {
      en: track("norse-helgakvitha-hundingsbana-ii-dag-kills-helgi", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "At Fjoturlund, Dag pierces Helgi with the spear, then takes the news to Sigrun. The place has no secure modern location, yet it keeps the blow in a named scene of the poem. Sigrun curses her brother by the oaths she and Helgi had made. Her words are speech after a husband's death, not a spell the timeline verifies for the reader.",
      }),
      'zh-Hant-mandarin': track("norse-helgakvitha-hundingsbana-ii-dag-kills-helgi", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 43,
        text: "達格在費約圖爾林德用那支矛刺穿赫爾吉，然後把消息帶給西格倫。這個地名沒有可靠的現代位置，卻把一擊留在明確的詩中現場。西格倫以她和赫爾吉曾立的誓言詛咒兄長。她的話是失去丈夫後的發話，而不是時間線替讀者驗證的法術結果。同一段落讓兄妹關係與婚姻同時斷裂；它不要求讀者選擇哪一種悲傷較有資格。",
      }),
    },
  },
  {
    sceneId: "norse-helgakvitha-hundingsbana-ii-dag-offers-rings",
    globalOrder: 137,
    storyId: "norse-helgakvitha-hundingsbana-ii",
    stepId: "dag-offers-rings",
    sourceStepIds: ["dag-offers-rings", "helgi-received-in-valhalla"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-ii-28-50"],
    tracks: {
      en: track("norse-helgakvitha-hundingsbana-ii-dag-offers-rings", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Dag offers red rings and land, yet Sigrun does not become happy again. It is compensation that acknowledges harm, not proof that the two are reconciled. Prose inserted between the poem's fragments says Helgi reaches Valhalla and Odin asks him to rule with him. That placement belongs to this version's insertion, not a universal destination for every dead person.",
      }),
      'zh-Hant-mandarin': track("norse-helgakvitha-hundingsbana-ii-dag-offers-rings", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "達格提出紅環與土地，卻沒有讓西格倫重新快樂。這是一份承認傷害的賠償，不是兩人和解的證明。接在詩段之間的散文說赫爾吉到了瓦爾哈拉，奧丁要他同掌一切。這個安排屬於本版本的插段，不替每個死者寫成通行的去處。把兩幕放在一起，能看見文本如何先留住西格倫的失去，再切換到赫爾吉被接納的敘述。",
      }),
    },
  },
  {
    sceneId: "norse-helgakvitha-hundingsbana-ii-maid-sees-riders",
    globalOrder: 138,
    storyId: "norse-helgakvitha-hundingsbana-ii",
    stepId: "maid-sees-riders",
    sourceStepIds: ["maid-sees-riders", "sigrun-at-barrow"],
    sourceIds: ["poetic-edda-helgakvitha-hundingsbana-ii-28-50"],
    tracks: {
      en: track("norse-helgakvitha-hundingsbana-ii-maid-sees-riders", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "A maiden sees Helgi and his followers ride back to the mound at night, and Sigrun enters to make him a bed. Helgi says he must leave before dawn. He does not return on the next night. The closing prose then says Sigrun dies early and cautiously mentions a belief that they were reborn later.",
      }),
      'zh-Hant-mandarin': track("norse-helgakvitha-hundingsbana-ii-maid-sees-riders", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 38,
        text: "侍女夜裡看見赫爾吉與隨從騎回墓丘，西格倫便進去替他鋪床。赫爾吉說自己必須在天亮前離開。下一夜他沒有再回來。結尾散文再說西格倫早逝，並以保留語氣提到兩人後世重生的信念。這條路停在未再歸來之處：墓丘夜訪是詩中的短暫相見，不是一條可以延伸成固定復活規則的門。",
      }),
    },
  },
  {
    sceneId: "norse-fra-dautha-sinfjotla-kin-and-blank",
    globalOrder: 139,
    storyId: "norse-fra-dautha-sinfjotla",
    stepId: "kin-and-blank",
    sourceStepIds: ["kin-and-blank", "brother-killed", "atonement"],
    sourceIds: ["poetic-edda-fra-dautha-sinfjotla-prose-kin-atonement"],
    tracks: {
      en: track("norse-fra-dautha-sinfjotla-kin-and-blank", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Borghild's brother has no name. The prose says only that he and Sinfjotli court the same woman, and Sinfjotli kills him. Borghild tells her stepson to leave; Sigmund offers atonement-money, which she must accept. The money is received, but the death does not disappear. The opening deliberately leaves two names blank, so later supplied names cannot pull responsibility elsewhere.",
      }),
      'zh-Hant-mandarin': track("norse-fra-dautha-sinfjotla-kin-and-blank", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "博爾吉爾德的兄長沒有名字。散文只說他和辛菲奧特利同時追求一名女子，辛菲奧特利便殺了他。博爾吉爾德要繼子離去；西格蒙德提出賠償金，她只得接受。這筆錢收下了，卻沒有讓故事裡的死亡消失。開頭刻意留下兩個人的名字空白，使責任不會被後來附上的名字帶偏。能確定的是一個人死了、家裡沒有恢復平靜，而賠償只能暫時把衝突留在屋內。",
      }),
    },
  },
  {
    sceneId: "norse-fra-dautha-sinfjotla-first-horn",
    globalOrder: 140,
    storyId: "norse-fra-dautha-sinfjotla",
    stepId: "first-horn",
    sourceStepIds: ["first-horn", "second-horn"],
    sourceIds: ["poetic-edda-fra-dautha-sinfjotla-prose-poisoned-horns"],
    tracks: {
      en: track("norse-fra-dautha-sinfjotla-first-horn", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "At the funeral feast, Borghild brings the first horn. Sinfjotli sees the drink is muddy, and Sigmund takes it and drinks. The second horn comes, repeating warning and rescue between father and son. The prose says Sigmund can withstand poison, while his sons can resist it only on their outer skin. The first two acts seem to hold danger outside, yet make the third harder to read.",
      }),
      'zh-Hant-mandarin': track("norse-fra-dautha-sinfjotla-first-horn", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 45,
        text: "在葬宴上，博爾吉爾德端來第一角酒。辛菲奧特利一看便說酒混濁，西格蒙德拿過來喝下。第二角酒又來，父子之間重演同樣的警告與替飲。散文說西格蒙德能承受毒藥，兒子卻只能抵抗沾在皮膚外的毒。這兩次看似把危險擋在外面，卻把第三次變得更難解。故事沒有說博爾吉爾德放棄，也沒有讓前兩次的成功保證兒子能平安離開宴席。",
      }),
    },
  },
  {
    sceneId: "norse-fra-dautha-sinfjotla-third-horn",
    globalOrder: 141,
    storyId: "norse-fra-dautha-sinfjotla",
    stepId: "third-horn",
    sourceStepIds: ["third-horn", "sinfjotli-dies"],
    sourceIds: ["poetic-edda-fra-dautha-sinfjotla-prose-poisoned-horns"],
    tracks: {
      en: track("norse-fra-dautha-sinfjotla-third-horn", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "When the third horn arrives, Sinfjotli again says the drink is muddy. Sigmund does not drink for him, but tells him to let it run through his beard. Sinfjotli dies at once after drinking. The prose does not say whether Sigmund knows the poison will still kill him, nor does it supply an intention that could explain the tragedy away. Their last exchange weighs more heavily than an answer.",
      }),
      'zh-Hant-mandarin': track("norse-fra-dautha-sinfjotla-third-horn", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "第三角酒到來時，辛菲奧特利再一次說酒混濁。西格蒙德沒有替他喝，而是叫他讓酒從鬍鬚流過。辛菲奧特利喝下後立刻死去。散文沒有說西格蒙德是否知道毒藥仍會害死他，也沒有替父親補上一個可以解釋悲劇的意圖。父子間最後一句話因此比答案更沉。它把原本反覆成功的保護折斷，讓死亡留在一句沒有被後文說明的命令後面。",
      }),
    },
  },
  {
    sceneId: "norse-fra-dautha-sinfjotla-ferry-disappears",
    globalOrder: 142,
    storyId: "norse-fra-dautha-sinfjotla",
    stepId: "ferry-disappears",
    sourceStepIds: ["ferry-disappears"],
    sourceIds: ["poetic-edda-fra-dautha-sinfjotla-prose-ferry-transition", "poetic-edda-fra-dautha-sinfjotla-introduction"],
    tracks: {
      en: track("norse-fra-dautha-sinfjotla-ferry-disappears", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "Sigmund carries his son's body far until he reaches a narrow, long fjord. A man in a small boat offers to take the body across. Once the body is aboard, the boat is full. The ferryman tells Sigmund to go around the inner end, pushes off, and disappears. The prose does not name him or let Sigmund catch up.",
      }),
      'zh-Hant-mandarin': track("norse-fra-dautha-sinfjotla-ferry-disappears", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "西格蒙德把兒子的遺體抱了很遠，來到狹長的峽灣。小船上的男子說可以載遺體過去。遺體一上船，船便滿了。船夫叫西格蒙德繞內灣走，自己把船划開，隨即消失。散文不說他是誰，也不讓西格蒙德追上他。這個短段落在無法同渡之處結束，才把編者的敘事轉向西格蒙德與日後的西格爾德。",
      }),
    },
  },
  {
    sceneId: "norse-gripisspo-arrival",
    globalOrder: 143,
    storyId: "norse-gripisspo",
    stepId: "arrival",
    sourceStepIds: ["arrival", "kinship-named", "first-future"],
    sourceIds: ["poetic-edda-gripisspo-prose-1-6-arrival", "poetic-edda-gripisspo-introduction", "poetic-edda-gripisspo-7-19-early-future"],
    tracks: {
      en: track("norse-gripisspo-arrival", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Sigurth comes alone to Gripir's hall. Geitir announces him, and Gripir comes out to receive him. Sigurth calls the king his mother's brother and asks for the shape of his life. Gripir first answers with fame, courage, and generosity. The kinship title remains inside this poem's frame; the translator's note even suspects that Gripir was made for its prophecy.",
      }),
      'zh-Hant-mandarin': track("norse-gripisspo-arrival", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "西格爾德獨自到格里皮爾的廳前。蓋提爾通報他，格里皮爾親自出來接見。西格爾德稱這位王為母親的兄弟，要求他說出自己的一生。格里皮爾先答以名聲、勇氣與慷慨。這個親屬稱呼只在本詩的框架中保留；譯註甚至懷疑格里皮爾是詩人為預言所造的人物。",
      }),
    },
  },
  {
    sceneId: "norse-gripisspo-vengeance-and-dragon",
    globalOrder: 144,
    storyId: "norse-gripisspo",
    stepId: "vengeance-and-dragon",
    sourceStepIds: ["vengeance-and-dragon", "sleeping-woman"],
    sourceIds: ["poetic-edda-gripisspo-7-19-early-future"],
    tracks: {
      en: track("norse-gripisspo-vengeance-and-dragon", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Gripir goes on to name the wrongs of Sigurth's father and Eylimi, Hunding's sons, Fafnir, Regin, and the hoard. He also speaks of a sleeping woman in firelight and a meeting that will give wisdom. These are waypoints spoken in an epitome; the actual fights, meeting, and names still have differing versions in their own lays.",
      }),
      'zh-Hant-mandarin': track("norse-gripisspo-vengeance-and-dragon", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "格里皮爾接着說父親與埃利米的仇、洪丁之子、法夫尼爾、雷金和寶藏。他又說到火光中的沉睡女子與會傳授智慧的相遇。這些是摘要詩預先說出的關卡；實際的戰鬥、相遇與命名，仍要在各自的詩歌裡看見不同版本。",
      }),
    },
  },
  {
    sceneId: "norse-gripisspo-truth-pressed",
    globalOrder: 145,
    storyId: "norse-gripisspo",
    stepId: "truth-pressed",
    sourceStepIds: ["truth-pressed", "forgetting-and-wooing"],
    sourceIds: ["poetic-edda-gripisspo-7-19-early-future", "poetic-edda-gripisspo-20-53-dark-future"],
    tracks: {
      en: track("norse-gripisspo-truth-pressed", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Gripir says his sight is finished, but Sigurth knows he has left words unsaid. Pressed further, the prophecy turns to Gjuki's house, a drink of forgetting, wooing for Gunnar, and Brynhild's anger. The introduction says this order joins conflicting traditions. The timeline therefore shows how Gripir speaks, not a single biography of Sigurth.",
      }),
      'zh-Hant-mandarin': track("norse-gripisspo-truth-pressed", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "格里皮爾說所見已盡，西格爾德卻知道他還有話沒有說。在逼問下，預言轉入久基之家、遺忘的飲料、代貢納爾求婚和布琳希爾德的怨恨。引言明說這個次序把互相衝突的傳統接在一起。時間線因此只呈現格里皮爾如何說，不把它變成唯一的西古爾德傳記。",
      }),
    },
  },
  {
    sceneId: "norse-gripisspo-death-spoken",
    globalOrder: 146,
    storyId: "norse-gripisspo",
    stepId: "death-spoken",
    sourceStepIds: ["death-spoken"],
    sourceIds: ["poetic-edda-gripisspo-20-53-dark-future"],
    tracks: {
      en: track("norse-gripisspo-death-spoken", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Sigurth does not let his uncle leave a blank; he asks who will bring death. Gripir finally speaks betrayal and death to the end. The poem does not replay the killing scene of any lay, nor does prophecy excuse anyone from it. It ends at departure: knowing the future is not escaping it, and Sigurth still rides away from the hall.",
      }),
      'zh-Hant-mandarin': track("norse-gripisspo-death-spoken", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "西格爾德不准外舅留下空白，要求知道死亡由誰帶來。格里皮爾終於把背叛與死說到最後。詩沒有重演任何一首詩中的殺局，也沒有讓預言替人免除它。它在告別處結束：知道未來不是逃出未來，西格爾德仍要騎離這座廳堂。",
      }),
    },
  },
  {
    sceneId: "norse-svipdagsmol-stepmothers-charge",
    globalOrder: 147,
    storyId: "norse-svipdagsmol",
    stepId: "stepmothers-charge",
    sourceStepIds: ["stepmothers-charge", "calling-groa"],
    sourceIds: ["poetic-edda-svipdagsmol-grogaldr-1-16"],
    tracks: {
      en: track("norse-svipdagsmol-stepmothers-charge", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Svipdag says that his stepmother has ordered him to find Mengloth, without giving her reason. Afraid of the errand, he comes to Groa's grave threshold and calls on his dead mother to answer. Groa first asks why her son has come to seek her still; he admits that he fears the road. The dialogue leaves an unnamed stepmother and a command that is hard to bear.",
      }),
      'zh-Hant-mandarin': track("norse-svipdagsmol-stepmothers-charge", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 40,
        text: "斯維普達格說繼母要他去尋孟格洛斯，卻沒有說明理由。害怕這個差事時，他走到格羅阿的墳門前，叫已逝的母親回答。格羅阿先問兒子為何仍要來找她；他承認自己怕那條路。這段對話只留下無名的繼母與一件難以承擔的命令。故事不替繼母安排心思，也不把這個開端改成一場已有地圖的英雄出征。",
      }),
    },
  },
  {
    sceneId: "norse-svipdagsmol-nine-charms",
    globalOrder: 148,
    storyId: "norse-svipdagsmol",
    stepId: "nine-charms",
    sourceStepIds: ["nine-charms", "unwritten-passage"],
    sourceIds: ["poetic-edda-svipdagsmol-grogaldr-1-16", "poetic-edda-svipdagsmol-introduction", "poetic-edda-svipdagsmol-fjolsvinnsmol-17-58"],
    tracks: {
      en: track("norse-svipdagsmol-nine-charms", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Groa speaks nine charms in turn, asking protection against dangers her son may meet. She gives words and protection, not passage through any gate for him. When her charms end, the next poem already has Svipdag before a hall ringed by fire. No journey survives between the two passages. This timeline leaves that blank where it is: protective charms cannot be turned into a chain of trials the text never gives.",
      }),
      'zh-Hant-mandarin': track("norse-svipdagsmol-nine-charms", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 38,
        text: "格羅阿逐一念出九道咒，為兒子可能遇到的危險求護。她給的是話與保護，並沒有替他跨過任何一座門。她的咒語結束後，下一首詩已直接讓斯維普達格站在火焰環繞的廳前。兩段之間沒有保留下來的行程。這條時間線把空白留在原處：不能從護身咒推演出一連串文本沒有寫下的試煉。",
      }),
    },
  },
  {
    sceneId: "norse-svipdagsmol-gate-questioning",
    globalOrder: 149,
    storyId: "norse-svipdagsmol",
    stepId: "gate-questioning",
    sourceStepIds: ["gate-questioning", "hall-names"],
    sourceIds: ["poetic-edda-svipdagsmol-fjolsvinnsmol-17-58"],
    tracks: {
      en: track("norse-svipdagsmol-gate-questioning", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Fjolsvith asks the visitor where he comes from and whom he seeks. Svipdag initially calls himself 'Wind-Cold,' and the gatekeeper rejects him with the fire and defences outside the hall. The exchange goes deeper, naming Mengloth, Lyfjaberg, and many defences. Some names are unstable in the witnesses and editorial handling, yet they give rhythm to the refusal at the gate.",
      }),
      'zh-Hant-mandarin': track("norse-svipdagsmol-gate-questioning", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 47,
        text: "菲奧爾斯維德在廳前問來客從何而來、要找誰。斯維普達格起初自稱「風寒」，守門人便以廳外的火焰與防備拒絕他。問答越說越深，帶出孟格洛斯、萊夫亞貝格與許多守備。這些名字有些在傳本與編校裡並不穩定，卻足以讓門前的拒絕成為兩人說話的節奏。故事不把名稱清單硬接成一條可以破解的關卡，因為詩本身沒有讓斯維普達格實際走完那個循環。",
      }),
    },
  },
  {
    sceneId: "norse-svipdagsmol-name-recognized",
    globalOrder: 150,
    storyId: "norse-svipdagsmol",
    stepId: "name-recognized",
    sourceStepIds: ["name-recognized", "mutual-recognition"],
    sourceIds: ["poetic-edda-svipdagsmol-fjolsvinnsmol-59-66"],
    tracks: {
      en: track("norse-svipdagsmol-name-recognized", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Fjolsvith finally learns that the visitor is Svipdag, and the gate-side questioning stops. Mengloth says that she has been waiting at Lyfjaberg, and Svipdag also speaks of long longing. This version closes at recognition, without supplying a wedding, a later life, or a fixed identification of Mengloth with any other goddess.",
      }),
      'zh-Hant-mandarin': track("norse-svipdagsmol-name-recognized", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "菲奧爾斯維德終於知道來客是斯維普達格，門前的盤問便停止了。孟格洛斯說自己一直在萊夫亞貝格等待；斯維普達格也說出長久的想念。這個版本在相認處收束，不補上婚禮、後來的生活，或把孟格洛斯固定認作任何其他女神。",
      }),
    },
  },
  {
    sceneId: "norse-grottasongr-two-women-at-mill",
    globalOrder: 151,
    storyId: "norse-grottasongr",
    stepId: "two-women-at-mill",
    sourceStepIds: ["two-women-at-mill", "wealth-and-peace"],
    sourceIds: ["grottasongr-1-8-labour-and-peace"],
    tracks: {
      en: track("norse-grottasongr-two-women-at-mill", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Fenja and Menja arrive at Frodi's house and are set before the great mill called Grotti. The mill can make what its turners name. The king first demands wealth, peace, and prosperity; the song even wishes that no one should harm another. Peace here is not a blessing freely offered by the two giant women. It is a product the king first commands.",
      }),
      'zh-Hant-mandarin': track("norse-grottasongr-two-women-at-mill", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "芬雅與曼雅到了弗羅迪家，被帶到名為格羅蒂的巨大磨坊前。這座磨能隨推磨者的話產出所求之物。王要她們先磨出財富、太平和福祉；歌裡的願語甚至說，世上不應有人傷害另一人。太平在這裡不是兩位巨女自由給出的祝福，而是王命先要求的產物。",
      }),
    },
  },
  {
    sceneId: "norse-grottasongr-no-rest",
    globalOrder: 152,
    storyId: "norse-grottasongr",
    stepId: "no-rest",
    sourceStepIds: ["no-rest", "king-does-not-know"],
    sourceIds: ["grottasongr-1-8-labour-and-peace", "grottasongr-9-16-giant-memory"],
    tracks: {
      en: track("norse-grottasongr-no-rest", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "Frodi hears the song but only orders them on: no rest longer than a cuckoo's call or one of his own songs. The two women answer that when he bought them, he saw only strength and appearance, never asking where they came from. The sharper the command, the less the mill-song remains only a blessing for the king.",
      }),
      'zh-Hant-mandarin': track("norse-grottasongr-no-rest", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "弗羅迪聽見歌，卻只吩咐她們繼續；休息不得長過杜鵑鳴叫，或他念一首歌。兩女回應，他買下她們時只看見力量與外貌，從沒問過她們的來歷。命令越短促，磨坊裡的歌越不再只是替王祝福。",
      }),
    },
  },
  {
    sceneId: "norse-grottasongr-giant-memory",
    globalOrder: 153,
    storyId: "norse-grottasongr",
    stepId: "giant-memory",
    sourceStepIds: ["giant-memory", "cold-at-frodi-house"],
    sourceIds: ["grottasongr-9-16-giant-memory"],
    tracks: {
      en: track("norse-grottasongr-giant-memory", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Fenja and Menja look back in song to giant kin, moved mountains, and a Swedish battlefield; these are both their memories and a warning to the king. Yet they now serve in Frodi's house, feet in mud and heads in cold, turning the millstone they call an enemy. The poem lets ancient strength and present labour be spoken together, without replacing the women's voices with a larger genealogy.",
      }),
      'zh-Hant-mandarin': track("norse-grottasongr-giant-memory", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "芬雅與曼雅在歌裡回望巨人親族、移動山石與瑞典戰場；這些既是自身記憶，也是給王的提醒。她們現在卻在弗羅迪家受役，腳踩泥濘、頭頂寒冷，推著被稱為敵人的磨石。詩讓遠古力量和眼前勞役同時被說出，不用一段更大的家譜取代兩人的聲音。",
      }),
    },
  },
  {
    sceneId: "norse-grottasongr-fire-and-fall",
    globalOrder: 154,
    storyId: "norse-grottasongr",
    stepId: "fire-and-fall",
    sourceStepIds: ["fire-and-fall", "mill-breaks"],
    sourceIds: ["grottasongr-17-24-fall-and-ending"],
    tracks: {
      en: track("norse-grottasongr-fire-and-fall", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "The women say their hands will not be allowed rest, then drive the mill-song toward bloodied weapons, fire at the king's hall, and Frodi's lost throne. The mill trembles and the heavy stone breaks; they say that it is ground. The twenty-four stanzas end here. Adjacent prose tells of a night raid and sea salt, but this timeline does not turn that later tale into a scene at this mill.",
      }),
      'zh-Hant-mandarin': track("norse-grottasongr-fire-and-fall", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "兩女說手不會獲准休息，隨即把磨聲推向帶血武器、王城火光和弗羅迪將失去的王座。磨坊震動，重石裂開；她們說：已磨完。二十四節歌到此結束。相鄰散文另講夜襲與海鹽，這條時間線不把那個後日談演進這座磨坊。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-i-gudrun-cannot-weep",
    globalOrder: 155,
    storyId: "norse-guthrunarkvitha-i",
    stepId: "gudrun-cannot-weep",
    sourceStepIds: ["gudrun-cannot-weep", "gjaflaug-speaks"],
    sourceIds: ["poetic-edda-guthrunarkvitha-i-1-25"],
    tracks: {
      en: track("norse-guthrunarkvitha-i-gudrun-cannot-weep", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Guthrun keeps vigil beside Sigurd's body, near to breaking but unable to weep. Gjaflaug tells of her own losses, yet cannot reach her tears. This is not a contest over who has lost more. The poem lets the first comfort fail, preserving Guthrun's grief before she can speak it. Gjaflaug's name appears only in this lament, so the route does not invent a biography for her beyond it.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-i-gudrun-cannot-weep", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "古德倫守在西古爾德遺體旁，心近乎裂開卻哭不出來。賈夫勞格說起自己的失親，仍沒有碰到她的眼淚。這不是比較誰失去更多；詩讓第一份安慰失效，保留古德倫尚未能說出口的悲傷。賈夫勞格的名字只在這首悼歌裡出現，因此這條線也不替她補出詩外家譜。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-i-herborg-speaks",
    globalOrder: 156,
    storyId: "norse-guthrunarkvitha-i",
    stepId: "herborg-speaks",
    sourceStepIds: ["herborg-speaks", "gollrond-unveils-body"],
    sourceIds: ["poetic-edda-guthrunarkvitha-i-1-25"],
    tracks: {
      en: track("norse-guthrunarkvitha-i-herborg-speaks", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Herborg finishes her account of loss, burial, and captivity, and Guthrun remains silent. Gollrond instead has Sigurd's shroud lifted and his head placed on Guthrun's knees. The two comforters' experience does not replace Guthrun's grief; Gollrond changes whether she can face the body herself. This turning point belongs only to the poem's lament scene and does not repeat Sigurd's death story.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-i-herborg-speaks", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "赫爾堡講完失親、埋葬與受俘，古德倫仍然沉默。戈爾倫德改命人揭開西古爾德的裹屍布，把他的頭放到古德倫膝上。兩個安慰者的經驗沒有被用來替代古德倫的悲傷；戈爾倫德改變的是她能否親眼面對遺體。這個轉折只屬本詩的悼歌場景，不重複西古爾德的死亡故事。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-i-gudrun-weeps",
    globalOrder: 157,
    storyId: "norse-guthrunarkvitha-i",
    stepId: "gudrun-weeps",
    sourceStepIds: ["gudrun-weeps", "gudrun-accuses-brothers"],
    sourceIds: ["poetic-edda-guthrunarkvitha-i-1-25"],
    tracks: {
      en: track("norse-guthrunarkvitha-i-gudrun-weeps", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Guthrun sees blood, blinded eyes, and the chest wound, then finally weeps. She immediately directs grief toward Gjuki's sons and names Gunnar. Her accusation is speech inside a lament, not a cross-text trial completed for the reader. Only after tears does the poem let responsibility be spoken, holding grief and blame in the same breath.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-i-gudrun-weeps", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "古德倫看見血、失明的眼和胸口的傷，終於哭出來。她隨即把悲傷指向久基諸子，並點名貢納爾。她的控訴是悼詞中的發話，不替讀者完成一場跨文本的審判。詩在哭泣後才讓責任被說出，因而把哀傷與指責放在同一口氣裡。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-i-gudrun-curses-gold",
    globalOrder: 158,
    storyId: "norse-guthrunarkvitha-i",
    stepId: "gudrun-curses-gold",
    sourceStepIds: ["gudrun-curses-gold", "brynhild-answers"],
    sourceIds: ["poetic-edda-guthrunarkvitha-i-1-25"],
    tracks: {
      en: track("norse-guthrunarkvitha-i-gudrun-curses-gold", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Guthrun curses Gunnar's gold and points grief toward Brynhild. Brynhild in turn blames Atli; the stanza itself is damaged and does not agree with other versions. Closing prose has Guthrun leave and mentions Brynhild's death, while leaving details to other material. The lament stops with departure: it lets several accounts meet without sealing any one as the single Sigurd story.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-i-gudrun-curses-gold", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 40,
        text: "古德倫詛咒貢納爾的金子，又把不幸指向布琳希爾德。布琳希爾德反過來把痛苦歸咎阿特利；這一節本身損壞，且與其他版本並不一致。結尾散文讓古德倫離開，並提到布琳希爾德死亡，卻把細節留給別的材料。悼歌在離開處停下：它讓多份說法相碰，沒有把其中一份封成唯一的西古爾德故事。",
      }),
    },
  },
  {
    sceneId: "norse-sigurtharkvitha-en-skamma-oaths-with-gunnar",
    globalOrder: 159,
    storyId: "norse-sigurtharkvitha-en-skamma",
    stepId: "oaths-with-gunnar",
    sourceStepIds: ["oaths-with-gunnar", "gudrun-given", "riding-to-brynhild"],
    sourceIds: ["poetic-edda-sigurtharkvitha-death"],
    tracks: {
      en: track("norse-sigurtharkvitha-en-skamma-oaths-with-gunnar", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "The Short Lay first has Sigurd trust Gjuki's sons and swear oaths with them; Guthrun is given to him, and he rides with Gunnar toward Brynhild. These arrangements place marriage and alliance in one royal house without turning them into a single seamless genealogy or backstory. The shared ride is this lay's beginning, not proof that every version of the wooing is the same.",
      }),
      'zh-Hant-mandarin': track("norse-sigurtharkvitha-en-skamma-oaths-with-gunnar", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "短歌先讓西古爾德信任久基的兒子，與他們立誓；古德倫被許給他，西古爾德隨貢納爾向布琳希爾德而去。這些安排把婚姻和盟誓放在同一個王家內，卻不替讀者整理成唯一無縫的家譜或前史。同行是本歌的開端，不是往後每一版本都相同的求婚證明。",
      }),
    },
  },
  {
    sceneId: "norse-sigurtharkvitha-en-skamma-sword-between",
    globalOrder: 160,
    storyId: "norse-sigurtharkvitha-en-skamma",
    stepId: "sword-between",
    sourceStepIds: ["sword-between", "brynhild-speaks", "brynhild-presses-gunnar"],
    sourceIds: ["poetic-edda-sigurtharkvitha-death"],
    tracks: {
      en: track("norse-sigurtharkvitha-en-skamma-sword-between", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "This lay says a sword lay between Sigurd and Brynhild in bed, then lets Brynhild speak alone of wanting Sigurd. She presses Gunnar with departure and Sigurd's death; this is pressure inside the poem, not a responsibility chart settled for the whole tradition. The sword, desire, and threat remain within the Short Lay's handling, because other lays and sagas do not fully agree.",
      }),
      'zh-Hant-mandarin': track("norse-sigurtharkvitha-en-skamma-sword-between", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "本歌說西古爾德和布琳希爾德同床時有一把劍隔在中間，接著讓布琳希爾德獨自說出她想要西古爾德。她以離去逼迫貢納爾面對西古爾德之死；這是詩中的壓力，不是整個傳統可以一次判定的責任表。劍、欲望與威脅都留在短歌的處理方式裡，因為其他詩和傳奇並不完全一致。",
      }),
    },
  },
  {
    sceneId: "norse-sigurtharkvitha-en-skamma-hogni-refuses",
    globalOrder: 161,
    storyId: "norse-sigurtharkvitha-en-skamma",
    stepId: "hogni-refuses",
    sourceStepIds: ["hogni-refuses", "gunnar-chooses-gotthorm", "gotthorm-strikes"],
    sourceIds: ["poetic-edda-sigurtharkvitha-death"],
    tracks: {
      en: track("norse-sigurtharkvitha-en-skamma-hogni-refuses", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Hogni says the oaths with Sigurd must not be broken by swords. Gunnar instead chooses Gotthorm, who did not share the oaths; after a gap, the sword is already in Sigurd's heart. The timeline does not fill the blank left in stanza 21 or add motives the Short Lay never gives Gotthorm. This is a bedside, indoor killing version; other material places the death outdoors, and neither is erased.",
      }),
      'zh-Hant-mandarin': track("norse-sigurtharkvitha-en-skamma-hogni-refuses", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 38,
        text: "霍格尼說不該以劍破壞和西古爾德立下的誓。貢納爾卻選中沒有參與誓言的戈特霍姆；遺失段落之後，劍已在西古爾德心口。時間線不填補第21節留下的空白，也不替戈特霍姆增加短歌沒有交代的心思。這是一個床邊／室內殺局版本；其他材料把死亡放在戶外，兩者都不被抹平。",
      }),
    },
  },
  {
    sceneId: "norse-sigurtharkvitha-en-skamma-sigurd-strikes-back",
    globalOrder: 162,
    storyId: "norse-sigurtharkvitha-en-skamma",
    stepId: "sigurd-strikes-back",
    sourceStepIds: ["sigurd-strikes-back", "gudrun-wakes", "sigurd-warns-gudrun"],
    sourceIds: ["poetic-edda-sigurtharkvitha-death"],
    tracks: {
      en: track("norse-sigurtharkvitha-en-skamma-sigurd-strikes-back", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Sigurd rises and kills Gotthorm in return. As Guthrun wakes beside him, he warns that their young son cannot escape and, in this lay, names Brynhild. These are the words of a dying man; his attribution is not elevated into a cross-text final verdict. The lay leaves an open wound: the oaths are broken and the return stroke is complete, but the family feud has not ended.",
      }),
      'zh-Hant-mandarin': track("norse-sigurtharkvitha-en-skamma-sigurd-strikes-back", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "西古爾德起身反殺戈特霍姆。古德倫在他身邊醒來時，西古爾德警告幼子無路可逃，並在本歌中把責任指向布琳希爾德。這是臨終者的話，不把他的指認升格成跨文本的最後裁決。短歌到此保留一個沒有真正關上的傷口：誓言已破，反擊完成，家族仇恨卻沒有結束。",
      }),
    },
  },
  {
    sceneId: "norse-helreith-brynhildar-brynhild-rides",
    globalOrder: 163,
    storyId: "norse-helreith-brynhildar",
    stepId: "brynhild-rides",
    sourceStepIds: ["brynhild-rides", "giantess-challenges"],
    sourceIds: ["poetic-edda-helreith-brynhildar-1-14"],
    tracks: {
      en: track("norse-helreith-brynhildar-brynhild-rides", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "After the pyre, Brynhild rides toward Hel. The poem already differs from other funeral accounts, so the timeline follows only this lay's wagon journey. The rock giantess stops her, not to announce a verdict but to force a hostile exchange. Here the road after death is a scene of speech: Brynhild must still answer how others tell her story.",
      }),
      'zh-Hant-mandarin': track("norse-helreith-brynhildar-brynhild-rides", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "火葬後，布琳希爾德乘車走向赫爾之路。詩一開始就和其他火葬說法不同，因此時間線只跟隨這一首歌的車行。岩居女巨人攔住她，並非為了宣布判決，而是逼出一場帶敵意的問答。死後道路在這裡是一個說話的場面：布琳希爾德仍要回應別人如何講述她。",
      }),
    },
  },
  {
    sceneId: "norse-helreith-brynhildar-giantess-accuses",
    globalOrder: 164,
    storyId: "norse-helreith-brynhildar",
    stepId: "giantess-accuses",
    sourceStepIds: ["giantess-accuses", "brynhild-answers"],
    sourceIds: ["poetic-edda-helreith-brynhildar-1-14"],
    tracks: {
      en: track("norse-helreith-brynhildar-giantess-accuses", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "The giantess says Brynhild brought down Gjuki's house; Brynhild answers that Gjuki's heirs made her joyless and a breaker of oaths. Both sides speak of responsibility, but the timeline does not complete a trial for the reader. The poem lets conflicting accounts stand face to face without requiring one to become the only answer for the tradition.",
      }),
      'zh-Hant-mandarin': track("norse-helreith-brynhildar-giantess-accuses", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "女巨人說布琳希爾德害久基家衰敗；布琳希爾德反說久基的後人使她不幸、成為破誓者。兩邊都在說責任，但這不是時間線替讀者完成的審判。詩讓互相衝突的說法面對面存在，並不要求我們把其中一方變成全傳統唯一答案。",
      }),
    },
  },
  {
    sceneId: "norse-helreith-brynhildar-brynhild-recalls-battle",
    globalOrder: 165,
    storyId: "norse-helreith-brynhildar",
    stepId: "brynhild-recalls-battle",
    sourceStepIds: ["brynhild-recalls-battle", "odin-punishes"],
    sourceIds: ["poetic-edda-helreith-brynhildar-1-14"],
    tracks: {
      en: track("norse-helreith-brynhildar-brynhild-recalls-battle", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Brynhild says she sent Hjalmgunnar down to Hel and gave victory to another man, drawing Odin's anger. Shields, sleep, and high fire follow. This is her own version of the past, not one punishment rule for every valkyrie story. The ring of fire makes the next arrival recognizable without reducing Brynhild to someone who only waits to be rescued.",
      }),
      'zh-Hant-mandarin': track("norse-helreith-brynhildar-brynhild-recalls-battle", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "布琳希爾德說她使希亞爾姆貢納爾下冥，把勝利給另一人；奧丁因此震怒。接著是盾牆、沉睡和高火。這是她回顧自身過去的版本，不替所有女武神故事訂出同一套懲罰規則。火環讓下一個來者必須被辨認，卻不把布琳希爾德壓成只有等待被救的一個角色。",
      }),
    },
  },
  {
    sceneId: "norse-helreith-brynhildar-sigurd-crosses-fire",
    globalOrder: 166,
    storyId: "norse-helreith-brynhildar",
    stepId: "sigurd-crosses-fire",
    sourceStepIds: ["sigurd-crosses-fire", "shared-bed"],
    sourceIds: ["poetic-edda-helreith-brynhildar-1-14"],
    tracks: {
      en: track("norse-helreith-brynhildar-sigurd-crosses-fire", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 45,
        text: "Sigurd crosses the fire with the gold Fafnir guarded. Brynhild says they shared a bed untouched for eight nights; Guthrun's words then make her learn she was deceived. This version includes a possibly interpolated stanza and does not fully agree with other Sigurd material. She ends by saying she and Sigurd will still live together; the timeline stops at that defense instead of making it a verified afterlife rule.",
      }),
      'zh-Hant-mandarin': track("norse-helreith-brynhildar-sigurd-crosses-fire", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "西古爾德帶著法夫尼爾守護的金穿過火環。布琳希爾德說二人同床八夜未相碰；古德倫的話則讓她得知受騙。這個版本既有可能插入的詩節，也同其他西古爾德材料不完全一致。她最後說自己仍會同西古爾德活著；時間線停在這句辯白，不把它做成已驗證的死後規則。",
      }),
    },
  },
  {
    sceneId: "norse-brot-sigurtharkvithu-oath-dispute",
    globalOrder: 167,
    storyId: "norse-brot-sigurtharkvithu",
    stepId: "oath-dispute",
    sourceStepIds: ["oath-dispute", "gotthorm-prepared"],
    sourceIds: ["poetic-edda-brot-sigurtharkvithu-1-20"],
    tracks: {
      en: track("norse-brot-sigurtharkvithu-oath-dispute", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Gunnar says Sigurd broke oaths, and Hogni points to Brynhild's anger. Grimhild then has Gotthorm fed wolf and snake flesh. The fragment does not retain all the material before this dispute, so the timeline does not supply anyone's full motive. The feeding is an old motif for inciting ferocity, not a verifiable psychological diagnosis.",
      }),
      'zh-Hant-mandarin': track("norse-brot-sigurtharkvithu-oath-dispute", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "貢納爾說西古爾德破誓；霍格尼把恨意指向布琳希爾德。格里姆希爾德隨即讓人餵戈特霍姆狼與蛇肉。殘篇沒有留下這場爭辯之前的全部材料，因此時間線不替誰補出完整動機。餵食是一個古老的煽動凶暴母題，不是可驗證的心理診斷。",
      }),
    },
  },
  {
    sceneId: "norse-brot-sigurtharkvithu-outdoor-death",
    globalOrder: 168,
    storyId: "norse-brot-sigurtharkvithu",
    stepId: "outdoor-death",
    sourceStepIds: ["outdoor-death", "raven-speaks"],
    sourceIds: ["poetic-edda-brot-sigurtharkvithu-1-20"],
    tracks: {
      en: track("norse-brot-sigurtharkvithu-outdoor-death", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "The fragment places Sigurd's death outdoors south of the Rhine. The raven then speaks of Atli's blade, chains, and the Niflungs' consequences. Closing prose also admits a different bedside killing; this scene follows only the outdoor version and does not delete the other. The raven's words point later, rather than being fulfilled here early.",
      }),
      'zh-Hant-mandarin': track("norse-brot-sigurtharkvithu-outdoor-death", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "殘篇把西古爾德的死亡放在萊茵河以南的戶外。烏鴉隨後說阿特利之刃、鎖鏈與尼夫龍家的後果。結尾散文也承認另一種床邊殺局；這一幕只走戶外版本，沒有把另一個版本刪去。烏鴉的話指向後來，並不在此刻提前實現。",
      }),
    },
  },
  {
    sceneId: "norse-brot-sigurtharkvithu-gudrun-grieves",
    globalOrder: 169,
    storyId: "norse-brot-sigurtharkvithu",
    stepId: "gudrun-grieves",
    sourceStepIds: ["gudrun-grieves", "brynhild-answers"],
    sourceIds: ["poetic-edda-brot-sigurtharkvithu-1-20"],
    tracks: {
      en: track("norse-brot-sigurtharkvithu-gudrun-grieves", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 31,
        text: "Guthrun grieves for Sigurd, while Brynhild tells Gunnar that Sigurd kept his oaths. Her words conflict with Gunnar's accusation; the timeline keeps both accounts visible rather than deciding which is true. After Sigurd's death, no single account or feeling prevails at court.",
      }),
      'zh-Hant-mandarin': track("norse-brot-sigurtharkvithu-gudrun-grieves", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 28,
        text: "古德倫為西古爾德哀哭，布琳希爾德卻對貢納爾說西古爾德曾守誓。她的話與貢納爾的指控互相衝突；時間線讓兩種說法同時可見，而非裁決誰是真相。西古爾德死後，王庭沒有人只剩下一種說法或感受。",
      }),
    },
  },
  {
    sceneId: "norse-brot-sigurtharkvithu-sword-between",
    globalOrder: 170,
    storyId: "norse-brot-sigurtharkvithu",
    stepId: "sword-between",
    sourceStepIds: ["sword-between", "brynhild-dreams"],
    sourceIds: ["poetic-edda-brot-sigurtharkvithu-1-20"],
    tracks: {
      en: track("norse-brot-sigurtharkvithu-sword-between", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Brynhild recalls the sharp sword between her and Sigurd, then dreams Gunnar bound and the Niflungs falling. The sword belongs to her version of the wooing, and the dream is only speech reaching toward a future after the killing. The fragment stops at prophecy's edge and leaves later heroic lays to handle its echoes.",
      }),
      'zh-Hant-mandarin': track("norse-brot-sigurtharkvithu-sword-between", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "布琳希爾德回憶鋒利的劍隔在她與西古爾德之間，又夢見貢納爾被束縛、尼夫龍家墜落。隔床之劍屬她提出的求婚版本；夢也只是殺局後向未來伸出的說法。殘篇停在預言的邊緣，讓後續英雄詩處理它們的回聲。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-ii-opening",
    globalOrder: 171,
    storyId: "norse-guthrunarkvitha-ii",
    stepId: "opening",
    sourceStepIds: ["opening", "marriage"],
    sourceIds: ["poetic-edda-guthrunarkvitha-ii-1-45"],
    tracks: {
      en: track("norse-guthrunarkvitha-ii-opening", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Thjothrek and Guthrun grieve together at Atli's court; Guthrun begins with Giuki giving her to Sigurd. The prose frame carries an anachronism and is not historical court fact confirmed by the timeline. It makes Guthrun a narrator of recollection, not someone watched only in a death scene.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-ii-opening", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "提奧達雷克與古德倫同在阿特利處哀哭；古德倫從久基把她交給西古爾德說起。這個散文框架帶有時代錯置，不是時間線替讀者確認的歷史宮廷。它讓古德倫成為回顧者，而非只在一個死亡場面被觀看的人。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-ii-grani-returns",
    globalOrder: 172,
    storyId: "norse-guthrunarkvitha-ii",
    stepId: "grani-returns",
    sourceStepIds: ["grani-returns", "death-recalled"],
    sourceIds: ["poetic-edda-guthrunarkvitha-ii-1-45"],
    tracks: {
      en: track("norse-guthrunarkvitha-ii-grani-returns", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Grani returns without his master, and Guthrun asks him for news. Hogni says Sigurd died on the southern road. This is a recollected forest/outdoor version; it cannot correct the bedside version in the Short Lay or the Fragment's arrangement. The horse's lowered head and the brothers' words make loss felt before it is spoken.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-ii-grani-returns", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "格拉尼無主人返回，古德倫向牠求訊。霍格尼說西古爾德死在南路。這是森林／戶外版本的回顧，不能改正短歌的床邊版本或殘篇的安排。馬的低頭與兄弟的話讓失去先被感到，才被說出。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-ii-denmark",
    globalOrder: 173,
    storyId: "norse-guthrunarkvitha-ii",
    stepId: "denmark",
    sourceStepIds: ["denmark", "return"],
    sourceIds: ["poetic-edda-guthrunarkvitha-ii-1-45"],
    tracks: {
      en: track("norse-guthrunarkvitha-ii-denmark", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "After the killing, Guthrun lives in Denmark with Thora. Grimhild finds her, and Gjuki's house again brings her into a marriage arrangement with Atli. Omissions and interpolations do not allow a complete process of persuasion to be invented. This scene records return and arrangement without letting 'amends' erase Guthrun's grief.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-ii-denmark", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "古德倫離開殺局後在丹麥與索拉同住。格里姆希爾德找到她，久基家再次把她帶進與阿特利的婚配安排。遺失和插入的行文不容許我們補出一條完整的說服過程。這一幕只寫回返與安排，讓古德倫的哀傷沒有被『補償』抹掉。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-ii-atli-court",
    globalOrder: 174,
    storyId: "norse-guthrunarkvitha-ii",
    stepId: "atli-court",
    sourceStepIds: ["atli-court", "dream"],
    sourceIds: ["poetic-edda-guthrunarkvitha-ii-1-45"],
    tracks: {
      en: track("norse-guthrunarkvitha-ii-atli-court", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 33,
        text: "Even at Atli's court, Guthrun still remembers Sigurd. Atli dreams danger to their sons; Guthrun answers and joins foreboding to her brothers. The dream is an omen within dialogue, not an early enactment of the sons' deaths. The lay stops at fear of the future and lets later material handle its echo.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-ii-atli-court", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "即使在阿特利處，古德倫仍回想西古爾德。阿特利夢見子嗣危險，古德倫回答並把不祥感連向兄弟。夢是一段對話中的預兆，不是時間線提前展演兒子的死亡。這首歌停在對未來的恐懼，讓後續材料自己處理它的回聲。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-iii-accusation",
    globalOrder: 175,
    storyId: "norse-guthrunarkvitha-iii",
    stepId: "accusation",
    sourceStepIds: ["accusation", "atli-questions"],
    sourceIds: ["poetic-edda-guthrunarkvitha-iii-1-10"],
    tracks: {
      en: track("norse-guthrunarkvitha-iii-accusation", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Herkja says she saw Guthrun and Thjothrek together in bed. Atli's troubled silence hardens into a direct question. The accusation belongs to one speaker in the poem; being spoken first does not make it proven. Guthrun must answer in a court that has mistaken private grief for betrayal.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-iii-accusation", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "赫爾嘉說自己看見古德倫和提奧德里克同臥。阿特利的沉默與怒意隨即轉成直接的質問。這是詩中一方提出的告發；它不會因為先說出口便成為已證實的事。古德倫必須在一個把私密哀傷誤讀成背叛的宮廷裡回答。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-iii-gudrun-oath",
    globalOrder: 176,
    storyId: "norse-guthrunarkvitha-iii",
    stepId: "gudrun-oath",
    sourceStepIds: ["gudrun-oath", "gudrun-grief"],
    sourceIds: ["poetic-edda-guthrunarkvitha-iii-1-10"],
    tracks: {
      en: track("norse-guthrunarkvitha-iii-gudrun-oath", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "Guthrun swears by the sacred stone that she and Thjothrek only spoke their griefs in private. She then tells Atli that he took her brothers and kinsmen. The lay names her meeting with Thjothrek as shared grief and supplies no fuller biography for their historical identities. She does not leave grief in memory; she brings it before Atli.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-iii-gudrun-oath", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "古德倫以聖石起誓，說她和提奧德里克只是暗中互訴悲傷。她接著對阿特利說，他奪走了她的兄弟與族人。詩歌把提奧德里克和她的相處說成哀傷的交談，也不替兩人的歷史身份補上更多細節。她沒有讓哀傷只停在回憶裡，而是把它帶到阿特利面前。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-iii-saxi-called",
    globalOrder: 177,
    storyId: "norse-guthrunarkvitha-iii",
    stepId: "saxi-called",
    sourceStepIds: ["saxi-called", "gudrun-tested"],
    sourceIds: ["poetic-edda-guthrunarkvitha-iii-1-10"],
    tracks: {
      en: track("norse-guthrunarkvitha-iii-saxi-called", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 32,
        text: "Guthrun summons Saxi to hallow the boiling cauldron, then reaches its bottom and brings out shining stones. Saxi has only the poem's title, \"southern king\"; the ritual setting does not need a supplied biography. Her hand is unhurt, and the accusation receives an answer that cannot be ignored.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-iii-saxi-called", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "古德倫召來薩克西祝聖沸水鍋，然後把手伸到鍋底，取出閃亮的石頭。薩克西在詩中只有「南方王」這個稱呼；鍋的儀式背景不必由後人補成完整傳記。她的手沒有受傷，於是控訴第一次有了不能忽略的回答。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarkvitha-iii-gudrun-cleared",
    globalOrder: 178,
    storyId: "norse-guthrunarkvitha-iii",
    stepId: "gudrun-cleared",
    sourceStepIds: ["gudrun-cleared", "herkja-tested"],
    sourceIds: ["poetic-edda-guthrunarkvitha-iii-1-10"],
    tracks: {
      en: track("norse-guthrunarkvitha-iii-gudrun-cleared", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 36,
        text: "Atli sees Guthrun's hand whole and orders Herkja to the same kettle. Herkja's hands are burned, and she is finally thrown into a bog. This is the ending given by this short lay, not a route into other stories of revenge or legal practice. Guthrun's grievance receives its answer here, and the lay stops with Herkja's punishment.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarkvitha-iii-gudrun-cleared", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "阿特利見古德倫的手完好，便要赫爾嘉到同一鍋前受試。赫爾嘉雙手燙傷，最後被投入泥沼。這是這首短歌給出的結局，不把它擴成其他故事裡的報復或法律制度。古德倫的冤屈在此得到回應，而這首歌也在赫爾嘉受罰處停下。",
      }),
    },
  },
  {
    sceneId: "norse-oddrunargratr-labour",
    globalOrder: 179,
    storyId: "norse-oddrunargratr",
    stepId: "labour",
    sourceStepIds: ["labour", "journey"],
    sourceIds: ["poetic-edda-oddrunargratr-1-32"],
    tracks: {
      en: track("norse-oddrunargratr-labour", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 31,
        text: "Borgny suffers in difficult labour. Hearing of it, Oddrun brings out her black horse and rides to the high hall. The lay's places and route are unstable; this scene follows only her move from hearing to arrival. When she reaches the bedside, danger has not yet become her own lament.",
      }),
      'zh-Hant-mandarin': track("norse-oddrunargratr-labour", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "博爾格尼因難產受苦，奧德倫得知後牽出黑馬，趕到高廳。這首歌的地名和路徑並不穩定；此處只跟隨她從聽聞到抵達的動作。她走進床邊時，危險還沒有被講成自己的哀歌。",
      }),
    },
  },
  {
    sceneId: "norse-oddrunargratr-secret",
    globalOrder: 180,
    storyId: "norse-oddrunargratr",
    stepId: "secret",
    sourceStepIds: ["secret", "birth"],
    sourceIds: ["poetic-edda-oddrunargratr-1-32"],
    tracks: {
      en: track("norse-oddrunargratr-secret", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "A servant says Vilmund and Borgny were secretly together for five years. Oddrun sits at the bedside and chants, and Borgny finally gives birth to a son and a daughter. The poem's phrase for Hogni's slayer needs a note to connect it with Vilmund, so it remains an attributed claim within the lay. The birth briefly draws pain away and brings the women's older history to the surface.",
      }),
      'zh-Hant-mandarin': track("norse-oddrunargratr-secret", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "侍女說維爾蒙德與博爾格尼秘密相伴五年。奧德倫坐在床邊施咒，博爾格尼終於生下一男一女。詩中對『霍格尼殺手』的稱呼需要註釋才能連到維爾蒙德，因此這裡保留它作為詩內的說法。新生讓痛苦暫時退去，也讓兩名女子的舊事開始浮現。",
      }),
    },
  },
  {
    sceneId: "norse-oddrunargratr-thanks",
    globalOrder: 181,
    storyId: "norse-oddrunargratr",
    stepId: "thanks",
    sourceStepIds: ["thanks", "lament"],
    sourceIds: ["poetic-edda-oddrunargratr-1-32"],
    tracks: {
      en: track("norse-oddrunargratr-thanks", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Borgny thanks Oddrun, but Oddrun says she only fulfilled an old oath. She begins to remember loving Gunnar and Atli's refusal of the marriage payment. This is Oddrun's first-person recollection and does not supply genealogy or marriage terms beyond her words. Helping another woman through labour makes her speak of a loss she could never cross.",
      }),
      'zh-Hant-mandarin': track("norse-oddrunargratr-thanks", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "博爾格尼感謝奧德倫，奧德倫卻說自己只是履行舊誓。她開始回憶對貢納爾的愛，以及阿特利拒絕接納婚配。這是奧德倫的第一人稱回顧，不把詩外的家譜與婚約補進她的話。幫別人渡過生產，反而讓她不得不說起自己從未渡過的失去。",
      }),
    },
  },
  {
    sceneId: "norse-oddrunargratr-spying",
    globalOrder: 182,
    storyId: "norse-oddrunargratr",
    stepId: "spying",
    sourceStepIds: ["spying", "rescue"],
    sourceIds: ["poetic-edda-oddrunargratr-1-32"],
    tracks: {
      en: track("norse-oddrunargratr-spying", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Atli sends men to spy on Oddrun and Gunnar; later Hogni is killed and Gunnar is put in the serpent cave. Oddrun hears the harp, sails to him, and cannot bring him back. Stanza 30's account of Atli's mother differs from other versions; this scene retains only the unnamed woman in Oddrun's lament. The story ends with Oddrun grieving for Gunnar, rather than deciding versions for other Atli stories in advance.",
      }),
      'zh-Hant-mandarin': track("norse-oddrunargratr-spying", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "阿特利派人窺探奧德倫和貢納爾；後來霍格尼被害，貢納爾落入蛇穴。奧德倫聽見豎琴聲，乘船趕去，卻無法挽回他。第30節對阿特利之母的說法與其他版本不合；這一幕只保留奧德倫哀歌中的未命名婦人。故事在奧德倫對貢納爾的哀哭裡結束，而不是替其他阿特利故事預先裁定版本。",
      }),
    },
  },
  {
    sceneId: "norse-atlakvitha-invitation",
    globalOrder: 183,
    storyId: "norse-atlakvitha",
    stepId: "invitation",
    sourceStepIds: ["invitation", "warning"],
    sourceIds: ["poetic-edda-atlakvitha-1-46-greenland-lay"],
    tracks: {
      en: track("norse-atlakvitha-invitation", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Atli sends Knefröth with promises of wealth. Guthrun, however, weaves wolf hair into a ring so her brothers can see that this is no ordinary invitation. The ring is this lay's warning, not a rewritten version of the runic message in the Atlamol. The brothers understand the danger and still choose to go through Myrkwood.",
      }),
      'zh-Hant-mandarin': track("norse-atlakvitha-invitation", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 31,
        text: "阿特利派克內弗羅特（Knefröth）帶著財寶承諾而來。古德倫卻把狼毛織進戒指，讓兄弟看出這不是普通邀請。這枚戒指是本歌的警告；不改寫成《阿特利敘事歌》的盧恩訊息。兄弟知道危險，仍決定經暗林赴約。",
      }),
    },
  },
  {
    sceneId: "norse-atlakvitha-journey",
    globalOrder: 184,
    storyId: "norse-atlakvitha",
    stepId: "journey",
    sourceStepIds: ["journey", "capture"],
    sourceIds: ["poetic-edda-atlakvitha-1-46-greenland-lay"],
    tracks: {
      en: track("norse-atlakvitha-journey", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "Gunnar and Hogni leave home with their followers. On reaching Atli's side, Gunnar is bound while Hogni still fights for him with his sword. The poem is defective here, so this scene holds only its explicit arrival, capture, and resistance. Atli then seeks the hoard, and Gunnar first demands to see Hogni's heart.",
      }),
      'zh-Hant-mandarin': track("norse-atlakvitha-journey", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "貢納爾和霍格尼帶著隨從離家。到達阿特利一方後，貢納爾被縛，霍格尼仍以劍替他奮戰。詩在這段有缺失，因此這一幕只接住它明說的抵達、俘獲與抵抗。阿特利接著要求寶藏，而貢納爾要先看霍格尼的心。",
      }),
    },
  },
  {
    sceneId: "norse-atlakvitha-hearts",
    globalOrder: 185,
    storyId: "norse-atlakvitha",
    stepId: "hearts",
    sourceStepIds: ["hearts", "hoard"],
    sourceIds: ["poetic-edda-atlakvitha-1-46-greenland-lay"],
    tracks: {
      en: track("norse-atlakvitha-hearts", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Hjalli's heart trembles on the platter, so Gunnar sees through the test. After Hogni dies, he takes the secret of the Niflungs' hoard toward the Rhine and the serpent den. Hjalli's death and the heart episode are affected by defective text; the story marks the uncertainty without inventing dialogue. In the serpent den, Gunnar still uses the harp to keep his choice not to surrender the hoard.",
      }),
      'zh-Hant-mandarin': track("norse-atlakvitha-hearts", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "赫亞利的心仍在盤上顫動，貢納爾因而識破試探。霍格尼死後，他把尼弗隆格的寶藏祕密帶往萊茵河與蛇穴。赫亞利遇害和心臟段落受殘缺文本影響，故事只標示其不確定性，不替它補對話。在蛇穴裡，貢納爾仍以豎琴守住不交出寶藏的選擇。",
      }),
    },
  },
  {
    sceneId: "norse-atlakvitha-feast",
    globalOrder: 186,
    storyId: "norse-atlakvitha",
    stepId: "feast",
    sourceStepIds: ["feast", "fire"],
    sourceIds: ["poetic-edda-atlakvitha-1-46-greenland-lay"],
    tracks: {
      en: track("norse-atlakvitha-feast", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "At the feast Guthrun lets Atli learn that he has eaten the hearts and flesh of his two sons. Drunk and unguarded, he is killed by her sword before she burns the hall. The poem gives the sons' deaths and the feast's result but not a full killing scene; this timeline does not turn silence into detail. She first lets the innocent dogs and slaves go, then revenge falls on those still in the hall.",
      }),
      'zh-Hant-mandarin': track("norse-atlakvitha-feast", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 34,
        text: "古德倫讓阿特利在宴席上得知自己吃下兩個兒子的心與肉。阿特利醉而無備，她用劍殺夫，再焚毀殿堂。詩給出兩子已死與宴席的結果，卻沒有完整描寫殺害的過程；這裡不把沉默變成細節。她先放走無辜的狗和奴僕，復仇才落在仍留在殿中的人身上。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarhvot-sea",
    globalOrder: 187,
    storyId: "norse-guthrunarhvot",
    stepId: "sea",
    sourceStepIds: ["sea", "svanhild"],
    sourceIds: ["poetic-edda-guthrunarhvot-1-22"],
    tracks: {
      en: track("norse-guthrunarhvot-sea", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Unable to drown at sea, Guthrun is carried to Jonak's land and bears Sorli, Erp, and Hamther. Svanhild, meanwhile, marries Jormunrek. This lineage comes from opening prose; a linked poem gives Erp a different maternal line, which this timeline does not decide. The two households stand beside each other before the next loss caused by a king's command.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarhvot-sea", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "古德倫在海上未能沉沒，被浪帶到約納克之地，並生下索利、厄爾普與哈姆瑟。斯萬希爾德則嫁給約爾蒙雷克。這是開頭散文的家系；厄爾普在相連詩歌有不同母系，不被此時間線裁定。兩個家室的並列，準備了下一場由王命造成的失去。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarhvot-accusation",
    globalOrder: 188,
    storyId: "norse-guthrunarhvot",
    stepId: "accusation",
    sourceStepIds: ["accusation", "death"],
    sourceIds: ["poetic-edda-guthrunarhvot-1-22"],
    tracks: {
      en: track("norse-guthrunarhvot-accusation", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 34,
        text: "Bikki reports something to Jormunrek. The king hangs his son Randver and has horses trample Svanhild. The prose never says what Bikki reports; the note only infers a relationship accusation, so it is not written as proven guilt. When the news reaches Guthrun, she will not let her sons remain silently at home.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarhvot-accusation", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 29,
        text: "比基向約爾蒙雷克傳話，國王絞死兒子蘭德維爾，又令馬踩死斯萬希爾德。散文沒有說明比基究竟傳了甚麼；註釋只推測是關係指控，因此不把它寫成證實的罪行。消息回到古德倫，她不讓兒子沉默地留在家中。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarhvot-incitement",
    globalOrder: 189,
    storyId: "norse-guthrunarhvot",
    stepId: "incitement",
    sourceStepIds: ["incitement", "arming"],
    sourceIds: ["poetic-edda-guthrunarhvot-1-22"],
    tracks: {
      en: track("norse-guthrunarhvot-incitement", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Guthrun tells Sorli and Hamther to avenge their sister. Hamther asks about the cost of her killing her sons to avenge herself on Atli; she laughs and brings out helmets and mail. This is an argument inside the family, not a cost-free heroic duty of revenge. Hamther foretells that the brothers will not return, and the story turns toward the mother left alone with lament.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarhvot-incitement", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 30,
        text: "古德倫要索利與哈姆瑟為姊妹復仇。哈姆瑟反問她殺子報復阿特利的代價；她笑著取出頭盔與鎧甲。這是家內的爭論，不把復仇當成無代價的英雄義務。哈姆瑟預告兩兄弟不會回來，故事轉向母親獨自留下的哀歌。",
      }),
    },
  },
  {
    sceneId: "norse-guthrunarhvot-lament",
    globalOrder: 190,
    storyId: "norse-guthrunarhvot",
    stepId: "lament",
    sourceStepIds: ["lament", "pyre"],
    sourceIds: ["poetic-edda-guthrunarhvot-1-22"],
    tracks: {
      en: track("norse-guthrunarhvot-lament", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 40,
        text: "Guthrun remembers the deaths of Sigurth, Atli, her brothers, and Svanhild, then calls to Sigurth and asks for a pyre. The introduction considers the last stanzas possibly drawn from another lost version; they express a wish to die without guaranteeing what follows. The lay ends when the lament has been spoken, not by giving the later outcome of Hamthesmol in advance.",
      }),
      'zh-Hant-mandarin': track("norse-guthrunarhvot-lament", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 35,
        text: "古德倫回想西古爾德、阿特利、兄弟和斯萬希爾德的死亡，最後呼喚西古爾德並要求柴堆。引言認為詩末幾節可能來自另一失傳版本；它表達求死意願，卻不保證後來發生了甚麼。本歌在哀歌被說盡之處結束，而不是替〈哈姆瑟之歌〉提前交代復仇結局。",
      }),
    },
  },
  {
    sceneId: "norse-hamthesmol-incitement",
    globalOrder: 191,
    storyId: "norse-hamthesmol",
    stepId: "incitement",
    sourceStepIds: ["incitement", "departure"],
    sourceIds: ["poetic-edda-hamthesmol-1-31"],
    tracks: {
      en: track("norse-hamthesmol-incitement", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 28,
        text: "Guthrun again urges Hamther and Sorli to avenge Svanhild. Knowing they may not return, they still set out. This poem shares wording with the earlier lay but does not erase their lineage variants. Their companion Erp will make revenge more fractured.",
      }),
      'zh-Hant-mandarin': track("norse-hamthesmol-incitement", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 27,
        text: "古德倫再次催促哈姆瑟與索利為斯萬希爾德復仇。二人知道自己未必能回來，仍然出發。這首詩與前一首共享若干行文，但不因此把兩首的家系異說消除。路上同行的厄爾普會令復仇變得更破碎。",
      }),
    },
  },
  {
    sceneId: "norse-hamthesmol-road",
    globalOrder: 192,
    storyId: "norse-hamthesmol",
    stepId: "road",
    sourceStepIds: ["road", "road-killing"],
    sourceIds: ["poetic-edda-hamthesmol-1-31"],
    tracks: {
      en: track("norse-hamthesmol-road", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "Erp travels with the brothers but is killed on the road. Neither text nor note can fill in motive and dialogue. This timeline does not expand defective passage into certain conflict. Hamther later admits that the attack might have succeeded had Erp lived.",
      }),
      'zh-Hant-mandarin': track("norse-hamthesmol-road", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 24,
        text: "厄爾普與兄弟同行，卻在路上被殺。文本與註釋都不能補足動機和對話。此處不把殘缺段落擴寫成可確證的衝突。哈姆瑟其後承認，厄爾普若還活著，刺殺或許已成功。",
      }),
    },
  },
  {
    sceneId: "norse-hamthesmol-attack",
    globalOrder: 193,
    storyId: "norse-hamthesmol",
    stepId: "attack",
    sourceStepIds: ["attack", "regret"],
    sourceIds: ["poetic-edda-hamthesmol-1-31"],
    tracks: {
      en: track("norse-hamthesmol-attack", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 29,
        text: "The brothers wound Jormunrek but do not finish the killing. Hamther remembers Erp's absence. The surviving poem is fragmented, so only result and regret are retained without assuming a full battle order. Their fame and death are spoken in the same damaged passage.",
      }),
      'zh-Hant-mandarin': track("norse-hamthesmol-attack", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 25,
        text: "兄弟傷及約爾蒙雷克，卻沒有完成刺殺。哈姆瑟回想厄爾普的缺席。現存詩的編排破碎，因此只保留結果與後悔，不假定完整戰場次序。他們的名聲和死亡在同一段殘詩裡被說出。",
      }),
    },
  },
  {
    sceneId: "norse-hamthesmol-death",
    globalOrder: 194,
    storyId: "norse-hamthesmol",
    stepId: "death",
    sourceStepIds: ["death", "aftermath"],
    sourceIds: ["poetic-edda-hamthesmol-1-31"],
    tracks: {
      en: track("norse-hamthesmol-death", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 25,
        text: "Sorli falls beside the gable and Hamther behind the house. The poem leaves only this ending and cannot reconstruct all lost lines backward. Revenge does not restore a household; it leaves Guthrun with another layer of loss.",
      }),
      'zh-Hant-mandarin': track("norse-hamthesmol-death", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 20,
        text: "索利倒在山牆旁，哈姆瑟倒在屋後。詩末只留下這個結局，不能倒推所有失落行文。復仇沒有恢復家室，只留下另一層古德倫的失去。",
      }),
    },
  },
  {
    sceneId: "norse-drap-niflunga-bridge-1",
    globalOrder: 195,
    storyId: "norse-drap-niflunga",
    stepId: "bridge-1",
    sourceStepIds: ["bridge-1", "bridge-2"],
    sourceIds: ["poetic-edda-drap-niflunga-bridge"],
    tracks: {
      en: track("norse-drap-niflunga-bridge-1", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 18,
        text: "The prose bridge leads from Sigurth's death toward Guthrun's next story. It does not replace the individual lays with a summary. The next entrance is Atli.",
      }),
      'zh-Hant-mandarin': track("norse-drap-niflunga-bridge-1", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 17,
        text: "散文橋從西古爾德死後，帶讀者走向古德倫的下一段故事。它不以摘要取代各首詩。下一個入口是阿特利。",
      }),
    },
  },
  {
    sceneId: "norse-drap-niflunga-bridge-3",
    globalOrder: 196,
    storyId: "norse-drap-niflunga",
    stepId: "bridge-3",
    sourceStepIds: ["bridge-3", "bridge-4"],
    sourceIds: ["poetic-edda-drap-niflunga-bridge"],
    tracks: {
      en: track("norse-drap-niflunga-bridge-3", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 19,
        text: "Atli's invitation and the brothers' deaths have distinct details in separate lays. This bridge only marks where readers should turn. Guthrun carries revenge forward.",
      }),
      'zh-Hant-mandarin': track("norse-drap-niflunga-bridge-3", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 17,
        text: "阿特利的邀請和古德倫兄弟的死亡，在各首詩中各有細節。這裡只標出讀者應轉往哪一首。復仇由古德倫承接。",
      }),
    },
  },
  {
    sceneId: "norse-drap-niflunga-bridge-5",
    globalOrder: 197,
    storyId: "norse-drap-niflunga",
    stepId: "bridge-5",
    sourceStepIds: ["bridge-5", "bridge-6"],
    sourceIds: ["poetic-edda-drap-niflunga-bridge"],
    tracks: {
      en: track("norse-drap-niflunga-bridge-5", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 19,
        text: "Guthrun's story does not stop after Atli; Svanhild begins another tragic thread. The prose decides none of the poetic variants. Hamther's lay connects from here.",
      }),
      'zh-Hant-mandarin': track("norse-drap-niflunga-bridge-5", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 18,
        text: "古德倫的故事沒有在阿特利後停止，斯萬希爾德成為另一條悲劇線。散文不裁定詩歌異本。哈姆瑟的故事由此接上。",
      }),
    },
  },
  {
    sceneId: "norse-drap-niflunga-bridge-7",
    globalOrder: 198,
    storyId: "norse-drap-niflunga",
    stepId: "bridge-7",
    sourceStepIds: ["bridge-7", "bridge-8"],
    sourceIds: ["poetic-edda-drap-niflunga-bridge"],
    tracks: {
      en: track("norse-drap-niflunga-bridge-7", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 20,
        text: "The prose stops before Hamther's expedition, leaving the fragmented ending to the lay itself. A bridge is not a new canonical version. The reader returns to Hamthesmol.",
      }),
      'zh-Hant-mandarin': track("norse-drap-niflunga-bridge-7", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 17,
        text: "散文停在哈姆瑟的遠征之前，讓詩歌自己說出殘缺的結局。橋接不是新的正典版本。讀者回到〈哈姆瑟之歌〉。",
      }),
    },
  },
  {
    sceneId: "norse-fafnismol-approach",
    globalOrder: 199,
    storyId: "norse-fafnismol",
    stepId: "approach",
    sourceStepIds: ["approach", "trench"],
    sourceIds: ["poetic-edda-fafnismol-1-44"],
    tracks: {
      en: track("norse-fafnismol-approach", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Sigurth and Regin go up to Gnitaheith and find Fafnir's track from the hoard toward water. Sigurth digs a trench and waits as the dragon crawls overhead. The opening prose explicitly has venom pouring down and a sword driven to the heart; this is not remade as a familiar later face-to-face duel. Fafnir can still speak, so the slaying becomes a conversation.",
      }),
      'zh-Hant-mandarin': track("norse-fafnismol-approach", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 32,
        text: "西古爾德與雷金上格尼塔荒原，看見法夫尼爾從寶藏爬往水邊的足跡。西古爾德挖溝伏下，讓巨龍從上方爬過。開頭散文明說毒液流下，而劍刺進心臟；這裡不把它改成後來常見的正面決鬥。法夫尼爾仍能說話，刺殺於是轉成一段對話。",
      }),
    },
  },
  {
    sceneId: "norse-fafnismol-name",
    globalOrder: 200,
    storyId: "norse-fafnismol",
    stepId: "name",
    sourceStepIds: ["name", "warning"],
    sourceIds: ["poetic-edda-fafnismol-1-44"],
    tracks: {
      en: track("norse-fafnismol-name", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Fafnir asks who has killed him. Sigurth first hides his name, then acknowledges that he is Sigmund's son. The dying dragon warns that gold, wealth, and rings bring ruin, and says Regin will betray both sides. These are a speaker's warnings in the poem, not reports of a fate already completed. When Regin returns, Sigurth already knows the danger has not died with the dragon.",
      }),
      'zh-Hant-mandarin': track("norse-fafnismol-name", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 33,
        text: "法夫尼爾追問誰殺了他。西古爾德先隱名，後承認自己是西格蒙德之子。瀕死的龍警告金、財與指環會帶來禍害，又說雷金會背叛兩邊。這些是詩中說話者的警告，不是已完成的命運報告。雷金回來時，西古爾德已知道危險不會隨龍死去。",
      }),
    },
  },
  {
    sceneId: "norse-fafnismol-heart",
    globalOrder: 201,
    storyId: "norse-fafnismol",
    stepId: "heart",
    sourceStepIds: ["heart", "birds"],
    sourceIds: ["poetic-edda-fafnismol-1-44"],
    tracks: {
      en: track("norse-fafnismol-heart", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 35,
        text: "Regin takes out his brother's heart, tells Sigurth to cook it, and lies down to rest. Sigurth touches the hot blood, puts his finger in his mouth, and suddenly understands birds. The birds first warn that Regin is plotting, then speak of different directions: Gjuki's daughter and a sleeping battle-maid on Hindarfjoll.",
      }),
      'zh-Hant-mandarin': track("norse-fafnismol-heart", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 38,
        text: "雷金取出兄長的心，叫西古爾德烤熟，自己躺下休息。西古爾德碰到熱血，把手指放進口中，忽然聽懂鳥語。鳥先警告雷金正在圖謀，之後又說起不同方向：居基家的女兒，與辛達爾山上的沉睡女戰士。引言與註釋指出鳥語段落可能不單一個來源，所以它是指引，不是強迫單一路線。",
      }),
    },
  },
  {
    sceneId: "norse-fafnismol-departure",
    globalOrder: 202,
    storyId: "norse-fafnismol",
    stepId: "departure",
    sourceStepIds: ["departure", "boundary"],
    sourceIds: ["poetic-edda-fafnismol-1-44"],
    tracks: {
      en: track("norse-fafnismol-departure", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 43,
        text: "Sigurth fills chests in Fafnir's lair, takes the fear-helm and other treasures, and puts them on Grani. The horse will not move until he mounts. The poem takes the hoard out of the dragon's lair and into Sigurth's journey without performing every later tragedy as a settled outcome here. Bird-speech only opens the door toward Brynhild's story; variants of that lay still need their own context.",
      }),
      'zh-Hant-mandarin': track("norse-fafnismol-departure", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "西古爾德在法夫尼爾的巢中裝滿寶箱，取走恐懼之盔和其他寶物，放上格拉尼。馬要等他上背才肯前行。詩把寶藏從龍的巢穴帶往西古爾德的旅程，但沒有在這裡把後來所有悲劇演成定局。鳥語只開出通往布倫希爾德故事的門；那一首的異本仍需在其自身脈絡讀取。",
      }),
    },
  },
  {
    sceneId: "norse-sigrdrifumol-mountain",
    globalOrder: 203,
    storyId: "norse-sigrdrifumol",
    stepId: "mountain",
    sourceStepIds: ["mountain", "awakening"],
    sourceIds: ["poetic-edda-sigrdrifumol-1-37"],
    tracks: {
      en: track("norse-sigrdrifumol-mountain", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Sigurth rides onto Hindarfjoll and sees a fire-like light rising to heaven. A fully armed figure sleeps in the shield-tower; only after he removes the helm does he know she is a woman. The mail seems grown to the flesh, so he cuts from the head opening to both arms. When the armour comes away, she wakes and asks what broke her sleep and cut her byrnie. Sigurth answers as Sigmund's son; their meeting begins with this awakening.",
      }),
      'zh-Hant-mandarin': track("norse-sigrdrifumol-mountain", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "西古爾德騎上辛達爾山，看見火光直上天空。盾塔裡睡著一名全副武裝的人；他取下頭盔，才知道那是一名女子。鎖子甲像貼著肌膚，他由頭口一路割到雙臂。鎧甲一除，她醒來，問是甚麼割開甲衣、打破睡眠。西古爾德以西格蒙德之子的身分回答；相遇由這場喚醒開始。",
      }),
    },
  },
  {
    sceneId: "norse-sigrdrifumol-punishment",
    globalOrder: 204,
    storyId: "norse-sigrdrifumol",
    stepId: "punishment",
    sourceStepIds: ["punishment", "mead"],
    sourceIds: ["poetic-edda-sigrdrifumol-1-37"],
    tracks: {
      en: track("norse-sigrdrifumol-punishment", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "Brynhild says that she killed Hjalmgunnar, whom Othin had promised victory, and was pierced with the sleep-thorn; Othin also said she would no longer bring victory to warriors. She had vowed not to marry a man who knew fear. Sigurth asks her name and her wisdom, and she gives him a horn of mead. The drink carries the newly wakened battle-maid into a dialogue that remains unfinished.",
      }),
      'zh-Hant-mandarin': track("norse-sigrdrifumol-punishment", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 36,
        text: "布倫希爾德說，自己曾殺死奧丁已許諾勝利的赫亞姆貢納爾，因此被睡眠刺刺中；奧丁又說她往後不再為戰士帶來勝利。她曾發誓，不嫁給知道恐懼的人。西古爾德問她的名字與智慧，她則把一角蜜酒交到他手上。這杯酒把剛醒來的女戰士帶進一段仍未完結的問答。",
      }),
    },
  },
  {
    sceneId: "norse-sigrdrifumol-runes",
    globalOrder: 205,
    storyId: "norse-sigrdrifumol",
    stepId: "runes",
    sourceStepIds: ["runes", "choice"],
    sourceIds: ["poetic-edda-sigrdrifumol-1-37"],
    tracks: {
      en: track("norse-sigrdrifumol-runes", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "After the mead come teachings of winning, ale, birth, waves, and speech runes. They are copied after the pair's dialogue, yet may not originally belong to this meeting. When Brynhild speaks again, she gives Sigurth a choice of speech or silence and names their fated trouble. He says he will not flee and asks only for her loving word.",
      }),
      'zh-Hant-mandarin': track("norse-sigrdrifumol-runes", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 42,
        text: "接在蜜酒後的是勝利、麥酒、出生、海浪與言語等符文教誨。它們被抄在兩人的對話之後，卻未必原本就屬於這場相遇。當布倫希爾德再開口，她把說與沉默交給西古爾德選擇，並提到兩人命定的災厄。西古爾德說自己不會逃走，只求她的愛語。中間失去的詩節不能由這幾句話補回，這段相遇只留下選擇的重量。",
      }),
    },
  },
  {
    sceneId: "norse-sigrdrifumol-counsels",
    globalOrder: 206,
    storyId: "norse-sigrdrifumol",
    stepId: "counsels",
    sourceStepIds: ["counsels", "fragment"],
    sourceIds: ["poetic-edda-sigrdrifumol-1-37"],
    tracks: {
      en: track("norse-sigrdrifumol-counsels", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "What follows is a sequence of counsels about kin, oaths, drunken quarrels, enemies, and burial. They come after the meeting in the manuscript but do not grow naturally from one shared plot. The leaf after stanza 29 is lost, and later added verses do not restore the ending. This scene does not write a rounded ending into the silence.",
      }),
      'zh-Hant-mandarin': track("norse-sigrdrifumol-counsels", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 40,
        text: "隨後是一連串關於親族、誓言、酒後爭吵、敵人與葬禮的箴言。它們在手稿裡接著這段相遇，卻不像從同一個情節自然長出。第29節後的頁葉已失去，後補詩節也沒有讓結尾重現。這裡不替沉默寫出一個圓滿結局。西古爾德與布倫希爾德的話停在殘篇邊緣；後面的故事要由其他詩歌各自說下去。",
      }),
    },
  },
  {
    sceneId: "norse-hyndluljoth-freyja-calls-from-cave",
    globalOrder: 207,
    storyId: "norse-hyndluljoth",
    stepId: "freyja-calls-from-cave",
    sourceStepIds: ["freyja-calls-from-cave", "hyndla-sees-companion"],
    sourceIds: ["poetic-edda-hyndluljoth-1-29"],
    tracks: {
      en: track("norse-hyndluljoth-freyja-calls-from-cave", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 37,
        text: "At night Freyja comes to Hyndla's cave and asks her to travel. As Hyndla wakes, her attention goes at once to the mount and companion beside Freyja. She says the figure is not simply a boar but Ottar; Freyja instead identifies the gold-bristled Hildisvini and does not accept the charge. The dispute is not a final solution to a riddle.",
      }),
      'zh-Hant-mandarin': track("norse-hyndluljoth-freyja-calls-from-cave", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 44,
        text: "芙蕾雅在夜裡來到海因德拉的洞前，要她同行。海因德拉一醒來便把目光放到芙蕾雅身旁的坐騎與同行者上。她說那不是單純的野豬，而是奧塔爾；芙蕾雅則指認金鬃的希爾迪斯維尼，並沒有接受這個說法。這段爭執不是替讀者解謎的定論。時間線把海因德拉的話留在她夜行時的敵意裡，也讓金鬃野豬作為芙蕾雅明說的坐騎留下。",
      }),
    },
  },
  {
    sceneId: "norse-hyndluljoth-wager-needs-ancestry",
    globalOrder: 208,
    storyId: "norse-hyndluljoth",
    stepId: "wager-needs-ancestry",
    sourceStepIds: ["wager-needs-ancestry", "paternal-line-recited"],
    sourceIds: ["poetic-edda-hyndluljoth-1-29"],
    tracks: {
      en: track("norse-hyndluljoth-wager-needs-ancestry", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 46,
        text: "Freyja says Ottar has wagered with Angantyr over inherited wealth and his descent, so she asks Hyndla to recall names for him. Hyndla moves from Ottar to his father Instein, then back through Alf the Old, Ulf, Saefari, and Svan the Red. The names are not decoration but a paternal line that can be followed generation by generation. The poem supplies no courtroom verdict; it shows the act of speaking a genealogy for a wager.",
      }),
      'zh-Hant-mandarin': track("norse-hyndluljoth-wager-needs-ancestry", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 49,
        text: "芙蕾雅說奧塔爾正為父親留下的財富與自己的出身同安甘蒂爾打賭，因此要海因德拉替他記起名字。海因德拉由奧塔爾說到父親因斯坦，再倒數老阿爾夫、烏爾夫、賽法里與紅髮斯萬。名字在這裡不是裝飾，而是一條可被逐代檢查的父系路線。詩沒有提供一場法庭裁決；它展示的是為了賭注而把家系說出來的過程。這條時間線也不把同名人物同其他英雄故事自動合併。",
      }),
    },
  },
  {
    sceneId: "norse-hyndluljoth-maternal-line-recited",
    globalOrder: 209,
    storyId: "norse-hyndluljoth",
    stepId: "maternal-line-recited",
    sourceStepIds: ["maternal-line-recited", "heroic-kin-names"],
    sourceIds: ["poetic-edda-hyndluljoth-1-29"],
    tracks: {
      en: track("norse-hyndluljoth-maternal-line-recited", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 31,
        text: "The line does not run only through fathers. Hyndla next names Hledis, then Hledis's father Frothi and mother Friaut. She then joins a longer list of heroic names to Ottar's kin. That broadens his claim without showing that every distant connection has been proved one by one.",
      }),
      'zh-Hant-mandarin': track("norse-hyndluljoth-maternal-line-recited", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "家系並未只沿父方而行。海因德拉接著說出赫萊迪絲，以及她的父親弗羅西和母親弗里奧特。之後她把更長的英雄名單連到奧塔爾的親屬關係。這使奧塔爾的主張顯得更廣，卻不等於詩已逐一證明每一條遙遠連線。近親關係可以逐代看清；更遠的英雄名字只在吟誦中延展，不必替每一個名字定出唯一的家譜。",
      }),
    },
  },
  {
    sceneId: "norse-hyndluljoth-genealogy-closes",
    globalOrder: 210,
    storyId: "norse-hyndluljoth",
    stepId: "genealogy-closes",
    sourceStepIds: ["genealogy-closes", "stop-before-short-voluspo"],
    sourceIds: ["poetic-edda-hyndluljoth-1-29"],
    tracks: {
      en: track("norse-hyndluljoth-genealogy-closes", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 39,
        text: "Once parents and ancestors have been spoken, Ottar's descent becomes proof he can use to support the wager. The pressure remains on memory and recital rather than on a reported result of the bet. This night route ends with stanza 29. The following Short Voluspo has its own material and manuscript problems, so it is not treated as the wager's next scene.",
      }),
      'zh-Hant-mandarin': track("norse-hyndluljoth-genealogy-closes", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 52,
        text: "父母與祖先被說出口後，奧塔爾的出身便從一個主張變成他能拿去支持賭注的證明。詩在此處仍把壓力放在記憶與背誦，而不是在賭局結果上。這條夜行至第29節為止。接在後面的《短女巫預言》有自己的材料與傳本問題，並不被收作這個賭注的下一幕。因此結尾保留一個有意識的界線：我們知道海因德拉替奧塔爾說了甚麼，卻不把緊接的另一首詩，或其他傳統的結果，當成這段對話必然延續。",
      }),
    },
  },
  {
    sceneId: "norse-reginsmol-regin-remembers",
    globalOrder: 211,
    storyId: "norse-reginsmol",
    stepId: "regin-remembers",
    sourceStepIds: ["regin-remembers", "andvari-captured"],
    sourceIds: ["poetic-edda-reginsmol-prose-1-12-hoard"],
    tracks: {
      en: track("norse-reginsmol-regin-remembers", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 38,
        text: "Regin tells Sigurth that after Otr's death, Loki caught Andvari in pike form with Ran's net. Andvari gives up the gold but tries to hide one ring. Loki takes the ring as well. As Regin tells it, Fafnir is no longer only a dragon on the heath but a brother bound to the same gold. Before the hero draws his sword, danger already lives in family memory.",
      }),
      'zh-Hant-mandarin': track("norse-reginsmol-regin-remembers", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "雷金告訴西格爾德，奧特爾死後，洛基曾用羅恩的網捕住化作梭魚的安德瓦利。安德瓦利交出黃金，卻想把一枚戒指藏下來。洛基連戒指也奪走。雷金講這段話時，法夫尼爾不再只是盤踞荒原的龍，而是同一份黃金牽出的兄弟。故事把英雄尚未拔劍以前的危險放在家族記憶裡。",
      }),
    },
  },
  {
    sceneId: "norse-reginsmol-andvari-curse",
    globalOrder: 212,
    storyId: "norse-reginsmol",
    stepId: "andvari-curse",
    sourceStepIds: ["andvari-curse", "hreidmar-killed"],
    sourceIds: ["poetic-edda-reginsmol-prose-1-12-hoard"],
    tracks: {
      en: track("norse-reginsmol-andvari-curse", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 41,
        text: "Andvari withdraws before his rocky cave and curses the gold to bring death; although the poem gives him the line, it bears marks of another source. When the ransom reaches Hreithmar's house, Fafnir and Regin demand shares. Hreithmar refuses, and Fafnir stabs his sleeping father and takes the gold alone. Before the gold reaches Sigurth, father and son have already bled for it.",
      }),
      'zh-Hant-mandarin': track("norse-reginsmol-andvari-curse", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 37,
        text: "安德瓦利退回石洞前，咒罵黃金會帶來死亡；這一節在詩中雖由他說出，卻帶着來自別處的痕跡。賠償金送到赫雷德瑪爾家後，法夫尼爾與雷金要求分產。赫雷德瑪爾拒絕，法夫尼爾便在父親熟睡時刺死他，獨佔黃金。黃金還未落到西格爾德手中，父子之間已先為它流血。",
      }),
    },
  },
  {
    sceneId: "norse-reginsmol-inheritance-refused",
    globalOrder: 213,
    storyId: "norse-reginsmol",
    stepId: "inheritance-refused",
    sourceStepIds: ["inheritance-refused", "gram-and-fafnir", "father-vengeance"],
    sourceIds: ["poetic-edda-reginsmol-prose-1-12-hoard", "poetic-edda-reginsmol-prose-13-15-forging"],
    tracks: {
      en: track("norse-reginsmol-inheritance-refused", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 44,
        text: "Regin cannot recover his inheritance from Fafnir, so he brings that grievance to Sigurth. He makes Gram, a sword that cuts wool on the water and cleaves an anvil. He urges Sigurth to kill Fafnir, but Sigurth says he cannot pursue red gold first; Hunding's sons would mock him if he did not avenge his father. Dragon-slaying is set aside for a time, and the material turns to storm at sea and battle omens.",
      }),
      'zh-Hant-mandarin': track("norse-reginsmol-inheritance-refused", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 39,
        text: "雷金向法夫尼爾討不回父產，只能把這份仇怨帶到西格爾德身邊。他造出格拉姆，劍能斷開水上的羊毛，也能劈開鐵砧。他催西格爾德去殺法夫尼爾，西格爾德卻說自己不能先追逐紅金；洪丁之子若見他不替父親報仇，必會嘲笑他。屠龍被暫時放下，這一串材料轉到海上的風暴與戰鬥徵兆。",
      }),
    },
  },
  {
    sceneId: "norse-reginsmol-hnikar-and-battle",
    globalOrder: 214,
    storyId: "norse-reginsmol",
    stepId: "hnikar-and-battle",
    sourceStepIds: ["hnikar-and-battle"],
    sourceIds: ["poetic-edda-reginsmol-16-26-omens-and-battle"],
    tracks: {
      en: track("norse-reginsmol-hnikar-and-battle", 'en', {
        label: labels.en,
        azureLocale: 'en-US',
        azureVoice: 'en-US-JennyNeural',
        prosodyRate: '-3%',
        estimatedDurationSeconds: 42,
        text: "When the ship is caught in storm, a man on the mountain calls himself Hnikar, boards, and tells Sigurth of ravens, wolves, and signs before battle. The following prose and stanzas give the deaths of Hunding's sons to Sigurth, but their language is close to Helgi material and cannot be treated as an unquestioned episode of his life. As the battle ends, Regin again urges him toward Fafnir.",
      }),
      'zh-Hant-mandarin': track("norse-reginsmol-hnikar-and-battle", 'zh-Hant-mandarin', {
        label: labels.mandarin,
        azureLocale: 'zh-CN',
        azureVoice: 'zh-CN-YunjianNeural',
        azureStyle: 'narration-relaxed',
        prosodyRate: '-4%',
        estimatedDurationSeconds: 41,
        text: "船在風暴中受困時，山上的人自稱希尼卡爾，登船同行，告訴西格爾德烏鴉、狼與敵人的徵兆。隨後的散文和詩節把洪丁之子的死亡歸到西格爾德；但這段用語與赫爾吉故事相近，不能把它當作毫無疑問的一段西格爾德經歷。戰鬥一結束，雷金立刻再催他去對付法夫尼爾。詩就在屠龍真正開始之前停下。",
      }),
    },
  }
]

const sceneById = new Map(norseTimelineNarrationScenes.map((scene) => [scene.sceneId, scene]))

export function norseTimelineNarrationTrackForScene(sceneId: string | undefined, mode: TimelineNarrationMode) {
  if (!sceneId || mode === 'off') return undefined
  return sceneById.get(sceneId)?.tracks[mode]
}

export function norseTimelineNarrationCharacterCount() {
  return norseTimelineNarrationScenes.reduce((total, scene) => (
    total + Array.from(scene.tracks.en.text).length + Array.from(scene.tracks['zh-Hant-mandarin'].text).length
  ), 0)
}
